import {
  clonePoint,
  type DxfAssocArrayObject,
  type DxfBlockDefinition,
  type DxfBlockRecordEntry,
  type DxfBlocksSection,
  type DxfCommonEntityProps,
  type DxfDictionaryEntry,
  type DxfDimensionStyleEntry,
  type DxfDocument,
  type DxfDynamicBlockObject,
  type DxfEntity,
  type DxfGroup,
  type DxfHatchBoundaryEdge,
  type DxfHatchBoundaryPath,
  type DxfLayerEntry,
  type DxfLayoutEntry,
  type DxfLinetypeElement,
  type DxfLinetypeEntry,
  type DxfLwPolylineVertex,
  type DxfMultileaderStyleEntry,
  type DxfMLeaderEntity,
  type DxfObjectsSection,
  type DxfPoint3D,
  type DxfRawSection,
  type DxfRevcloudEntity,
  type DxfSection,
  type DxfSeqendEntity,
  type DxfPolylineSubtype,
  type DxfSupportedEntity,
  type DxfSupportedEntityType,
  dxfPolylineSubtype,
  dxfVertexSubtype,
  type DxfViewportEntity,
  type DxfTablesSection,
  type DxfTextStyleEntry,
  type DxfXDataEntry,
} from "./types";
import { defaultExtrusion } from "./parser";
// T9 PR-3 — Cyrillic mojibake fix. `\U+XXXX` escaping of non-ASCII string
// content on the R2000 (ANSI_1252) write path so a UTF-8 IO boundary survives
// AutoCAD's codepage decode. `chunkEscapedString` keeps MTEXT group-3 chunking
// from splitting an escape across a 250-char boundary.
import {
  STRING_CONTENT_GROUP_CODES,
  chunkEscapedString,
  decodeDxfUnicode,
  escapeDxfUnicode,
} from "./dxfUnicodeEscape";
// SL-3 — the ONE-TRUTH DIMENSION subtype → 2nd subclass marker mapping. The
// writer uses it as the by-construction safety-net: any DIMENSION that reaches
// the writer WITHOUT a parsed/carried subtype marker (a from-scratch / marker-
// less dim) has its 2nd subclass marker(s) synthesized from `dimensionType & 7`
// so the subtype defpoints are never orphaned. The drawing-model export
// boundary (toDxfEntity) also populates `subclassMarkers` from the same
// function, so an editor dim arrives here WITH the markers and this net stays
// inert (no double-emit). Imported dims carry their source markers verbatim.
import { dimensionSubtypeSubclassMarkers } from "./dimensionSubtype";
import { createHandSeedMinter, type HandSeedMinter } from "./handSeedMinter";
// REVCLOUD RevcloudProps XData is encoded HERE (the writer is the single
// encode point). The typed `geometry` fields (revisionCloudStyle /
// creationMode / approximateArcLength / arcLength = minimum arc chord) are
// projected into the "RevcloudProps" XData appid on serialize. The bridge sets the typed
// geometry fields only; it must NOT also pre-stuff this XData (that would
// double-emit). The guard below additionally skips encoding when the
// entity already carries a RevcloudProps entry so a direct caller that
// supplies verbatim xdata is not duplicated.
import { decodeRevcloudProps, encodeRevcloudProps, REVCLOUD_PROPS_APPID } from "./entityParser";
// PR-5 — annotative OBJECTS writers. Imported alongside the
// BLOCK_RECORD writer so AcadAnnotativeDecomposition XData synthesis
// stays in the same file as the rest of the BLOCK_RECORD emission.
import {
  serializeAcadAnnotativeDecomposition,
  serializeAcDbScale,
  serializeAcDbScaleListDict,
  serializeAnnotScaleContextRecord,
} from "./objects/objectsAnnotativeWriter";
// Batch-8 S-ARRAYASSOC-WRITE — AcDbAssocArray OBJECTS graph writer.
import {
  serializeAssocArrayObject,
  serializeAssocArrayPlaceholder,
} from "./assocArrayWriter";
import { ASSOC_ARRAY_PLACEHOLDER_CODE } from "./assocArrayPlaceholder";
// EPIC-VIEWPORT-CLIP SLICE 2 — XCLIP / SPATIAL_FILTER OBJECTS re-synth.
import { serializeSpatialFilter } from "./objects/spatialFilterWriter";
import {
  SPATIAL_FILTER_PLACEHOLDER_CODE,
  SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX,
  type DxfSpatialFilter,
} from "./objects/spatialFilterTypes";
// S-MLEADER-CONTEXT-STRUCTURAL (sub-slice C) — byte-faithful CONTEXT_DATA emit
// from the typed nested tree (symmetric inverse of mleaderContextBuilder).
import { buildAllMLeaderContextGroups } from "./mleaderContextWriter";

/**
 * ## Function dependency map
 *
 * Public API:
 *   serializeDxfDocument(doc, opts?)
 *     -> assignAnonymousDimensionBlockNames(doc) -> buildStubBlockDefinition
 *     -> buildBlocksSectionStub(blockNames)
 *     -> serializeSection(section) for each section in document order
 *         -> serializeTablesSection -> serializeTextStyleEntry, serializeDimStyleEntry
 *         -> serializeBlocksSection -> serializeBlockDefinition
 *         -> serializeObjectsSection -> serializeDictionaryEntry,
 *                                       serializeMultileaderStyleEntry,
 *                                       serializeLayoutEntry,
 *                                       serializeDynamicBlockObject,
 *                                       serializeAcDbScale (PR-5),
 *                                       serializeAnnotScaleContextRecord (PR-5),
 *                                       serializeAcDbScaleListDict (PR-5),
 *                                       alreadyInDictionaries (PR-5)
 *         -> entities: serializeEntity -> serializeSupportedEntity
 *             -> PRE_ENTITY_WRITERS (5 handle, 102 ext-dict, 330 owner)
 *             -> 100 AcDbEntity
 *             -> ACDBENTITY_WRITERS (67 410 8 6 347 62 420 430 370 48 60 440 390 284)
 *             -> mergeSubclassMarkers (restore specific 100-code markers)
 *             -> writeEntityGeometry(groups, entity, specificMarkers)
 *                 (owns the specific markers + 39/210 interleave per type)
 *                 -> writePoint / writeHatchBoundaryPath -> writeHatchBoundaryEdge
 *                 -> pushGroup -> formatDxfNumber
 *
 *   createRawSection(name, groups) -> DxfRawSection
 *
 * Guard helpers:
 *   isDefaultLayer / isDefaultLinetype / isDefaultExtrusion (suppress default values)
 *
 * DIMVAR mapping:
 *   DIMVAR_NAME_TO_CODE (record: string -> DXF group code)
 */

export type SerializeDxfOptions = {
  newline?: string;
  /**
   * Whether to escape non-ASCII characters in string-content groups as
   * `\U+XXXX` inline escapes (T9 PR-3 Cyrillic mojibake fix).
   *
   * The export is currently fixed at R2000 (`$ACADVER = AC1015` +
   * `$DWGCODEPAGE = ANSI_1252`), where AutoCAD decodes text as the declared
   * codepage and a raw-UTF-8 Cyrillic byte stream mojibakes. Escaping is
   * REQUIRED on that path.
   *
   * When omitted, the writer derives the answer from the document's
   * `$ACADVER`: pre-R2007 (< AC1021, i.e. legacy/ANSI-codepage versions) →
   * escape; R2007+ (AC1021+, UTF-16/UTF-8-native) → emit raw. This is the
   * version-gate seam a future DXF-version selector switches on; pass `true` /
   * `false` to force it explicitly.
   */
  escapeUnicode?: boolean;
};

type GroupWriter = (
  groups: DxfGroup[],
  common: DxfCommonEntityProps,
  type: DxfSupportedEntityType,
) => void;

export function formatDxfNumber(value: number): string {
  if (!Number.isFinite(value)) {
    throw new Error(`Cannot serialize non-finite DXF number: ${value}`);
  }

  if (Number.isInteger(value)) {
    return String(value);
  }

  const fixed = value.toFixed(12).replace(/\.?0+$/, "");
  return fixed === "-0" ? "0" : fixed;
}

function pushGroup(
  groups: DxfGroup[],
  code: number,
  value: string | number,
): void {
  groups.push({
    code,
    value: typeof value === "number" ? formatDxfNumber(value) : value,
  });
}

function isDefaultLayer(value: string): boolean {
  return value.trim() === "0";
}

function isDefaultLinetype(value: string): boolean {
  return value.trim().toUpperCase() === "BYLAYER";
}

function isDefaultExtrusion(point: DxfPoint3D): boolean {
  return point.x === 0 && point.y === 0 && point.z === 1;
}

// DXF-ORDER FIX (Autodesk DXF reference R2000/AC1015 + ezdxf 1.4.3). The
// AutoCAD-conformant entity layout is:
//
//   0 <type>
//   5 <handle>  102 {ACAD_XDICTIONARY 360 <h> 102 }  330 <owner>   ← PRE_ENTITY
//   100 AcDbEntity
//   [67 410 8 6 347 62 420 430 370 48 60 440 390 284]              ← ACDBENTITY
//   100 <specific marker>
//   [specific-subclass fields + geometry, incl 39 thickness + 210 extrusion]
//
// The PRE-`100 AcDbEntity` identity codes (handle / ext-dict / owner) and the
// 14 AcDbEntity-common codes are partitioned into the two arrays below. The
// previously-co-located thickness(39) + extrusion(210/220/230) writers are
// GONE from the common channel: per ezdxf those are SPECIFIC-subclass fields
// (AcDbLine.thickness, AcDbCircle.extrusion, …), not AcDbEntity fields, so they
// are emitted from `writeThickness` / `writeExtrusion` at each type's
// spec-correct interleave position inside writeEntityGeometry.

/** Identity codes emitted BEFORE the `100 AcDbEntity` subclass marker. */
const PRE_ENTITY_WRITERS: GroupWriter[] = [
  (groups, common) => {
    if (common.handle) {
      pushGroup(groups, 5, common.handle);
    }
  },
  // 102 {ACAD_XDICTIONARY 360 <handle> 102 } — must appear AFTER
  // group 5 (handle) and BEFORE group 330 (ownerHandle), matching the
  // BLOCK_RECORD writer ordering. Used by INSERTs of dynamic blocks
  // to point at the per-INSERT AcDbBlockRepresentation DICTIONARY
  // (see types.ts:DxfCommonEntityProps.extensionDictionaryHandle).
  (groups, common) => {
    if (common.extensionDictionaryHandle) {
      pushGroup(groups, 102, "{ACAD_XDICTIONARY");
      pushGroup(groups, 360, common.extensionDictionaryHandle);
      pushGroup(groups, 102, "}");
    }
  },
  (groups, common) => {
    if (common.ownerHandle) {
      pushGroup(groups, 330, common.ownerHandle);
    }
  },
];

/** The 14 AcDbEntity-common codes, emitted AFTER the `100 AcDbEntity` marker. */
const ACDBENTITY_WRITERS: GroupWriter[] = [
  (groups, common) => {
    const space = common.space ?? "model";

    if (space === "paper") {
      pushGroup(groups, 67, 1);
    }
  },
  (groups, common) => {
    if (common.layoutName) {
      pushGroup(groups, 410, common.layoutName);
    }
  },
  (groups, common) => {
    // FIX — the conformant from-scratch export sets `forceLayerEmit` so EVERY
    // entity carries an explicit `8/<layer>`, including the default layer "0"
    // (user decision: "write code 8 on every entity"). `projectCommonEntityProps`
    // only sets `common.layer` for a NON-default layer, so default the layer to
    // "0" here on the force path. The carry-forward / imported round-trip path
    // leaves `forceLayerEmit` undefined → byte-identical "omit code 8 when 0".
    if (common.forceLayerEmit) {
      pushGroup(groups, 8, common.layer && common.layer.length > 0 ? common.layer : "0");
    } else if (common.layer && !isDefaultLayer(common.layer)) {
      pushGroup(groups, 8, common.layer);
    }
  },
  (groups, common) => {
    if (common.linetype && !isDefaultLinetype(common.linetype)) {
      pushGroup(groups, 6, common.linetype);
    }
  },
  (groups, common) => {
    if (common.materialHandle) {
      pushGroup(groups, 347, common.materialHandle);
    }
  },
  (groups, common) => {
    // Group 62 (ACI). A STANDALONE 256 (BYLAYER) is omitted — it is the implicit
    // default AutoCAD writes by omission. But an EXPLICIT 256 that coexists with
    // a 420 (true-colour) / 430 (colour-book) sibling is a real source group and
    // must be preserved for a lossless round-trip (EPIC-COLOR-UNION S1 — see
    // colorFromDxf/colorToDxf, which now carry a coexisting 256 as rgb/bookColor
    // `aci`). All other ACI values (0/BYBLOCK, 1-255, negative layer-off) emit
    // verbatim.
    if (
      common.colorNumber !== undefined &&
      (common.colorNumber !== 256 ||
        common.trueColor !== undefined ||
        common.colorName !== undefined)
    ) {
      pushGroup(groups, 62, common.colorNumber);
    }
  },
  (groups, common) => {
    if (common.trueColor !== undefined) {
      pushGroup(groups, 420, common.trueColor);
    }
  },
  (groups, common) => {
    if (common.colorName) {
      pushGroup(groups, 430, common.colorName);
    }
  },
  (groups, common) => {
    if (common.lineweight !== undefined && common.lineweight !== -1) {
      pushGroup(groups, 370, common.lineweight);
    }
  },
  (groups, common) => {
    if (common.linetypeScale !== undefined && common.linetypeScale !== 1) {
      pushGroup(groups, 48, common.linetypeScale);
    }
  },
  (groups, common) => {
    if (common.visibility !== undefined && common.visibility !== 0) {
      pushGroup(groups, 60, common.visibility);
    }
  },
  (groups, common) => {
    if (common.transparency !== undefined) {
      pushGroup(groups, 440, common.transparency);
    }
  },
  (groups, common) => {
    if (common.plotStyleHandle) {
      pushGroup(groups, 390, common.plotStyleHandle);
    }
  },
  (groups, common) => {
    if (common.shadowMode !== undefined && common.shadowMode !== 0) {
      pushGroup(groups, 284, common.shadowMode);
    }
  },
];

// Thickness(39) + extrusion(210/220/230) are SPECIFIC-subclass fields (ezdxf
// 1.4.3: AcDbLine/AcDbCircle/AcDbText/…). The parser routes 39/210/220/230 into
// `common.thickness` / `common.extrusion` (a generic common reader, code-routed
// + order-independent), so these helpers READ FROM `common` but EMIT at each
// type's spec-correct interleave position. HATCH + INSERT self-emit their OCS
// normal from `geometry.extrusion` (single-channel) and never call
// writeExtrusion — see their geometry cases.

/** Emit group 39 (thickness) from `common` when non-default. */
function writeThickness(groups: DxfGroup[], common: DxfCommonEntityProps): void {
  if (common.thickness !== undefined && common.thickness !== 0) {
    pushGroup(groups, 39, common.thickness);
  }
}

/**
 * Emit one AcDbPolyline vertex in the CANONICAL ezdxf group-code order:
 *   10, 20 (x, y) → 40 (start width) → 41 (end width) → 42 (bulge) → 91 (vertex id)
 *
 * Verified against ezdxf 1.4.3 `entities/lwpolyline.py`
 * (`LWPOINTCODES = (10, 20, 40, 41, 42)` + the `# 40, 41, 42: start width, end
 * width, bulge` inline doc) and AutoCAD's published AcDbPolyline layout — the
 * width pair (40/41) precedes the bulge (42). The prior order emitted 42 BEFORE
 * 40/41 (SL-22 F2). Width / bulge / the persistent vertex id (91) are emitted
 * only when non-default so plain polylines stay byte-compact. Shared by the
 * LWPOLYLINE and REVCLOUD writers — BOTH serialize as `100 AcDbPolyline` and
 * carry the SAME vertex sub-order, so this is the SINGLE source of truth (no
 * cloned per-vertex loop).
 */
function writeAcDbPolylineVertex(
  groups: DxfGroup[],
  v: DxfLwPolylineVertex,
): void {
  pushGroup(groups, 10, v.x);
  pushGroup(groups, 20, v.y);
  if (v.startWidth !== undefined && v.startWidth !== 0) {
    pushGroup(groups, 40, v.startWidth);
  }
  if (v.endWidth !== undefined && v.endWidth !== 0) {
    pushGroup(groups, 41, v.endWidth);
  }
  if (v.bulge !== undefined && v.bulge !== 0) {
    pushGroup(groups, 42, v.bulge);
  }
  if (v.vertexId !== undefined) {
    pushGroup(groups, 91, v.vertexId);
  }
}

/** Emit groups 210/220/230 (OCS extrusion normal) from `common` when non-default. */
function writeExtrusion(groups: DxfGroup[], common: DxfCommonEntityProps): void {
  const extrusion = clonePoint(common.extrusion ?? defaultExtrusion());

  if (!isDefaultExtrusion(extrusion)) {
    pushGroup(groups, 210, extrusion.x);
    pushGroup(groups, 220, extrusion.y);
    pushGroup(groups, 230, extrusion.z);
  }
}

const CANONICAL_SUBCLASS_MARKERS: Record<DxfSupportedEntityType, string[]> = {
  LINE: ["AcDbEntity", "AcDbLine"],
  POINT: ["AcDbEntity", "AcDbPoint"],
  CIRCLE: ["AcDbEntity", "AcDbCircle"],
  ARC: ["AcDbEntity", "AcDbCircle", "AcDbArc"],
  ELLIPSE: ["AcDbEntity", "AcDbEllipse"],
  SPLINE: ["AcDbEntity", "AcDbSpline"],
  LWPOLYLINE: ["AcDbEntity", "AcDbPolyline"],
  TEXT: ["AcDbEntity", "AcDbText"],
  MTEXT: ["AcDbEntity", "AcDbMText"],
  DIMENSION: ["AcDbEntity", "AcDbDimension"],
  INSERT: ["AcDbEntity", "AcDbBlockReference"],
  HATCH: ["AcDbEntity", "AcDbHatch"],
  VIEWPORT: ["AcDbEntity", "AcDbViewport"],
  MLEADER: ["AcDbEntity", "AcDbMLeader"],
  ARRAYRECT: ["AcDbEntity", "AcDbBlockReference"],
  REVCLOUD: ["AcDbEntity", "AcDbPolyline"],
  // WIPEOUT is a SINGLE-subtype entity: AutoCAD/ezdxf emit exactly ONE
  // `100 AcDbWipeout` marker that carries the WHOLE raster-image body. ezdxf
  // `entities/image.py` defines `acdb_wipeout = DefSubclass("AcDbWipeout",
  // dict(acdb_image.attribs))` (its `# DXF reference error: Subclass marker
  // (AcDbRasterImage)` note documents the historical AcDbRasterImage
  // confusion), and `ImageBase.load_dxf_attribs` reads ONLY
  // `processor.subclasses[2]`. A SECOND `100 AcDbRasterImage` marker (the old
  // dual-marker split) therefore buries the 14/24 clip boundary under an
  // unread subclass index → the mask is silently LOST on read. One marker only.
  WIPEOUT: ["AcDbEntity", "AcDbWipeout"],
  ATTDEF: ["AcDbEntity", "AcDbText", "AcDbAttributeDefinition"],
  ATTRIB: ["AcDbEntity", "AcDbText", "AcDbAttribute"],
  SEQEND: ["AcDbEntity"],
  // POLYLINE / VERTEX subtype markers are a FUNCTION OF GEOMETRY (group 70),
  // exactly like INSERT's AcDbBlockReference↔AcDbMInsertBlock — a 3D polyline
  // is `AcDb3dPolyline`, a polygon mesh `AcDbPolygonMesh`, a poly-face mesh
  // `AcDbPolyFaceMesh`. The static entry here holds the 2D default; the real
  // one is emitted by emitPolylineSubtypeMarkers / emitVertexSubtypeMarkers
  // (see those helpers), which also drop every sibling subtype from the merged
  // list so an imported 3D polyline cannot double-emit both markers.
  POLYLINE: ["AcDbEntity", "AcDb2dPolyline"],
  VERTEX: ["AcDbEntity", "AcDbVertex", "AcDb2dVertex"],
  // ACAD_TABLE carries BOTH subclasses: the block reference to the anonymous
  // `*T<N>` graphics block, then the table content model.
  ACAD_TABLE: ["AcDbEntity", "AcDbBlockReference", "AcDbTable"],
  OLE2FRAME: ["AcDbEntity", "AcDbOle2Frame"],
};

/**
 * The POLYLINE subtype markers, keyed by {@link dxfPolylineSubtype}. Verified
 * against ezdxf 1.4.4 `entities/polyline.py` (`Polyline.get_mode()` returns
 * exactly these four strings) and the Autodesk DXF reference.
 */
const POLYLINE_SUBTYPE_MARKER: Record<DxfPolylineSubtype, string> = {
  "2d": "AcDb2dPolyline",
  "3d": "AcDb3dPolyline",
  "polygon-mesh": "AcDbPolygonMesh",
  "polyface-mesh": "AcDbPolyFaceMesh",
};

/** The matching VERTEX subtype markers (ezdxf `Polyline.append_vertex`). */
const VERTEX_SUBTYPE_MARKER: Record<DxfPolylineSubtype, string> = {
  "2d": "AcDb2dVertex",
  "3d": "AcDb3dPolylineVertex",
  "polygon-mesh": "AcDbPolygonMeshVertex",
  "polyface-mesh": "AcDbPolyFaceMeshVertex",
};

/**
 * Every marker the POLYLINE/VERTEX emitters own, for dedupe on the merged
 * list. `AcDbVertex` is included even though it is not a "subtype": a VERTEX's
 * emitter passes it explicitly as a LEADING marker, and a poly-face FACE
 * record must NOT have it at all (ezdxf 1.4.4 `DXFVertex.export_entity` writes
 * the single `AcDbFaceRecord` marker for a face record, and `AcDbVertex` +
 * subtype only for a real vertex). Leaving it out of this set let
 * `mergeSubclassMarkers`' canonical seed leak a spurious `100 AcDbVertex`
 * after `100 AcDbFaceRecord` on export.
 */
const POLYLINE_FAMILY_SUBTYPE_MARKERS: ReadonlySet<string> = new Set<string>([
  ...Object.values(POLYLINE_SUBTYPE_MARKER),
  ...Object.values(VERTEX_SUBTYPE_MARKER),
  "AcDbVertex",
  // A poly-face FACE record replaces the vertex subtype entirely.
  "AcDbFaceRecord",
]);

// SL-2 (DXF R2018 conformance): REVCLOUD is an INTERNAL discriminator type with
// NO DXF entity of the same name — AutoCAD stores a revision cloud as an
// LWPOLYLINE (arc bulges) carrying a `RevcloudProps` XData marker, so the
// group-0 name we serialize must be the REAL DXF entity or external readers
// (AutoCAD / ezdxf strict readfile) reject the unknown `0 REVCLOUD`. The
// subclass-marker chain above is ALREADY the correct LWPOLYLINE body
// (AcDbEntity / AcDbPolyline), so only the group-0 name is overridden here; the
// internal type survives export→import via the RevcloudProps XData discriminator
// (always emitted — see resolveRevcloudXData), which the parser reclassifies on
// read (finalizeSupportedEntity in entityParser.ts).
//
// NOTE (ARRAYRECT — deferred): ARRAYRECT (also a non-DXF name — an AutoCAD
// COMMAND, drawn as an INSERT/MINSERT) is INTENTIONALLY NOT overridden in this
// slice. A non-associative array is byte-identical to a plain MINSERT (INSERT
// with col/row>1) — it has NO entity-level discriminator — and an associative
// array's discriminator is its extension dictionary → ACAD_ASSOCARRAY graph,
// which is only resolvable with OBJECTS-section context (the bridge's assoc
// resolver). The `arrayrect-bespoke-0-name` P1 therefore lands WITH SL-9
// (MINSERT marker) + SL-18 (assoc graph + ext-dict discriminator). See the
// SL-2 report / plan.
const DXF_ENTITY_NAME_OVERRIDE: Partial<Record<DxfSupportedEntityType, string>> =
  {
    REVCLOUD: "LWPOLYLINE",
  };

function mergeSubclassMarkers(
  type: DxfSupportedEntityType,
  parsedMarkers: string[],
): string[] {
  const merged = [...CANONICAL_SUBCLASS_MARKERS[type]];

  for (const marker of parsedMarkers) {
    if (!merged.includes(marker)) {
      merged.push(marker);
    }
  }

  return merged;
}

function writePoint(
  groups: DxfGroup[],
  codes: [number, number, number],
  point: DxfPoint3D,
): void {
  pushGroup(groups, codes[0], point.x);
  pushGroup(groups, codes[1], point.y);
  pushGroup(groups, codes[2], point.z);
}

// SL-7 (edge-points-z): a HATCH boundary EDGE point is emitted as a 2D pair
// (X + Y ONLY) — the AcDbHatch edge-data group codes for line/arc/ellipse edges
// are 10/20 and 11/21 with NO 30/31 (Autodesk DXF reference, HATCH "Boundary
// path data"). ezdxf's Line/Arc/EllipseEdge.export_dxf writes exactly
// `write_tag2(10, x); write_tag2(20, y)` (and 11/21) — never a Z. Our edge
// points are always planar (the parser drops any boundary Z; the model stores
// z=0), so a `30 0`/`31 0` is spurious. Emit 2 codes.
function writePoint2D(
  groups: DxfGroup[],
  codes: [number, number],
  point: { x: number; y: number },
): void {
  pushGroup(groups, codes[0], point.x);
  pushGroup(groups, codes[1], point.y);
}

function writeHatchBoundaryEdge(
  groups: DxfGroup[],
  edge: DxfHatchBoundaryEdge,
): void {
  if (edge.edgeType === "line") {
    pushGroup(groups, 72, 1); // edge type: line
    writePoint2D(groups, [10, 20], edge.start);
    writePoint2D(groups, [11, 21], edge.end);
  } else if (edge.edgeType === "arc") {
    pushGroup(groups, 72, 2); // edge type: arc
    writePoint2D(groups, [10, 20], edge.center);
    pushGroup(groups, 40, edge.radius);
    pushGroup(groups, 50, edge.startAngle);
    pushGroup(groups, 51, edge.endAngle);
    pushGroup(groups, 73, edge.counterclockwise ? 1 : 0);
  } else if (edge.edgeType === "ellipticArc") {
    pushGroup(groups, 72, 3); // edge type: elliptic arc
    writePoint2D(groups, [10, 20], edge.center);
    writePoint2D(groups, [11, 21], edge.majorAxisEndpoint);
    pushGroup(groups, 40, edge.minorAxisRatio);
    pushGroup(groups, 50, edge.startAngle);
    pushGroup(groups, 51, edge.endAngle);
    pushGroup(groups, 73, edge.counterclockwise ? 1 : 0);
  }
}

// DXF path-type bit 1 (value 2) declares the POLYLINE boundary form. We always
// serialize boundaries in the EDGE-BASED form (72-typed line/arc/ellipticArc
// edges) — the model has no polyline edge kind — so the written pathType MUST
// clear bit 2; otherwise a reader (incl. our own parser) would expect polyline
// 10/20 vertices and silently mis-parse the 72-typed edges. ezdxf does the same
// (an EdgePath is exported with PathType.EXTERNAL/OUTERMOST, never POLYLINE).
const PATH_TYPE_POLYLINE_BIT = 2;

function writeHatchBoundaryPath(
  groups: DxfGroup[],
  path: DxfHatchBoundaryPath,
): void {
  pushGroup(groups, 92, path.pathType & ~PATH_TYPE_POLYLINE_BIT);
  pushGroup(groups, 93, path.edges.length);
  for (const edge of path.edges) {
    writeHatchBoundaryEdge(groups, edge);
  }
  // SL-7 (missing-97-source-count): the group-97 SOURCE-boundary-object COUNT is
  // MANDATORY per boundary path and terminates it — Autodesk DXF reference lists
  // "97 Number of source boundary objects" for every AcDbHatch path, and ezdxf's
  // `export_source_boundary_objects` writes `write_tag2(97, len(handles))`
  // UNCONDITIONALLY (i.e. `97 0` when there are none), THEN one group-330
  // soft-pointer per source entity. Previously we gated the WHOLE 97/330 block on
  // `associative && handles.length>0`, so a non-associative hatch (71=0, the
  // common case) emitted NO 97 at all — a rigid forward-scan reader then
  // mis-consumes the following 75/76 as the count. Emit `97 <n>` ALWAYS; emit the
  // 330 handles only when there are any. The count is INDEPENDENT of the 71
  // associativity flag: ezdxf serializes a path's `source_boundary_objects`
  // verbatim regardless of `associative`, and a strict readfile+audit of a 71=0
  // hatch carrying 330 handles is clean (0 errors / 0 fixes) — so preserving them
  // is both lossless and conformant (ПРИОРИТЕТ #1 — ничего не терять).
  const sourceHandles = path.sourceBoundaryHandles ?? [];
  pushGroup(groups, 97, sourceHandles.length);
  for (const handle of sourceHandles) {
    pushGroup(groups, 330, handle);
  }
}

// DXF-ORDER FIX — writeEntityGeometry now OWNS the SPECIFIC subclass marker(s).
// The caller (serializeSupportedEntity) emits `100 AcDbEntity` + the AcDbEntity
// common block; everything from the FIRST specific marker onward is emitted here
// so the markers interleave with their subclass bodies at AutoCAD-conformant
// positions (e.g. ARC = AcDbCircle body THEN `100 AcDbArc` THEN start/end angle).
//
// `specificMarkers` is `mergeSubclassMarkers(type, parsed)` with the leading
// `AcDbEntity` dropped — i.e. the canonical specific markers PLUS any extra
// non-canonical markers the parser saw (which must still round-trip). Each
// case below emits its canonical specific markers at the correct interleave
// points; `emitTrailingExtraMarkers` flushes any remaining non-canonical
// markers after the canonical ones (before the geometry tail) so a parser-seen
// extra `100 …` is preserved.

/**
 * Emit every marker in `specificMarkers` from index `fromIndex` to the end that
 * is NOT one of this type's canonical specific markers (the caller has already
 * emitted the canonical ones positionally). Returns nothing — it flushes the
 * remaining non-canonical tail markers so round-trip of extra `100 …` records
 * is preserved. Canonical specific-marker COUNT = CANONICAL_SUBCLASS_MARKERS
 * minus the leading AcDbEntity, so `fromIndex` is that count.
 */
function emitTrailingExtraMarkers(
  groups: DxfGroup[],
  specificMarkers: string[],
  canonicalSpecificCount: number,
): void {
  for (let i = canonicalSpecificCount; i < specificMarkers.length; i += 1) {
    pushGroup(groups, 100, specificMarkers[i]);
  }
}

/**
 * Emit EVERY specific marker for a SINGLE-SUBCLASS entity whose subclass body
 * does not interleave with multiple markers (INSERT, HATCH, VIEWPORT, MLEADER,
 * ARRAYRECT, REVCLOUD). The body follows all markers. This preserves a parsed
 * non-canonical first marker (e.g. INSERT's `AcDbMInsertBlock` for a MINSERT)
 * AND any extra trailing markers.
 */
function emitAllSpecificMarkers(
  groups: DxfGroup[],
  specificMarkers: string[],
): void {
  for (const marker of specificMarkers) {
    pushGroup(groups, 100, marker);
  }
}

// SL-9 (R2018/AC1032 conformance): the block-reference subtype marker is a
// FUNCTION OF GEOMETRY, not a static entry — a MINSERT (INSERT/ARRAYRECT with a
// grid) replaces `AcDbBlockReference` with `AcDbMInsertBlock`. ezdxf 1.4.3
// `entities/insert.py` `Insert.export_entity` writes a SINGLE subtype marker:
//   if (self.dxf.column_count > 1) or (self.dxf.row_count > 1):
//       write "AcDbMInsertBlock"        # REPLACES AcDbBlockReference
//   else:
//       write "AcDbBlockReference"
// — a pure COUNT predicate (NOT the spacing-gated `mcount`). Because the marker
// can't be expressed by the static CANONICAL_SUBCLASS_MARKERS map, the
// INSERT/ARRAYRECT branches call this helper to emit the ONE correct subtype
// marker and drop BOTH `AcDbBlockReference` and `AcDbMInsertBlock` from the
// merged `specificMarkers`. This dedupe is load-bearing: `mergeSubclassMarkers`
// seeds the canonical `AcDbBlockReference` and then APPENDS a parser-seen
// `AcDbMInsertBlock` (imported MINSERT), so without the drop an imported grid
// would DOUBLE-emit `AcDbBlockReference` + `AcDbMInsertBlock`. Any genuinely
// non-subtype trailing `100 …` marker the parser saw still round-trips.
function emitBlockReferenceSubtypeMarker(
  groups: DxfGroup[],
  specificMarkers: string[],
  isMInsert: boolean,
): void {
  pushGroup(groups, 100, isMInsert ? "AcDbMInsertBlock" : "AcDbBlockReference");
  for (const marker of specificMarkers) {
    if (marker === "AcDbBlockReference" || marker === "AcDbMInsertBlock") {
      continue;
    }
    pushGroup(groups, 100, marker);
  }
}

/**
 * Emit the ONE correct POLYLINE subtype marker for this entity's group-70
 * flags, then any genuinely non-subtype marker the parser saw.
 *
 * Same shape and same reason as {@link emitBlockReferenceSubtypeMarker}: the
 * subtype cannot live in the static CANONICAL_SUBCLASS_MARKERS map because it
 * depends on geometry, and the dedupe is load-bearing — `mergeSubclassMarkers`
 * seeds the canonical `AcDb2dPolyline` and then APPENDS the parser-seen
 * `AcDb3dPolyline`, so without dropping the whole subtype family an imported
 * 3D polyline would emit BOTH markers.
 */
function emitPolylineFamilySubtypeMarker(
  groups: DxfGroup[],
  specificMarkers: string[],
  subtypeMarker: string,
  leadingMarkers: readonly string[] = [],
): void {
  for (const marker of leadingMarkers) {
    pushGroup(groups, 100, marker);
  }
  pushGroup(groups, 100, subtypeMarker);
  for (const marker of specificMarkers) {
    if (
      POLYLINE_FAMILY_SUBTYPE_MARKERS.has(marker) ||
      leadingMarkers.includes(marker)
    ) {
      continue;
    }
    pushGroup(groups, 100, marker);
  }
}

function writeEntityGeometry(
  groups: DxfGroup[],
  entity: DxfSupportedEntity,
  // Specific subclass markers (canonical-minus-AcDbEntity + extra non-canonical)
  // — writeEntityGeometry owns their emission, interleaved with the bodies.
  specificMarkers: string[],
  // T9 PR-3 — when escaping is enabled (legacy R2000 path) the MTEXT writer
  // escapes the full contents FIRST and then chunks the (ASCII) escaped string
  // so a `\U+XXXX` escape is never split across a group-3 boundary AND each
  // chunk stays ≤ 250 chars. Other content groups are escaped at the document
  // chokepoint; only MTEXT needs the early escape because of its chunking.
  escapeUnicode: boolean,
  // Version-gate: emit the WIPEOUT `clip_mode` (group 290) ONLY for a DXF2010+
  // (AC1024) target. Derived from `$ACADVER` in serializeDxfDocument (see
  // versionSupportsClipMode); mirrors ezdxf `ImageBase.export_entity`.
  emitClipMode: boolean,
): void {
  const common = entity.common;
  switch (entity.type) {
    case "LINE": {
      // AcDbLine: 39 thickness, 10/20/30 start, 11/21/31 end, 210/220/230
      // extrusion (ezdxf export order: start, end, thickness, extrusion → in
      // ezdxf 39 follows the points; we emit 39 here after the points to match).
      pushGroup(groups, 100, "AcDbLine");
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      writePoint(groups, [10, 20, 30], entity.geometry.start);
      writePoint(groups, [11, 21, 31], entity.geometry.end);
      writeThickness(groups, common);
      writeExtrusion(groups, common);
      break;
    }
    case "POINT": {
      // AcDbPoint (ezdxf order): location, thickness, extrusion, angle.
      pushGroup(groups, 100, "AcDbPoint");
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      writePoint(groups, [10, 20, 30], entity.geometry.location);
      writeThickness(groups, common);
      writeExtrusion(groups, common);
      // 50 — UCS X-axis angle (radians) used by PDMODE marker shapes.
      // Only emitted when non-zero; default is 0 and ezdxf / AutoCAD
      // omit the group when the value is zero to keep round-trip diffs
      // minimal.
      if (
        entity.geometry.rotation !== undefined &&
        entity.geometry.rotation !== 0
      ) {
        pushGroup(groups, 50, entity.geometry.rotation);
      }
      break;
    }
    case "CIRCLE": {
      // AcDbCircle (ezdxf order): center, radius, thickness, extrusion.
      pushGroup(groups, 100, "AcDbCircle");
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      writePoint(groups, [10, 20, 30], entity.geometry.center);
      pushGroup(groups, 40, entity.geometry.radius);
      writeThickness(groups, common);
      writeExtrusion(groups, common);
      break;
    }
    case "ARC": {
      // ARC = AcDbCircle (center/radius/thickness/extrusion) THEN AcDbArc
      // (start_angle 50, end_angle 51). ezdxf exports the AcDbCircle body
      // under acdb_circle, then writes the AcDbArc marker + 50/51.
      pushGroup(groups, 100, "AcDbCircle");
      writePoint(groups, [10, 20, 30], entity.geometry.center);
      pushGroup(groups, 40, entity.geometry.radius);
      writeThickness(groups, common);
      writeExtrusion(groups, common);
      pushGroup(groups, 100, "AcDbArc");
      emitTrailingExtraMarkers(groups, specificMarkers, 2);
      pushGroup(groups, 50, entity.geometry.startAngle);
      pushGroup(groups, 51, entity.geometry.endAngle);
      break;
    }
    case "ELLIPSE": {
      // AcDbEllipse (ezdxf order): center, major_axis, extrusion (FIRST,
      // before ratio — the ellipse normal is not an OCS but is serialized
      // here), ratio, start_param, end_param.
      const g = entity.geometry;
      pushGroup(groups, 100, "AcDbEllipse");
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      writePoint(groups, [10, 20, 30], g.center);
      writePoint(groups, [11, 21, 31], g.majorAxisEndpoint);
      writeExtrusion(groups, common);
      pushGroup(groups, 40, g.ratio);
      pushGroup(groups, 41, g.startParam);
      pushGroup(groups, 42, g.endParam);
      break;
    }
    case "SPLINE": {
      const g = entity.geometry;
      // AcDbSpline (ezdxf order): extrusion (FIRST), flags(70), degree(71),
      // then the counts 72/73/74, tolerances, tangents, knots, weights, CPs,
      // fit points.
      pushGroup(groups, 100, "AcDbSpline");
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      writeExtrusion(groups, common);
      // Counts must come before knots/control-points/fit-points per
      // AutoCAD's published group ordering.
      pushGroup(groups, 70, g.flags);
      pushGroup(groups, 71, g.degree);
      pushGroup(groups, 72, g.knots.length);
      pushGroup(groups, 73, g.controlPoints.length);
      pushGroup(groups, 74, g.fitPoints.length);
      if (g.knotTolerance !== undefined) {
        pushGroup(groups, 42, g.knotTolerance);
      }
      if (g.controlPointTolerance !== undefined) {
        pushGroup(groups, 43, g.controlPointTolerance);
      }
      if (g.fitTolerance !== undefined) {
        pushGroup(groups, 44, g.fitTolerance);
      }
      if (g.startTangent) {
        writePoint(groups, [12, 22, 32], g.startTangent);
      }
      if (g.endTangent) {
        writePoint(groups, [13, 23, 33], g.endTangent);
      }
      // Knot values
      for (const k of g.knots) {
        pushGroup(groups, 40, k);
      }
      // Per-control-point weights — only emitted when rational, but we
      // round-trip the weight array if it was present in the source.
      for (const w of g.weights) {
        pushGroup(groups, 41, w);
      }
      // Control points
      for (const cp of g.controlPoints) {
        pushGroup(groups, 10, cp.x);
        pushGroup(groups, 20, cp.y);
        pushGroup(groups, 30, cp.z);
      }
      // Fit points
      for (const fp of g.fitPoints) {
        pushGroup(groups, 11, fp.x);
        pushGroup(groups, 21, fp.y);
        pushGroup(groups, 31, fp.z);
      }
      break;
    }
    case "LWPOLYLINE": {
      // AcDbPolyline (ezdxf order): count(90), flags(70), const_width(43),
      // elevation(38), thickness(39), vertices, extrusion(210/220/230).
      const g = entity.geometry;
      pushGroup(groups, 100, "AcDbPolyline");
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      pushGroup(groups, 90, g.vertexCount);
      // DXF group 70: bit 1 = closed, bit 128 = plinegen (continuous
      // linetype generation). Both live in the same bitfield.
      pushGroup(groups, 70, (g.closed ? 1 : 0) | (g.plinegen ? 128 : 0));
      if (g.constantWidth !== undefined && g.constantWidth !== 0) {
        pushGroup(groups, 43, g.constantWidth);
      }
      if (g.elevation !== undefined && g.elevation !== 0) {
        pushGroup(groups, 38, g.elevation);
      }
      writeThickness(groups, common);
      for (const v of g.vertices) {
        writeAcDbPolylineVertex(groups, v);
      }
      writeExtrusion(groups, common);
      break;
    }
    case "TEXT": {
      // AcDbText (ezdxf export_acdb_text order): insert(10/20/30), height(40),
      // text(1), thickness(39), rotation(50), oblique(51), style(7),
      // width(41), text_generation_flag(71), halign(72), align_point(11/21/31),
      // extrusion(210/220/230). Then a SECOND `100 AcDbText` carrying valign(73)
      // (ezdxf acdb_text2 = a doubled AcDbText subclass marker).
      const g = entity.geometry;
      pushGroup(groups, 100, "AcDbText");
      writePoint(groups, [10, 20, 30], g.insertionPoint);
      pushGroup(groups, 40, g.height);
      pushGroup(groups, 1, g.contents);
      writeThickness(groups, common);
      if (g.rotation !== undefined && g.rotation !== 0) {
        pushGroup(groups, 50, g.rotation);
      }
      if (g.obliqueAngle !== undefined && g.obliqueAngle !== 0) {
        pushGroup(groups, 51, g.obliqueAngle);
      }
      if (g.styleName) {
        pushGroup(groups, 7, g.styleName);
      }
      if (g.widthFactor !== undefined && g.widthFactor !== 1) {
        pushGroup(groups, 41, g.widthFactor);
      }
      if (g.generationFlags !== undefined && g.generationFlags !== 0) {
        pushGroup(groups, 71, g.generationFlags);
      }
      if (g.hJustification !== undefined && g.hJustification !== 0) {
        pushGroup(groups, 72, g.hJustification);
      }
      if (g.alignmentPoint) {
        writePoint(groups, [11, 21, 31], g.alignmentPoint);
      }
      writeExtrusion(groups, common);
      // Second AcDbText marker (ezdxf acdb_text2) carries the vertical
      // justification (73). AutoCAD requires the doubled marker even when the
      // value is default; emit the marker only when there is a non-default
      // valign to write, so a default TEXT stays byte-compact (the parser is
      // marker-count tolerant — it routes 73 by code regardless).
      if (g.vJustification !== undefined && g.vJustification !== 0) {
        pushGroup(groups, 100, "AcDbText");
        pushGroup(groups, 73, g.vJustification);
      }
      // Any extra non-canonical markers the parser saw flush after the body.
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      break;
    }
    case "MTEXT": {
      // AcDbMText (ezdxf order): insert(10), char_height(40), width(41),
      // attachment_point(71), flow_direction(72), content(1/3), style(7),
      // extrusion(210/220/230), text_direction(11/21/31), rect_width(42)/
      // rect_height(43), rotation(50), …
      const g = entity.geometry;
      pushGroup(groups, 100, "AcDbMText");
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      writePoint(groups, [10, 20, 30], g.insertionPoint);
      pushGroup(groups, 40, g.nominalHeight);
      if (g.referenceWidth !== undefined) {
        pushGroup(groups, 41, g.referenceWidth);
      }
      if (g.attachmentPoint !== undefined) {
        pushGroup(groups, 71, g.attachmentPoint);
      }
      if (g.drawingDirection !== undefined) {
        pushGroup(groups, 72, g.drawingDirection);
      }
      // Split long text into group 3 chunks (250 chars each) + final group 1.
      // T9 PR-3 — when escaping is on, escape the FULL contents FIRST so the
      // chunking operates on the pure-ASCII escaped string; `chunkEscapedString`
      // never bisects a `\U+XXXX` escape and keeps every chunk ≤ 250 chars. When
      // escaping is off (R2007+ native UTF), chunk the raw contents directly.
      const contents = escapeUnicode
        ? escapeDxfUnicode(g.contents)
        : g.contents;
      const chunkSize = 250;
      if (contents.length > chunkSize) {
        const chunks = chunkEscapedString(contents, chunkSize);
        // All but the final chunk are group 3; the last is group 1.
        for (let c = 0; c < chunks.length - 1; c += 1) {
          pushGroup(groups, 3, chunks[c]);
        }
        pushGroup(groups, 1, chunks[chunks.length - 1]);
      } else {
        pushGroup(groups, 1, contents);
      }
      if (g.styleName) {
        pushGroup(groups, 7, g.styleName);
      }
      // AcDbMText extrusion (210/220/230) — ezdxf emits it after style(7) and
      // before text_direction(11/21/31). Sourced from common.extrusion.
      writeExtrusion(groups, common);
      // xAxisDirection (codes 11/21/31) – skip if default (1,0,0)
      if (
        g.xAxisDirection &&
        !(
          g.xAxisDirection.x === 1 &&
          g.xAxisDirection.y === 0 &&
          g.xAxisDirection.z === 0
        )
      ) {
        writePoint(groups, [11, 21, 31], g.xAxisDirection);
      }
      if (g.horizontalWidth !== undefined && g.horizontalWidth !== 0) {
        pushGroup(groups, 42, g.horizontalWidth);
      }
      if (g.verticalHeight !== undefined && g.verticalHeight !== 0) {
        pushGroup(groups, 43, g.verticalHeight);
      }
      if (g.rotation !== undefined && g.rotation !== 0) {
        pushGroup(groups, 50, g.rotation);
      }
      if (g.lineSpacingStyle !== undefined) {
        pushGroup(groups, 73, g.lineSpacingStyle);
      }
      if (g.lineSpacingFactor !== undefined && g.lineSpacingFactor !== 1) {
        pushGroup(groups, 44, g.lineSpacingFactor);
      }
      // Background fill settings
      if (
        g.backgroundFillSetting !== undefined &&
        g.backgroundFillSetting !== 0
      ) {
        pushGroup(groups, 90, g.backgroundFillSetting);
        if (
          g.backgroundFillColorACI !== undefined &&
          g.backgroundFillColorACI !== 0
        ) {
          pushGroup(groups, 63, g.backgroundFillColorACI);
        }
        if (g.backgroundFillColorRGB !== undefined) {
          pushGroup(groups, 420, g.backgroundFillColorRGB);
        }
        if (
          g.backgroundTransparency !== undefined &&
          g.backgroundTransparency !== 0
        ) {
          pushGroup(groups, 441, g.backgroundTransparency);
        }
        if (g.fillBoxScale !== undefined) {
          pushGroup(groups, 45, g.fillBoxScale);
        }
      }
      // Column settings
      if (g.columnType !== undefined && g.columnType !== 0) {
        pushGroup(groups, 75, g.columnType);
      }
      if (g.columnCount !== undefined && g.columnCount > 1) {
        pushGroup(groups, 76, g.columnCount);
      }
      if (g.columnFlowReversed) {
        pushGroup(groups, 78, 1);
      }
      if (g.columnAutoheight) {
        pushGroup(groups, 79, 1);
      }
      if (g.columnWidth !== undefined && g.columnWidth !== 0) {
        pushGroup(groups, 48, g.columnWidth);
      }
      if (g.columnGutter !== undefined && g.columnGutter !== 0) {
        pushGroup(groups, 49, g.columnGutter);
      }
      // NOTE: per-column heights (g.columnHeights) are intentionally NOT
      // emitted here. A group 46 in the top-level MTEXT body is the SCALAR
      // "defined column height"; AutoCAD/ezdxf store the per-column heights as
      // repeated group 46 INSIDE the `101 AcDbMText` embedded column block
      // (after the column-height-count, group 72). Emitting them in the entity
      // body would make CAD readers misinterpret the first as the defined
      // height and silently drop the rest. Faithful round-trip requires the
      // embedded-object writer path — deferred as S-MTEXT-COLUMN-HEIGHTS
      // (Codex PR #254 P1). The columnHeights model field is preserved in the
      // drawing model / compact cache but not yet serialised to DXF.
      break;
    }
    case "DIMENSION": {
      // DIMENSION = AcDbDimension (the common dimension body) THEN a
      // data-driven SECOND specific marker (AcDbAlignedDimension /
      // AcDbRotatedDimension / AcDbRadialDimension / AcDb3PointAngularDimension /
      // AcDbOrdinateDimension …) that carries the per-subtype defpoints + angle.
      //
      // The 2nd marker is NOT in CANONICAL_SUBCLASS_MARKERS (which lists only
      // [AcDbEntity, AcDbDimension]); for an IMPORTED dim it rides
      // `entity.subclassMarkers` (parsed from the source) → it arrives in
      // `specificMarkers` after AcDbDimension and is flushed verbatim between the
      // AcDbDimension body and the subtype-specific defpoint block (source-
      // preserve; no double-emit). For a FROM-SCRATCH / marker-less dim
      // (`specificMarkers === ["AcDbDimension"]`, no trailing marker) the 2nd
      // marker(s) are SYNTHESIZED from `dimensionType & 7` via the one-truth
      // `dimensionSubtypeSubclassMarkers` — the by-construction safety net that
      // closes SL-3 P1 (`missing-subtype-subclass-marker`). Note the type-0
      // (rotated linear) DUAL-marker case: AcDbAlignedDimension THEN the empty-
      // but-required AcDbRotatedDimension (ezdxf/AutoCAD both emit both).
      //
      // AcDbDimension body order (ezdxf acdb_dimension export list): version(280),
      // geometry/block(2), dimstyle(3), defpoint(10), text_midpoint(11),
      // insert(12), dimtype(70), attachment_point(71), line_spacing_style(72),
      // line_spacing_factor(41), actual_measurement(42), text(1), oblique(52),
      // text_rotation(53), horizontal_direction(51), thickness(39),
      // extrusion(210/220/230). NOTE our model's field NAMES differ from ezdxf
      // but the GROUP CODES match: code 51 = obliqueAngle, code 52 = textRotation,
      // code 53 = textAngleRelativeToDimLine. The subtype defpoints 13/14/15/16,
      // the angle 50 and leader_length 40 belong to the SECOND subclass and are
      // emitted after its marker (the parser routes them by code regardless of
      // which subtype subclass they appeared under, so emitting all present ones
      // is round-trip-faithful).
      const g = entity.geometry;
      pushGroup(groups, 100, "AcDbDimension");
      // ── AcDbDimension body ──
      if (g.version !== undefined && g.version !== 0) {
        pushGroup(groups, 280, g.version);
      }
      if (g.anonymousBlockName) {
        pushGroup(groups, 2, g.anonymousBlockName);
      }
      if (g.styleName) {
        pushGroup(groups, 3, g.styleName);
      }
      writePoint(groups, [10, 20, 30], g.defPoint);
      writePoint(groups, [11, 21, 31], g.textMidpoint);
      if (g.insertionPoint) {
        writePoint(groups, [12, 22, 32], g.insertionPoint);
      }
      pushGroup(groups, 70, g.dimensionType);
      // Group 71 — attachment point. AutoCAD default is 5 (Middle-Center);
      // emit only a non-default value to keep default dimensions byte-compact.
      if (g.attachmentPoint !== undefined && g.attachmentPoint !== 5) {
        pushGroup(groups, 71, g.attachmentPoint);
      }
      if (g.lineSpacingStyle !== undefined) {
        pushGroup(groups, 72, g.lineSpacingStyle);
      }
      if (g.lineSpacingFactor !== undefined && g.lineSpacingFactor !== 1) {
        pushGroup(groups, 41, g.lineSpacingFactor);
      }
      if (g.measurement !== undefined) {
        pushGroup(groups, 42, g.measurement);
      }
      if (g.dimensionText !== undefined) {
        pushGroup(groups, 1, g.dimensionText);
      }
      if (g.obliqueAngle !== undefined && g.obliqueAngle !== 0) {
        pushGroup(groups, 51, g.obliqueAngle);
      }
      if (g.textRotation !== undefined && g.textRotation !== 0) {
        pushGroup(groups, 52, g.textRotation);
      }
      if (
        g.textAngleRelativeToDimLine !== undefined &&
        g.textAngleRelativeToDimLine !== 0
      ) {
        pushGroup(groups, 53, g.textAngleRelativeToDimLine);
      }
      writeThickness(groups, common);
      writeExtrusion(groups, common);
      // ── SECOND (data-driven) subtype marker(s) + per-subtype defpoint block ──
      // Trailing markers (index ≥ 1) carried from the source / populated by the
      // export boundary are emitted verbatim; when NONE are present (marker-less
      // from-scratch dim) synthesize them from the subtype so the following
      // defpoints (13/14/15/16, 50, 40) always have an owning 2nd subclass.
      //
      // SL-3 P2 — CANONICAL LAYOUT: the subtype defpoints/params belong UNDER the
      // FIRST subtype marker; any TRAILING marker (the type-0 dual case's empty-
      // but-required `AcDbRotatedDimension`) is emitted LAST, AFTER the fields.
      // Emitting all markers up-front would orphan the defpoints under the empty
      // trailing `AcDbRotatedDimension` (malformed). Verified: ezdxf 1.4.3
      // `entities/dimension.py` (dim_type==0 → `AcDbAlignedDimension` + insert/
      // defpoints/angle THEN empty `AcDbRotatedDimension`) + Autodesk R2018
      // "Linear and Rotated Dimension Group Codes" (GUID-F0004556). This order
      // ALSO restores byte-faithful round-trip for imported type-0 dims (whose
      // source had the fields between the two markers). Single-marker subtypes
      // (1-6) are unaffected (marker THEN fields, no trailing marker).
      const trailingSubtypeMarkers = specificMarkers.slice(1);
      const subtypeMarkers =
        trailingSubtypeMarkers.length > 0
          ? trailingSubtypeMarkers
          : dimensionSubtypeSubclassMarkers(g.dimensionType);
      const [firstSubtypeMarker, ...restSubtypeMarkers] = subtypeMarkers;
      if (firstSubtypeMarker !== undefined) {
        pushGroup(groups, 100, firstSubtypeMarker);
      }
      if (g.extLine1Point) {
        writePoint(groups, [13, 23, 33], g.extLine1Point);
      }
      if (g.extLine2Point) {
        writePoint(groups, [14, 24, 34], g.extLine2Point);
      }
      if (g.thirdPoint) {
        writePoint(groups, [15, 25, 35], g.thirdPoint);
      }
      if (g.arcPoint) {
        writePoint(groups, [16, 26, 36], g.arcPoint);
      }
      if (g.linearAngle !== undefined && g.linearAngle !== 0) {
        pushGroup(groups, 50, g.linearAngle);
      }
      if (g.leaderLength !== undefined && g.leaderLength !== 0) {
        pushGroup(groups, 40, g.leaderLength);
      }
      // Trailing markers (e.g. the empty `AcDbRotatedDimension` for type 0) LAST.
      for (const marker of restSubtypeMarkers) {
        pushGroup(groups, 100, marker);
      }
      break;
    }
    case "INSERT": {
      // AcDbBlockReference, or AcDbMInsertBlock when the INSERT carries a grid
      // (columnCount>1 OR rowCount>1) — a MINSERT. The subtype marker is a pure
      // COUNT predicate per ezdxf `Insert.export_entity` and REPLACES (not
      // augments) AcDbBlockReference, so exactly ONE is emitted; see
      // emitBlockReferenceSubtypeMarker for the drop of the merged
      // AcDbBlockReference/AcDbMInsertBlock pair. Self-contained body: 66, 2, 10,
      // 41/42/43, 50, 70/71, 44/45, 210/220/230. INSERT emits its OCS normal
      // from `g.extrusion` (single-channel — the COMMON extrusion writer is
      // gone, so there is no double-emit) and never calls writeExtrusion.
      const g = entity.geometry;
      const isMInsert =
        (g.columnCount !== undefined && g.columnCount > 1) ||
        (g.rowCount !== undefined && g.rowCount > 1);
      emitBlockReferenceSubtypeMarker(groups, specificMarkers, isMInsert);
      // Group 66 (attributesFollow) must appear BEFORE the block name.
      // AutoCAD emits it immediately after the subclass marker; our
      // writeEntityGeometry runs after subclass markers, so we emit 66
      // as the first geometry group when true.
      if (g.attributesFollow === true) {
        pushGroup(groups, 66, 1);
      }
      pushGroup(groups, 2, g.blockName);
      writePoint(groups, [10, 20, 30], g.insertionPoint);
      if (g.scaleX !== undefined && g.scaleX !== 1) {
        pushGroup(groups, 41, g.scaleX);
      }
      if (g.scaleY !== undefined && g.scaleY !== 1) {
        pushGroup(groups, 42, g.scaleY);
      }
      if (g.scaleZ !== undefined && g.scaleZ !== 1) {
        pushGroup(groups, 43, g.scaleZ);
      }
      if (g.rotation !== undefined && g.rotation !== 0) {
        pushGroup(groups, 50, g.rotation);
      }
      if (g.columnCount !== undefined && g.columnCount !== 1) {
        pushGroup(groups, 70, g.columnCount);
      }
      if (g.rowCount !== undefined && g.rowCount !== 1) {
        pushGroup(groups, 71, g.rowCount);
      }
      if (g.columnSpacing !== undefined && g.columnSpacing !== 0) {
        pushGroup(groups, 44, g.columnSpacing);
      }
      if (g.rowSpacing !== undefined && g.rowSpacing !== 0) {
        pushGroup(groups, 45, g.rowSpacing);
      }
      if (
        g.extrusion &&
        !(g.extrusion.x === 0 && g.extrusion.y === 0 && g.extrusion.z === 1)
      ) {
        pushGroup(groups, 210, g.extrusion.x);
        pushGroup(groups, 220, g.extrusion.y);
        pushGroup(groups, 230, g.extrusion.z);
      }
      break;
    }
    case "ATTDEF": {
      // ATTDEF = AcDbText (the text body) THEN AcDbAttributeDefinition.
      // AcDbText body (ezdxf BaseAttrib export_acdb_attrib_text-style order):
      // insert(10), height(40), text(1)=defaultValue, thickness(39),
      // rotation(50), oblique(51), style(7), width(41), text_generation_flag(71),
      // halign(72), align_point(11), extrusion(210/220/230).
      // AcDbAttributeDefinition body: prompt(3), tag(2), flags(70),
      // field_length(73), valign(74), lock_position(280) + bg-fill (R2018).
      const g = entity.geometry;
      pushGroup(groups, 100, "AcDbText");
      writePoint(groups, [10, 20, 30], g.insertionPoint);
      pushGroup(groups, 40, g.height);
      pushGroup(groups, 1, g.defaultValue);
      writeThickness(groups, common);
      if (g.rotation !== undefined && g.rotation !== 0)
        pushGroup(groups, 50, g.rotation);
      if (g.obliqueAngle !== undefined && g.obliqueAngle !== 0)
        pushGroup(groups, 51, g.obliqueAngle);
      if (g.styleName) pushGroup(groups, 7, g.styleName);
      if (g.widthFactor !== undefined && g.widthFactor !== 1)
        pushGroup(groups, 41, g.widthFactor);
      if (g.generationFlags !== undefined && g.generationFlags !== 0)
        pushGroup(groups, 71, g.generationFlags);
      if (g.hJustification !== undefined && g.hJustification !== 0)
        pushGroup(groups, 72, g.hJustification);
      if (g.alignmentPoint) writePoint(groups, [11, 21, 31], g.alignmentPoint);
      writeExtrusion(groups, common);
      // ── AcDbAttributeDefinition ──
      pushGroup(groups, 100, "AcDbAttributeDefinition");
      emitTrailingExtraMarkers(groups, specificMarkers, 2);
      pushGroup(groups, 3, g.prompt);
      pushGroup(groups, 2, g.tag);
      pushGroup(groups, 70, g.flags);
      if (g.fieldLength !== undefined) pushGroup(groups, 73, g.fieldLength);
      if (g.vJustification !== undefined && g.vJustification !== 0)
        pushGroup(groups, 74, g.vJustification);
      if (g.lockPosition !== undefined)
        pushGroup(groups, 280, g.lockPosition ? 1 : 0);
      if (g.backgroundFill !== undefined && g.backgroundFill !== 0) {
        pushGroup(groups, 90, g.backgroundFill);
        if (
          g.backgroundFillColorACI !== undefined &&
          g.backgroundFillColorACI !== 0
        ) {
          pushGroup(groups, 63, g.backgroundFillColorACI);
        }
        if (g.backgroundFillColorRGB !== undefined) {
          pushGroup(groups, 420, g.backgroundFillColorRGB);
        }
        if (
          g.backgroundTransparency !== undefined &&
          g.backgroundTransparency !== 0
        ) {
          pushGroup(groups, 441, g.backgroundTransparency);
        }
        if (g.fillBoxScale !== undefined) {
          pushGroup(groups, 45, g.fillBoxScale);
        }
      }
      break;
    }
    case "ATTRIB": {
      // ATTRIB = AcDbText (the text body) THEN AcDbAttribute. Same AcDbText body
      // shape as ATTDEF (text(1)=value); AcDbAttribute body has NO prompt:
      // tag(2), flags(70), field_length(73), valign(74), lock_position(280)
      // + bg-fill (R2018).
      const g = entity.geometry;
      pushGroup(groups, 100, "AcDbText");
      writePoint(groups, [10, 20, 30], g.insertionPoint);
      pushGroup(groups, 40, g.height);
      pushGroup(groups, 1, g.value);
      writeThickness(groups, common);
      // GitHub Codex review PR #558 (formal bot review, HEAD 4fe0a323) P2 —
      // "Preserve explicit zero ATTRIB generation flags", fixed at its
      // named site (`dxfBridge.attribExportPosition.ts`'s WORLD/ORPHAN
      // branches) alongside the SAME class of bug discovered here for
      // `rotation`, in the SAME case block. UNLIKE TEXT/ATTDEF's own
      // `generationFlags`/`rotation` (backed by `.default(0)` model
      // fields — no presence to preserve, so `!== 0` is correct there),
      // ATTRIB's WORLD-placed regime (`BlockInsertAttribute
      // .overrideRotation`/`.overrideGenerationFlags`, both `.optional()`
      // in `model.ts`) passes its value through VERBATIM with no
      // resolved-value collapse (`resolveAttribExportGeometry`'s WORLD
      // branch) — the ORPHAN/block-local branches already collapse a
      // genuinely-resolved 0 to `undefined` upstream (`emitRotation`/
      // `emitFlags`) before it ever reaches this transport object, so
      // `g.rotation`/`g.generationFlags` arriving here as an EXPLICIT `0`
      // can only have come from the WORLD branch's byte-faithful
      // passthrough — this writer's OWN `!== 0` re-check was therefore
      // always redundant-and-harmful for that one case, silently
      // dropping an explicitly-authored group 50/71 = 0 on export.
      if (g.rotation !== undefined) pushGroup(groups, 50, g.rotation);
      if (g.obliqueAngle !== undefined && g.obliqueAngle !== 0)
        pushGroup(groups, 51, g.obliqueAngle);
      if (g.styleName) pushGroup(groups, 7, g.styleName);
      if (g.widthFactor !== undefined && g.widthFactor !== 1)
        pushGroup(groups, 41, g.widthFactor);
      if (g.generationFlags !== undefined)
        pushGroup(groups, 71, g.generationFlags);
      if (g.hJustification !== undefined && g.hJustification !== 0)
        pushGroup(groups, 72, g.hJustification);
      if (g.alignmentPoint) writePoint(groups, [11, 21, 31], g.alignmentPoint);
      writeExtrusion(groups, common);
      // ── AcDbAttribute ──
      pushGroup(groups, 100, "AcDbAttribute");
      emitTrailingExtraMarkers(groups, specificMarkers, 2);
      pushGroup(groups, 2, g.tag);
      pushGroup(groups, 70, g.flags);
      if (g.fieldLength !== undefined) pushGroup(groups, 73, g.fieldLength);
      if (g.vJustification !== undefined && g.vJustification !== 0)
        pushGroup(groups, 74, g.vJustification);
      if (g.lockPosition !== undefined)
        pushGroup(groups, 280, g.lockPosition ? 1 : 0);
      if (g.backgroundFill !== undefined && g.backgroundFill !== 0) {
        pushGroup(groups, 90, g.backgroundFill);
        if (
          g.backgroundFillColorACI !== undefined &&
          g.backgroundFillColorACI !== 0
        ) {
          pushGroup(groups, 63, g.backgroundFillColorACI);
        }
        if (g.backgroundFillColorRGB !== undefined) {
          pushGroup(groups, 420, g.backgroundFillColorRGB);
        }
        if (
          g.backgroundTransparency !== undefined &&
          g.backgroundTransparency !== 0
        ) {
          pushGroup(groups, 441, g.backgroundTransparency);
        }
        if (g.fillBoxScale !== undefined) {
          pushGroup(groups, 45, g.fillBoxScale);
        }
      }
      break;
    }
    case "POLYLINE": {
      // AcDb<subtype> body, in ezdxf 1.4.4 `Polyline.export_entity` order:
      // 66 (vertices follow), elevation(10/20/30), flags(70), default widths
      // (40/41), mesh counts(71/72), smooth densities(73/74), surface type(75),
      // thickness(39), extrusion(210/220/230).
      const g = entity.geometry;
      emitPolylineFamilySubtypeMarker(
        groups,
        specificMarkers,
        POLYLINE_SUBTYPE_MARKER[dxfPolylineSubtype(g.flags)],
      );
      // Group 66 is obsolete but still written by AutoCAD and ezdxf (which
      // hard-codes `write_tag2(66, 1)`). Re-emit the parsed value when the
      // source had one; otherwise 1, matching ezdxf — a POLYLINE always owns
      // a VERTEX run, so the flag is never legitimately 0 on export.
      pushGroup(groups, 66, g.verticesFollow ?? 1);
      writePoint(groups, [10, 20, 30], g.elevation);
      pushGroup(groups, 70, g.flags);
      // PRESENCE-preserving, not "emit when non-zero": on a heavy polyline the
      // 40/41 defaults are what every vertex WITHOUT its own 40/41 inherits, so
      // whether the group exists is itself information. Dropping an authored
      // `40 0` would silently re-type "this polyline declares a zero default"
      // as "this polyline declares no default".
      if (g.defaultStartWidth !== undefined) {
        pushGroup(groups, 40, g.defaultStartWidth);
      }
      if (g.defaultEndWidth !== undefined) {
        pushGroup(groups, 41, g.defaultEndWidth);
      }
      if (g.meshMCount !== undefined) pushGroup(groups, 71, g.meshMCount);
      if (g.meshNCount !== undefined) pushGroup(groups, 72, g.meshNCount);
      if (g.smoothSurfaceMDensity !== undefined) {
        pushGroup(groups, 73, g.smoothSurfaceMDensity);
      }
      if (g.smoothSurfaceNDensity !== undefined) {
        pushGroup(groups, 74, g.smoothSurfaceNDensity);
      }
      if (g.surfaceType !== undefined) pushGroup(groups, 75, g.surfaceType);
      writeThickness(groups, common);
      writeExtrusion(groups, common);
      break;
    }
    case "VERTEX": {
      // AcDbVertex + AcDb<subtype>Vertex (or the bare AcDbFaceRecord for a
      // poly-face FACE record), then the body in ezdxf `DXFVertex.
      // export_entity` order: location(10/20/30), start/end width(40/41),
      // bulge(42), flags(70), tangent(50), face indices(71..74), id(91).
      const g = entity.geometry;
      const subtype = dxfVertexSubtype(g.flags);
      if (subtype === "face-record") {
        // ezdxf writes the SINGLE `AcDbFaceRecord` marker for a face record —
        // no AcDbVertex ahead of it.
        emitPolylineFamilySubtypeMarker(groups, specificMarkers, "AcDbFaceRecord");
      } else {
        emitPolylineFamilySubtypeMarker(
          groups,
          specificMarkers,
          VERTEX_SUBTYPE_MARKER[subtype],
          ["AcDbVertex"],
        );
      }
      writePoint(groups, [10, 20, 30], g.location);
      // PRESENCE-preserving (property-test counterexample: a vertex authored
      // with `40 0`). On a VERTEX the width groups are an OVERRIDE of the
      // owning POLYLINE's 40/41 defaults, so "absent" and "present and 0" are
      // DIFFERENT: absent inherits the parent's width, an explicit 0 forces
      // zero width. Emitting only non-zero values turned an explicit
      // override-to-zero into an inherit, changing what the polyline renders
      // whenever its parent default is non-zero.
      if (g.startWidth !== undefined) {
        pushGroup(groups, 40, g.startWidth);
      }
      if (g.endWidth !== undefined) {
        pushGroup(groups, 41, g.endWidth);
      }
      if (g.bulge !== undefined && g.bulge !== 0) {
        pushGroup(groups, 42, g.bulge);
      }
      if (g.flags !== undefined) pushGroup(groups, 70, g.flags);
      if (g.curveFitTangent !== undefined) {
        pushGroup(groups, 50, g.curveFitTangent);
      }
      if (g.faceVertexIndices) {
        // Codes 71..74 in array order — the parser only builds this array from
        // a CONTIGUOUS run starting at 71, so the codes emitted here are
        // exactly the codes the source authored.
        for (let i = 0; i < g.faceVertexIndices.length; i += 1) {
          pushGroup(groups, 71 + i, g.faceVertexIndices[i]!);
        }
      }
      if (g.vertexId !== undefined) pushGroup(groups, 91, g.vertexId);
      break;
    }
    case "ACAD_TABLE": {
      // Two subclasses back to back. AcDbEntity-level cached graphics
      // (160 + 310*) come FIRST — AutoCAD R2018+ writes them ahead of
      // `100 AcDbBlockReference`, which is where the parser read them.
      const g = entity.geometry;
      if (g.cachedGraphicsByteCount !== undefined) {
        pushGroup(groups, 160, g.cachedGraphicsByteCount);
      }
      for (const chunk of g.cachedGraphicsChunks) {
        pushGroup(groups, 310, chunk);
      }
      pushGroup(groups, 100, "AcDbBlockReference");
      pushGroup(groups, 2, g.blockName);
      writePoint(groups, [10, 20, 30], g.insertionPoint);
      pushGroup(groups, 100, "AcDbTable");
      if (g.version !== undefined) pushGroup(groups, 280, g.version);
      if (g.tableStyleHandle !== undefined) {
        pushGroup(groups, 342, g.tableStyleHandle);
      }
      if (g.blockRecordHandle !== undefined) {
        pushGroup(groups, 343, g.blockRecordHandle);
      }
      if (g.horizontalDirection) {
        writePoint(groups, [11, 21, 31], g.horizontalDirection);
      }
      if (g.valueFlag !== undefined) pushGroup(groups, 90, g.valueFlag);
      if (g.rowCount !== undefined) pushGroup(groups, 91, g.rowCount);
      if (g.columnCount !== undefined) pushGroup(groups, 92, g.columnCount);
      if (g.overrideFlag !== undefined) pushGroup(groups, 93, g.overrideFlag);
      if (g.borderColorOverrideFlag !== undefined) {
        pushGroup(groups, 94, g.borderColorOverrideFlag);
      }
      if (g.borderLineweightOverrideFlag !== undefined) {
        pushGroup(groups, 95, g.borderLineweightOverrideFlag);
      }
      if (g.borderVisibilityOverrideFlag !== undefined) {
        pushGroup(groups, 96, g.borderVisibilityOverrideFlag);
      }
      // The row/column sizing and per-cell records, verbatim and in file
      // order. `rowHeights` / `columnWidths` are a READ-ONLY projection over
      // these same groups (see the type doc) — emitting them separately would
      // double-emit every 141/142.
      groups.push(...g.contentGroups);
      // Any extra non-canonical markers the parser saw flush after the body;
      // both canonical specific markers were emitted positionally above.
      emitTrailingExtraMarkers(groups, specificMarkers, 2);
      break;
    }
    case "OLE2FRAME": {
      // AcDbOle2Frame, in the Autodesk DXF reference's documented order:
      // 70 version, 3 object type, 10/20/30 upper-left, 11/21/31 lower-right,
      // 71 object type, 72 tile mode, 73 draw aspect, 90 byte count,
      // 310* payload, 1 "OLE" terminator.
      const g = entity.geometry;
      pushGroup(groups, 100, "AcDbOle2Frame");
      emitTrailingExtraMarkers(groups, specificMarkers, 1);
      if (g.oleVersion !== undefined) pushGroup(groups, 70, g.oleVersion);
      if (g.objectDescription !== undefined) {
        pushGroup(groups, 3, g.objectDescription);
      }
      writePoint(groups, [10, 20, 30], g.upperLeft);
      writePoint(groups, [11, 21, 31], g.lowerRight);
      if (g.oleObjectType !== undefined) pushGroup(groups, 71, g.oleObjectType);
      if (g.tileModeDescriptor !== undefined) {
        pushGroup(groups, 72, g.tileModeDescriptor);
      }
      if (g.drawAspect !== undefined) pushGroup(groups, 73, g.drawAspect);
      if (g.binaryByteCount !== undefined) {
        pushGroup(groups, 90, g.binaryByteCount);
      }
      // The OLE compound-document payload, verbatim. NEVER decoded — see
      // `DxfOle2FrameEntity`'s type doc for why the embedded EMF raster is
      // deliberately not rendered.
      for (const chunk of g.binaryChunks) {
        pushGroup(groups, 310, chunk);
      }
      if (g.terminator !== undefined) pushGroup(groups, 1, g.terminator);
      break;
    }
    case "SEQEND": {
      // Pure terminator (AcDbEntity only — no specific subclass marker). The
      // common props (handle, layer, …) already ran through PRE_ENTITY_WRITERS
      // + ACDBENTITY_WRITERS; specificMarkers is empty for the canonical SEQEND
      // (CANONICAL_SUBCLASS_MARKERS.SEQEND = [AcDbEntity]). Flush any extra
      // non-canonical markers a source SEQEND somehow carried, then nothing more.
      emitAllSpecificMarkers(groups, specificMarkers);
      break;
    }
    case "HATCH": {
      // AcDbHatch — self-contained, single-channel extrusion from
      // geometry.extrusion (the COMMON extrusion writer is gone).
      emitAllSpecificMarkers(groups, specificMarkers);
      const g = entity.geometry;
      // S-HATCH-ELEVATION-XY — the AcDbHatch elevation POINT (groups 10/20/30)
      // is written in its canonical LEADING position (before the pattern name),
      // matching AutoCAD's layout and the parser's leading-elevation readers.
      // Emit only when a component is non-zero so default (0,0,0) hatches stay
      // byte-compact. The 210/220/230 extrusion (S-HATCH-EXTRUSION) is emitted
      // from `geometry.extrusion` ONLY (single-channel) by the block immediately
      // below (SL-7 extrusion-order: right after this elevation point); the
      // COMMON group-writer skips HATCH. The Z component round-trips through this
      // leading 30 (NOT a separate post-boundary 30 — that would write Z twice).
      const elevX = g.elevationX ?? 0;
      const elevY = g.elevationY ?? 0;
      const elevZ = g.elevation ?? 0;
      if (elevX !== 0 || elevY !== 0 || elevZ !== 0) {
        pushGroup(groups, 10, elevX);
        pushGroup(groups, 20, elevY);
        pushGroup(groups, 30, elevZ);
      }
      // SL-7 (extrusion-order): the OCS normal 210/220/230 is emitted RIGHT AFTER
      // the elevation point (10/20/30) and BEFORE the pattern name (2) — the
      // AcDbHatch subclass order (Autodesk DXF reference) and ezdxf's
      // `Hatch.export_entity`, which serializes attribs in the order
      // `elevation, extrusion, pattern_name, solid_fill, associative` then the
      // boundary paths. It was previously emitted LAST (after the gradient block),
      // which is a unique-code mis-order. The identity (0,0,1) normal is the
      // default and is suppressed to keep output byte-compact (a reader
      // reconstructs it); this is the SOLE extrusion emit point for HATCH (the
      // COMMON group-writer skips HATCH — single channel, no double-write).
      if (g.extrusion && !isDefaultExtrusion(g.extrusion)) {
        pushGroup(groups, 210, g.extrusion.x);
        pushGroup(groups, 220, g.extrusion.y);
        pushGroup(groups, 230, g.extrusion.z);
      }
      pushGroup(groups, 2, g.patternName);
      pushGroup(groups, 70, g.solidFill ? 1 : 0);
      pushGroup(groups, 71, g.associative ? 1 : 0);
      // Write boundary paths
      pushGroup(groups, 91, g.boundaryPaths.length);
      for (const path of g.boundaryPaths) {
        writeHatchBoundaryPath(groups, path);
      }
      // SL-7 (style-pattern-type-zero): hatch style (75) and pattern type (76)
      // are emitted UNCONDITIONALLY with their defaults (0 = odd-parity / nested,
      // 1 = predefined). In ezdxf `acdb_hatch` both `hatch_style` and
      // `pattern_type` are non-optional attribs (always exported), and AutoCAD
      // always writes them. Emitting only when defined dropped a legitimate
      // `75 0` / `76 0`: a source `76 0` (user-defined pattern type) then re-reads
      // as the reader default 1 (predefined) — a silent metadata flip. `?? default`
      // keeps a dxf-io-only path (no bridge) conformant even when the field is
      // absent; the bridge now always projects the model's value (schema default
      // 0/1) so an authored hatch carries them through.
      pushGroup(groups, 75, g.hatchStyle ?? 0);
      pushGroup(groups, 76, g.patternType ?? 1);
      if (g.patternAngle !== undefined && g.patternAngle !== 0) {
        pushGroup(groups, 52, g.patternAngle);
      }
      if (g.patternScale !== undefined && g.patternScale !== 1) {
        pushGroup(groups, 41, g.patternScale);
      }
      // S-HATCH-CUSTOM — pattern data block. Order per AcDbHatch (DXF reference
      // / ezdxf Hatch.export_dxf): the double-line flag (77) then the
      // pattern-definition-line count (78) then each line, AFTER 52/41
      // (angle/scale) and BEFORE 47 (pixel size) / 98 (seed points). Group 77 is
      // the pattern double flag (0=not double, 1=double); group 73 is a
      // BOUNDARY-path code and is NEVER emitted here. Each line: 53 (angle),
      // 43/44 (base x/y), 45/46 (offset x/y), 79 (dash count), then `count`
      // repetitions of 49 (dash length). Emit 77 only when present (true OR
      // false — both round-trip a source that carried a 77); emit the 78 block
      // only when there are defining lines (solid/predefined → none).
      if (g.doubleLine !== undefined) {
        pushGroup(groups, 77, g.doubleLine ? 1 : 0);
      }
      if (g.customPatternLines && g.customPatternLines.length > 0) {
        pushGroup(groups, 78, g.customPatternLines.length);
        for (const line of g.customPatternLines) {
          pushGroup(groups, 53, line.angle);
          pushGroup(groups, 43, line.base.x);
          pushGroup(groups, 44, line.base.y);
          pushGroup(groups, 45, line.offset.x);
          pushGroup(groups, 46, line.offset.y);
          pushGroup(groups, 79, line.dashLengths.length);
          for (const dash of line.dashLengths) {
            pushGroup(groups, 49, dash);
          }
        }
      }
      // S-HATCH-PIXELSIZE — DXF group 47 (preserved verbatim).
      if (g.pixelSize !== undefined) {
        pushGroup(groups, 47, g.pixelSize);
      }
      // Seed points
      if (g.seedPoints && g.seedPoints.length > 0) {
        pushGroup(groups, 98, g.seedPoints.length);
        for (const sp of g.seedPoints) {
          pushGroup(groups, 10, sp.x);
          pushGroup(groups, 20, sp.y);
        }
      }
      // Gradient fill (group 450..470). Emit ONLY when the entity carries
      // a gradient definition so solid/pattern hatches stay byte-identical.
      // Group ORDER matches what AutoCAD writes (verified against fixture
      // tests-fixtures/autocad-references/hatch-patterns-showcase.dxf):
      //   450, 451, 460, 461, 452, 462, 453, [463, 63, 421]*, 470
      // ezdxf's GradientData.export_dxf emits the same scalar block then
      // the color stops then the name. The fixture's stops carry 463+421
      // (no explicit 63); we emit 63 only when the model supplies an ACI
      // so a stop's optional 421 is preserved exactly as read.
      if (g.gradient) {
        const grad = g.gradient;
        pushGroup(groups, 450, grad.kind);
        pushGroup(groups, 451, grad.reserved);
        pushGroup(groups, 460, grad.rotation);
        pushGroup(groups, 461, grad.shift);
        pushGroup(groups, 452, grad.oneColor);
        pushGroup(groups, 462, grad.tint);
        pushGroup(groups, 453, grad.numberOfColors);
        for (const color of grad.colors) {
          pushGroup(groups, 463, color.position);
          // 63 (ACI) + 421 (true color) are each emitted ONLY when the model
          // carries them, so a stop read without that code is written without
          // it — no spurious `63 0` injected (byte-faithful round-trip).
          if (color.aci !== undefined) {
            pushGroup(groups, 63, color.aci);
          }
          if (color.trueColor !== undefined) {
            pushGroup(groups, 421, color.trueColor);
          }
        }
        pushGroup(groups, 470, grad.name);
      }
      // NOTE: the OCS normal (210/220/230) is emitted in its canonical position
      // right after the elevation point, ABOVE (SL-7 extrusion-order) — NOT here.
      break;
    }
    case "VIEWPORT": {
      // AcDbViewport — self-contained typed block. Marker(s) precede the body.
      emitAllSpecificMarkers(groups, specificMarkers);
      const g = entity.geometry;
      writePoint(groups, [10, 20, 30], g.center);
      pushGroup(groups, 40, g.width);
      pushGroup(groups, 41, g.height);
      if (g.viewportStatus !== undefined) {
        pushGroup(groups, 68, g.viewportStatus);
      }
      if (g.viewportId !== undefined) {
        pushGroup(groups, 69, g.viewportId);
      }
      if (g.viewCenter) {
        pushGroup(groups, 12, g.viewCenter.x);
        pushGroup(groups, 22, g.viewCenter.y);
      }
      if (g.viewHeight !== undefined) {
        pushGroup(groups, 45, g.viewHeight);
      }
      if (g.viewDirection) {
        writePoint(groups, [16, 26, 36], g.viewDirection);
      }
      if (g.viewTarget) {
        writePoint(groups, [17, 27, 37], g.viewTarget);
      }
      if (g.viewTwistAngle !== undefined && g.viewTwistAngle !== 0) {
        pushGroup(groups, 51, g.viewTwistAngle);
      }
      if (g.lensLength !== undefined && g.lensLength !== 50) {
        pushGroup(groups, 42, g.lensLength);
      }
      if (g.frontClipZ !== undefined && g.frontClipZ !== 0) {
        pushGroup(groups, 43, g.frontClipZ);
      }
      if (g.backClipZ !== undefined && g.backClipZ !== 0) {
        pushGroup(groups, 44, g.backClipZ);
      }
      if (g.statusBitFlags !== undefined) {
        pushGroup(groups, 90, g.statusBitFlags);
      }
      // S-VIEWPORT-CLIP-HANDLE — clipping-boundary handle (340). Per ezdxf 1.4.3
      // acdb_viewport group order, `clipping_boundary_handle` (340) follows the
      // status `flags` (90) and precedes `plot_style_name`/`render_mode`. SINGLE
      // emit point: the parser routes 340 into the typed field (out of
      // extraGroups), and viewportTypedEmittedCodes drops 340 from the
      // passthrough when emitted here → no double-emit. Emitted verbatim
      // (preserve-source), including a literal "0" (= no clip) if the source had
      // one.
      if (g.clippingBoundaryHandle !== undefined) {
        pushGroup(groups, 340, g.clippingBoundaryHandle);
      }
      if (g.shadePlotMode !== undefined) {
        pushGroup(groups, 170, g.shadePlotMode);
      }
      if (g.renderMode !== undefined) {
        pushGroup(groups, 281, g.renderMode);
      }
      if (g.snapBase) {
        pushGroup(groups, 13, g.snapBase.x);
        pushGroup(groups, 23, g.snapBase.y);
      }
      if (g.snapSpacing) {
        pushGroup(groups, 14, g.snapSpacing.x);
        pushGroup(groups, 24, g.snapSpacing.y);
      }
      if (g.gridSpacing) {
        pushGroup(groups, 15, g.gridSpacing.x);
        pushGroup(groups, 25, g.gridSpacing.y);
      }
      // Batch-5 S-VIEWPORT-SNAPGRID — snap/grid scalars (50/61/72). These are
      // the SINGLE emit point: the parser routes 50/61/72 into typed fields
      // (out of extraGroups), so there is no risk of double-emit with the
      // generic extraGroups passthrough at serializeEntity.
      if (g.snapRotation !== undefined) {
        pushGroup(groups, 50, g.snapRotation);
      }
      if (g.gridMajor !== undefined) {
        pushGroup(groups, 61, g.gridMajor);
      }
      if (g.circleZoomPercent !== undefined) {
        pushGroup(groups, 72, g.circleZoomPercent);
      }
      // S-VIEWPORT-A — lighting block (DXF 141 brightness / 142 contrast,
      // truth-corrected: Autodesk DXF reference VIEWPORT + ezdxf 1.4.3
      // acdb_viewport view_brightness=141 / view_contrast=142). SINGLE emit
      // point — the parser routes 141/142 into typed fields, and the
      // dynamic drop-set (viewportTypedEmittedCodes) removes them from the
      // extraGroups passthrough only when actually emitted here.
      //
      // The former PR-13 emits (142 ← viewportCannoscaleFactor, 144 ←
      // "AcDbScale handle") are GONE: 142-as-factor wrote the model's
      // annotation-scale factor into AutoCAD's view-contrast slot, and 144
      // is not a VIEWPORT group code at all (the real per-VP annotation
      // scale rides the ext-dict XRECORD ASDK_XREC_ANNOTATION_SCALE_INFO →
      // 340 → AcDbScale, preserved via the OBJECTS passthrough). We never
      // fabricate either code; a source-file 144 round-trips verbatim via
      // extraGroups.
      if (g.brightness !== undefined) {
        pushGroup(groups, 141, g.brightness);
      }
      if (g.contrast !== undefined) {
        pushGroup(groups, 142, g.contrast);
      }
      // S-VIEWPORT-OVERRIDE-331 — per-VP frozen-layer handles (one 331 per
      // frozen layer) followed by the property-override soft-pointers
      // (335/343/344/91) in their original {code, handle} order. Single emit
      // point: the parser routes these into typed fields (out of extraGroups),
      // so viewportTypedEmittedCodes drops them from the passthrough → no
      // double-emit.
      if (g.frozenLayerHandles) {
        for (const h of g.frozenLayerHandles) {
          pushGroup(groups, 331, h);
        }
      }
      if (g.layerOverrideHandles) {
        for (const ref of g.layerOverrideHandles) {
          pushGroup(groups, ref.code, ref.handle);
        }
      }
      break;
    }
    case "MLEADER": {
      // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice C) — byte-faithful emit. The
      // AcDbMLeader CONTEXT_DATA record is re-emitted FIRST (ezdxf order: 270
      // version + CONTEXT_DATA precede the body fields; 270 round-trips via
      // extraGroups) from the TYPED nested tree (contextData + MTextData/
      // BlockData inline by 290/296 + leaderRoots + rawContextGroups), with the
      // REAL markers 300/301, 302/303, 304/305 — replacing the legacy synthesised
      // context (which dropped the header/MTextData/BlockData/plane/per-leader
      // metadata and opened leader lines with the wrong `302 "LEADER_LINE{"`).
      // FALLBACK (no contextData → marker-less / bridge export) re-emits the
      // minimal legacy-compatible context (still with real 304/305). See
      // mleaderContextWriter.ts.
      //
      // AcDbMLeader — self-contained; the marker precedes the 270/CONTEXT_DATA
      // body.
      emitAllSpecificMarkers(groups, specificMarkers);
      const g = entity.geometry;
      // SL-5 P3a — AcDbMLeader body group 270 = version (ezdxf `acdb_mleader`
      // default 2). ezdxf `export_entity` writes it FIRST — right after the
      // `100 AcDbMLeader` marker, BEFORE CONTEXT_DATA. Always emitted (matches
      // ezdxf's unconditional write); an imported 270 round-trips via g.version,
      // an authored MLEADER defaults to 2. Previously omitted for authored / only
      // flushed out-of-order via extraGroups for imported.
      pushGroup(groups, 270, g.version ?? 2);
      // CONTEXT_DATA emit. Common form is top-level (before the body). When the
      // source wrapped it in a `101 "Embedded Object"` / `100
      // AcDbMLeaderObjectContextData` record, re-emit it wrapped AFTER the body
      // instead (the embedded-object layout), preserving structure rather than
      // leaving a dangling 101/100 (Codex #268).
      // #432 r11 (@codex) — a legacy marker-less MLEADER carries body 48/49
      // line-spacing in extraGroups (re-emitted at the tail); tell the context
      // fallback NOT to ALSO materialize MTextData 45/170 (would double the
      // line-spacing). No context ⇒ fallback path ⇒ this gate is live.
      const legacyLineSpacing = entity.extraGroups.some(
        (eg) => eg.code === 48 || eg.code === 49,
      );
      const ctxGroups = buildAllMLeaderContextGroups(g, legacyLineSpacing);
      const ctxEmbedded = g.contextDataEmbedded === true;
      if (!ctxEmbedded) {
        for (const e of ctxGroups) {
          pushGroup(groups, e.code, e.value);
        }
      }
      // SL-5 P2 — AcDbMLeader body group 340 = style_handle (the MLEADERSTYLE
      // record handle). ezdxf `export_entity` writes `340 dxf.style_handle`
      // immediately AFTER CONTEXT_DATA, BEFORE 90 (property_override_flags). This
      // is the FIRST body field. Imported: source 340 preserved verbatim on
      // g.leaderStyleHandle. Authored: resolved from styleName by the document
      // orchestrator (stampMLeaderLeaderStyleHandles). REPLACES the legacy bogus
      // `3 <styleName>` (ezdxf never emits group 3) — removed below. Skipped only
      // for a hand-built geom with no handle (never fabricate a dangling value).
      // For the embedded-context layout the body precedes the 101/100 wrapper, so
      // 340 still leads the body — matching ezdxf's body-attribute ordering.
      //
      // SL-5 F2 (@codex round-3) — LEGACY-name fallback. A legacy MLEADER that
      // carried its leader style ONLY as a depth-0 `3 <name>` with NO body `340`
      // parses the name into `geometry.styleName` (not extraGroups). The bridge /
      // export path ALWAYS stamps `leaderStyleHandle` (stampMLeaderLeaderStyle
      // Handles assigns every MLEADER a 340 — object handle / preserved source /
      // synthetic hex), so real exports take the ezdxf-conformant `340` path and
      // never emit `3`. But a PURE dxf-io parse→serialize does NOT run the
      // stamper, so a handle-less legacy MLEADER had NEITHER 340 NOR 3 → the
      // style binding was silently DROPPED. When there is no `leaderStyleHandle`
      // but a `styleName` survives, re-emit `3 <styleName>` so the pure round-trip
      // preserves the name (the 340 path, when present, still wins and suppresses
      // 3 — the two are mutually exclusive).
      if (g.leaderStyleHandle) {
        pushGroup(groups, 340, g.leaderStyleHandle);
      } else if (g.styleName) {
        pushGroup(groups, 3, g.styleName);
      }

      // ── AcDbMLeader BODY (depth 0, AFTER 301}). ezdxf Scope-6 codes. Each
      // typed body field is emitted under its CORRECT ezdxf group code (gap-C +
      // gap-C.2 symmetry fix — SYMMETRIC to the corrected MLEADER_GROUP_READERS
      // in entityParser.ts): 170 leader_type, 172 content_type, 41 dogleg_length,
      // 42 arrow_head_size, 290 has_landing, 291 has_dogleg, 292 has_text_frame,
      // 176 block_connection_type, 90 property_override_flags, 175
      // text_alignment_type, 342 arrow_head_handle, 344 block_record_handle, 43
      // block_rotation. Every body code with NO typed home (270 version, 340
      // style handle, 91/92/93 colors, 95, 173, 174, 178/179, 271-273, 293-295,
      // 341 leader_linetype_handle, 343 text_style_handle, 45 scale, …)
      // round-trips verbatim via extraGroups.
      // LEGACY flat MTEXT fields. These were formerly emitted under a BLANKET
      // `if (!hasCtx)` — suppressed whenever ANY context was present. But the
      // body value is only redundant when the context ACTUALLY carries it
      // (Codex #268 F3): a context with no MTextData.defaultContent silently
      // DROPPED the body text on write. Gate each field PER-FIELD on whether
      // its CONTEXT_DATA home already carries the value, not on blanket
      // context-presence. (textStyleHandle is emitted inside CONTEXT_DATA by
      // buildMLeaderContextGroups — not re-emitted here.)
      const ctx = g.contextData;
      const ctxMText = ctx?.mTextData;
      // SL-5 P2 — the legacy bogus `3 <styleName>` emit is REMOVED. ezdxf
      // `acdb_mleader` has NO group 3; the leader style is referenced by the body
      // 340 handle (emitted above), never by a style NAME. The old code wrote
      // `3 Standard` on the marker-less fallback path, which ezdxf/AutoCAD do not
      // recognise on an MLEADER (garbage group). styleName round-trips via the
      // model + the 340 handle resolution, not a DXF group 3.
      // 304 textContents → MTextData.defaultContent. Emit at body level only
      // when the context does NOT already carry non-empty default content.
      if (
        g.textContents &&
        (ctxMText?.defaultContent === undefined || ctxMText.defaultContent === "")
      ) {
        pushGroup(groups, 304, g.textContents);
      }
      // 40 textHeight → context charHeight (41). Emit body 40 only when the
      // context carries no char height (else the header already emits it).
      if (g.textHeight !== undefined && ctx?.charHeight === undefined) {
        pushGroup(groups, 40, g.textHeight);
      }
      // NOTE: textRotation is NOT body-emitted — body group 42 is arrow_head_size
      // (gap-C). Its real home is MTextData.rotation (in CONTEXT_DATA), projected
      // by the bridge. Emitting it as body 42 would collide with arrow_head_size.
      // 12/22/32 textLocation → MTextData.insert. Emit body level only when the
      // context carries no insert.
      if (g.textLocation && ctxMText?.insert === undefined) {
        writePoint(groups, [12, 22, 32], g.textLocation);
      }
      // Corrected ezdxf Scope-6 body codes (emit whenever the typed field set).
      if (g.leaderLineType !== undefined) {
        pushGroup(groups, 170, g.leaderLineType); // leader_type
      }
      if (g.contentType !== undefined) {
        pushGroup(groups, 172, g.contentType); // content_type
      }
      if (g.landingEnabled !== undefined) {
        pushGroup(groups, 290, g.landingEnabled ? 1 : 0); // has_landing
      }
      if (g.doglegEnabled !== undefined) {
        pushGroup(groups, 291, g.doglegEnabled ? 1 : 0); // has_dogleg
      }
      if (g.doglegLength !== undefined) {
        pushGroup(groups, 41, g.doglegLength); // dogleg_length
      }
      if (g.arrowheadSize !== undefined) {
        pushGroup(groups, 42, g.arrowheadSize); // arrow_head_size
      }
      if (g.textFrameDisplay !== undefined) {
        pushGroup(groups, 292, g.textFrameDisplay ? 1 : 0); // has_text_frame
      }
      if (g.blockConnectionType !== undefined) {
        pushGroup(groups, 176, g.blockConnectionType); // block_connection_type
      }
      // #256 F1 — body group 171 is the leader LINEWEIGHT enum string.
      if (g.leaderLineWeight !== undefined) {
        pushGroup(groups, 171, g.leaderLineWeight);
      }
      // gap-C.2 (FIX #3): arrow_head_handle is ezdxf Scope-6 body group 342 (NOT
      // 341 — 341 is leader_linetype_handle, which has no typed home and
      // round-trips verbatim via extraGroups). Symmetric to the corrected reader.
      if (g.arrowheadBlock) {
        pushGroup(groups, 342, g.arrowheadBlock);
      }
      // gap-C.2 (FIX #2): NO body 44 emit. ezdxf has no body landing_gap; it
      // lives in CONTEXT_DATA group 145 (re-emitted from contextData by
      // buildMLeaderContextGroups). A depth-0 group 44 is block-attribute width
      // (trailing AttribData tuple), preserved verbatim via extraGroups.
      // SL-5 P3c — NO body 48/49 emit. ezdxf `acdb_mleader` has NO group 48/49;
      // MLEADER line-spacing lives in CONTEXT_DATA MTextData (group 45 factor /
      // 170 style, emitted by emitMTextData from contextData.mTextData). The old
      // body 48/49 emit invented out-of-spec groups from the model's flat
      // textLineSpacingStyle/Factor; line-spacing now round-trips solely via the
      // MTextData path (parser reads 45/170 into contextData.mTextData).
      // gap-C.2 (FIX #1): property_override_flags is ezdxf Scope-6 body group 90
      // (NOT 175). Emitted whenever the typed field is set so a parsed body 90
      // (incl 0) round-trips byte-faithfully. Symmetric to the corrected reader.
      if (g.propertyOverrideFlags !== undefined) {
        pushGroup(groups, 90, g.propertyOverrideFlags);
      }
      // gap-C.2 (FIX #1): text_alignment_type is ezdxf Scope-6 body group 175.
      if (g.textAlignmentType !== undefined) {
        pushGroup(groups, 175, g.textAlignmentType);
      }
      // gap-C.2 (FIX #3): block_record_handle is ezdxf Scope-6 body group 344
      // (NOT 342, which is arrow_head_handle above). Symmetric to the reader.
      // Emit the RAW handle verbatim (byte-faithful round-trip); only fall back
      // to blockName for a model-authored MLEADER that never carried a handle
      // (Codex #271 — the parser now keeps the handle separate from the resolved
      // block name).
      if (g.blockRecordHandle) {
        pushGroup(groups, 344, g.blockRecordHandle);
      } else if (g.blockName) {
        pushGroup(groups, 344, g.blockName);
      }
      if (g.blockScale !== undefined && g.blockScale !== 1) {
        pushGroup(groups, 45, g.blockScale);
      }
      // gap-C.2 (FIX #4): block_rotation is ezdxf Scope-6 body group 43 (NOT 46).
      // Emitted whenever set so a parsed body 43 (incl 0.0) round-trips.
      if (g.blockRotation !== undefined) {
        pushGroup(groups, 43, g.blockRotation);
      }
      // #432 r9 E2 — block_scale_vector (body 10/20/30). ezdxf `export_entity`
      // writes `write_vertex(10, dxf.block_scale_vector)`; the parser captures the
      // depth-0 body 10/20/30 into `blockScaleVector` (a legacy no-context leader
      // vertex never populates it), so a non-default block scale (2,2,2) round-
      // trips instead of being dropped by resolveMLeaderLeaderRoots.
      if (g.blockScaleVector) {
        writePoint(groups, [10, 20, 30], g.blockScaleVector);
      }
      // #432 r9 E1 — block-attribute linkage (330 ATTDEF handle / 177 index / 44
      // width / 302 text) per ezdxf `export_block_attribs`. Previously the tuple
      // was NEVER emitted → `get_block_content()` returned {} and the ATTDEF `330`
      // corrupted the entity owner. The parser now consumes the tuple into
      // `blockAttributeReferences` (never ownerHandle / extraGroups); re-emit it
      // here from the typed field (and mleaderTypedEmittedCodes strips a legacy
      // extraGroups copy so it cannot double-emit).
      for (const attr of g.blockAttributeReferences ?? []) {
        pushGroup(groups, 330, attr.attDefHandle);
        pushGroup(groups, 177, attr.index);
        pushGroup(groups, 44, attr.width);
        pushGroup(groups, 302, attr.value);
      }
      // Embedded-object-wrapped CONTEXT_DATA (Codex #268): emit the 101/100
      // wrapper + the CONTEXT_DATA groups AFTER the body, matching the source
      // AcDbMLeaderObjectContextData layout (parser dropped the bare wrapper
      // markers from extraGroups).
      if (ctxEmbedded) {
        pushGroup(groups, 101, "Embedded Object");
        pushGroup(groups, 100, "AcDbMLeaderObjectContextData");
        for (const e of ctxGroups) {
          pushGroup(groups, e.code, e.value);
        }
      }
      break;
    }
    case "ARRAYRECT": {
      // An ARRAYRECT serializes as an INSERT-style block reference and ALWAYS
      // emits `100 AcDbBlockReference` — NEVER AcDbMInsertBlock (@codex SL-9 P2,
      // raw-round-trip fix):
      //   • Real AutoCAD stores an associative array's anchor as a PLAIN block
      //     reference of the anonymous *U block; the column/row grid lives in
      //     the ACDBASSOCARRAY params record, NOT as a MINSERT grid on the
      //     entity. Every ARRAYRECT source in the repo fixtures is
      //     `100 AcDbBlockReference` (dxfBridge.arrayAssoc.test.ts,
      //     assocArrayParamsDecode.test.ts), and ezdxf upgrades ONLY a genuine
      //     INSERT grid to MInsert.
      //   • Whether an ARRAYRECT is associative is resolvable ONLY from the
      //     OBJECTS AcDbAssocArray graph (ext-dict → ACDBASSOCNETWORK → params),
      //     which the raw `entityParser` does NOT cross-reference — only the
      //     bridge's second-pass `resolveAssocArrays` does. So a bare
      //     `serializeDxfDocument(parseDxfDocument(...))` round-trip cannot see
      //     the flag; a COUNT/`!associative`-gated MInsert upgrade would flip an
      //     associative array (whose resolved columnCount/rowCount are >1) to
      //     AcDbMInsertBlock and CORRUPT it on the second raw round-trip. Always
      //     emitting AcDbBlockReference closes that gap BY CONSTRUCTION.
      //   • The genuinely non-associative rectangular grid (legacy MINSERT) is
      //     ALREADY handled by the INSERT/MINSERT arm (`0 INSERT` +
      //     AcDbMInsertBlock, ezdxf COUNT predicate). Emitting AcDbMInsertBlock
      //     under an ARRAYRECT is deferred to SL-18 (ARRAY-assoc, which owns the
      //     assoc graph) together with the `arrayrect-bespoke-0-name` P1 that
      //     rewrites `0 ARRAYRECT` → the conformant `0 INSERT` — an MInsert
      //     marker under a non-conformant `0 ARRAYRECT` name is not conformant
      //     anyway. See writer.ts DXF_ENTITY_NAME_OVERRIDE note.
      // Exactly ONE marker (emitBlockReferenceSubtypeMarker drops any merged
      // AcDbBlockReference/AcDbMInsertBlock pair so a legacy imported grid marker
      // never double-emits).
      const g = entity.geometry;
      emitBlockReferenceSubtypeMarker(groups, specificMarkers, false);
      pushGroup(groups, 2, g.blockName);
      writePoint(groups, [10, 20, 30], g.insertionPoint);
      if (g.scaleX !== undefined && g.scaleX !== 1) {
        pushGroup(groups, 41, g.scaleX);
      }
      if (g.scaleY !== undefined && g.scaleY !== 1) {
        pushGroup(groups, 42, g.scaleY);
      }
      if (g.scaleZ !== undefined && g.scaleZ !== 1) {
        pushGroup(groups, 43, g.scaleZ);
      }
      if (g.rotation !== undefined && g.rotation !== 0) {
        pushGroup(groups, 50, g.rotation);
      }
      pushGroup(groups, 70, g.columnCount);
      pushGroup(groups, 71, g.rowCount);
      // Presence-aware emit (Codex W2 #251-B follow-up): a spacing that was
      // ABSENT in the source stays absent on re-emit — never materialised as
      // a synthetic 0 (which a re-import could not tell apart from a real
      // explicit `44 0`). An explicit 0 IS emitted (unlike the INSERT path's
      // default-elision) so the source bytes round-trip verbatim.
      if (g.columnSpacing !== undefined) {
        pushGroup(groups, 44, g.columnSpacing);
      }
      if (g.rowSpacing !== undefined) {
        pushGroup(groups, 45, g.rowSpacing);
      }
      // AcDbBlockReference extrusion (210/220/230) from common — ARRAYRECT has no
      // typed geometry.extrusion (unlike INSERT), so its OCS normal rides the
      // common channel. Emitted LAST to match the INSERT block-reference layout.
      writeExtrusion(groups, common);
      break;
    }
    case "REVCLOUD": {
      // REVCLOUD writes as AcDbPolyline (LWPOLYLINE form): count(90), flags(70),
      // const_width(43), elevation(38), thickness(39), vertices, extrusion.
      emitAllSpecificMarkers(groups, specificMarkers);
      const g = entity.geometry;
      pushGroup(groups, 90, g.vertexCount);
      // DXF group 70: bit 1 = closed, bit 128 = plinegen — a revcloud is stored
      // as an LWPOLYLINE and carries the SAME flag bits. Pack both so an imported
      // cloud with plinegen (70 = 129) round-trips instead of dropping bit 128
      // on save (@codex #422 P2) — mirrors the LWPOLYLINE writer.
      pushGroup(groups, 70, (g.closed ? 1 : 0) | (g.plinegen ? 128 : 0));
      if (g.constantWidth !== undefined && g.constantWidth !== 0) {
        pushGroup(groups, 43, g.constantWidth);
      }
      if (g.elevation !== undefined && g.elevation !== 0) {
        pushGroup(groups, 38, g.elevation);
      }
      writeThickness(groups, common);
      for (const v of g.vertices) {
        writeAcDbPolylineVertex(groups, v);
      }
      writeExtrusion(groups, common);
      break;
    }
    case "WIPEOUT": {
      // WIPEOUT is a SINGLE `100 AcDbWipeout` marker carrying the WHOLE
      // raster-image body (ezdxf `entities/image.py`: `ImageBase.export_entity`
      // writes ONE subclass marker = `self._SUBCLASS_NAME` = "AcDbWipeout" for
      // Wipeout). The former dual-marker split (`AcDbRasterImage` + a second
      // `AcDbWipeout`) buried the 14/24 boundary under subclass index 3, which
      // `ImageBase.load_dxf_attribs` (reads `subclasses[2]` only) never sees.
      //
      // Placement is DETERMINISTICALLY the bbox of the clip boundary, exactly as
      // ezdxf `Wipeout.set_masking_area`: insert = bbox MIN corner, u_pixel =
      // (width,0,0), v_pixel = (0,height,0), image_size = (1,1). The boundary
      // 14/24 are then emitted in NORMALIZED ±0.5 image space (the y-axis is
      // flipped) — NOT world coords — so an external reader (AutoCAD / ezdxf)
      // applies insert/u/v and reconstructs the correct world boundary. The
      // symmetric import inversion lives in entityParser.ts
      // (reconstructWipeoutWorldVertices) so our own export → import is exact.
      //
      // The frame display flag is a DOCUMENT setting (WIPEOUTVARIABLES group 70),
      // NOT an entity attribute; the former trailing `280 frame` overwrote the
      // `clipping` flag (280 in acdb_image). It is dropped here — see the
      // docs/test-gaps follow-up for WIPEOUTVARIABLES routing.
      //
      // Emit exactly one `AcDbWipeout` marker; drop any stale `AcDbRasterImage`
      // a re-imported non-standard (old dual-marker) file still carries so it is
      // never re-introduced.
      const wg = entity.geometry;
      for (const marker of specificMarkers) {
        if (marker === "AcDbRasterImage") continue;
        pushGroup(groups, 100, marker);
      }

      // Bounding box of the clip-boundary vertices (insert = MIN corner).
      // Degenerate/empty boundary → a valid unit body (never a bare marker).
      let minX = 0;
      let minY = 0;
      let width = 1;
      let height = 1;
      if (wg.vertices.length > 0) {
        minX = wg.vertices[0]!.x;
        minY = wg.vertices[0]!.y;
        let maxX = minX;
        let maxY = minY;
        for (const v of wg.vertices) {
          if (v.x < minX) minX = v.x;
          if (v.x > maxX) maxX = v.x;
          if (v.y < minY) minY = v.y;
          if (v.y > maxY) maxY = v.y;
        }
        width = maxX - minX;
        height = maxY - minY;
      }
      // Guard a zero-extent bbox so normalization never divides by zero. The
      // SAME divisor is emitted as u_pixel/v_pixel, so the import inversion is
      // exact for ANY non-zero divisor (world = insert + u·(bx+0.5) + v·(0.5−by)).
      const uLen = width !== 0 ? width : 1;
      const vLen = height !== 0 ? height : 1;

      // AcDbWipeout body (ezdxf acdb_image group order).
      pushGroup(groups, 90, 0); // class_version
      pushGroup(groups, 10, minX); // insert.x (bbox MIN corner)
      pushGroup(groups, 20, minY); // insert.y
      pushGroup(groups, 30, 0); // insert.z
      pushGroup(groups, 11, uLen); // u_pixel = (width, 0, 0)
      pushGroup(groups, 21, 0);
      pushGroup(groups, 31, 0);
      pushGroup(groups, 12, 0); // v_pixel = (0, height, 0)
      pushGroup(groups, 22, vLen);
      pushGroup(groups, 32, 0);
      pushGroup(groups, 13, 1); // image_size = (1, 1) — 2D vector, codes 13/23 ONLY
      pushGroup(groups, 23, 1);
      pushGroup(groups, 340, "0"); // image_def_handle — null (Wipeout._reset_handles)
      pushGroup(groups, 70, 7); // flags = show | show-not-aligned | use-clipping-boundary
      pushGroup(groups, 280, 1); // clipping = on
      pushGroup(groups, 281, 50); // brightness
      pushGroup(groups, 282, 50); // contrast
      pushGroup(groups, 283, 0); // fade
      pushGroup(groups, 360, "0"); // image_def_reactor_handle — null (Wipeout._reset_handles)
      // CLOSE a polygonal (>2-vertex) boundary that is not already closed, before
      // deriving 71 / 91 / emitting 14/24 — ezdxf `set_boundary_path` appends
      // `vertices[0]` when `len > 2 and not vertices[-1].isclose(vertices[0])`
      // ("Close path, otherwise AutoCAD crashes"). An editor-created 4-corner rect
      // or a 3-vertex triangle is stored OPEN; without the repeated first point the
      // single-marker export is an incomplete clip polygon a reader must repair.
      // An already-closed boundary (e.g. the imported 5-vertex single.dxf form) is
      // NOT re-closed; a 2-vertex rectangular form (`71 = 1`) is NOT closed either.
      const first = wg.vertices[0];
      const last = wg.vertices[wg.vertices.length - 1];
      const alreadyClosed =
        first !== undefined &&
        last !== undefined &&
        Math.abs(last.x - first.x) <= 1e-9 &&
        Math.abs(last.y - first.y) <= 1e-9;
      const boundaryVerts =
        wg.vertices.length > 2 && !alreadyClosed
          ? [...wg.vertices, first!]
          : wg.vertices;
      // clipping_boundary_type: 1 = rectangular (< 3 corners), 2 = polygonal.
      // Mirrors ezdxf set_boundary_path (`1 if len(_vertices) < 3 else 2`) on the
      // CLOSED vertex list.
      pushGroup(groups, 71, boundaryVerts.length < 3 ? 1 : 2);
      pushGroup(groups, 91, boundaryVerts.length); // count_boundary_points
      // Boundary vertices, NORMALIZED ±0.5 image space (ezdxf set_masking_area):
      //   bx = (x − minX)/w − 0.5 ;  by = 0.5 − (y − minY)/h  (y-axis flipped)
      for (const v of boundaryVerts) {
        pushGroup(groups, 14, (v.x - minX) / uLen - 0.5);
        pushGroup(groups, 24, 0.5 - (v.y - minY) / vLen);
      }
      // clip_mode (0 = outside, 1 = inside) — MODELED, source-preserved (was
      // hardcoded 0, which flipped a source `290 1` to outside). TAIL, after boundary.
      // An in-memory entity persisted BEFORE the typed `clipMode` field existed
      // carries `clipMode === undefined` at runtime while its SOURCE `290` survives
      // ONLY in `extraGroups` (the pre-clipMode WIPEOUT parser routed the then-
      // unmodeled 290 there). Recover that legacy 290 before defaulting so open →
      // save does NOT flip a source `290 1` (inside clip) to outside. Precedence:
      // typed field > extraGroups-legacy > default 0. The recovered value rides
      // into THIS typed emit; the extraGroups 290 is then dropped by
      // WIPEOUT_RASTERIMAGE_SYNTHESISED_CODES (no double-emit). A direct dxf-io
      // caller with neither typed nor legacy 290 still produces a valid `290 0`
      // (never the invalid `290 undefined`).
      //
      // VERSION-GATE: `clip_mode` is a DXF2010+ (AC1024) attribute (ezdxf
      // `acdb_image` declares it `dxfversion=DXF2010`; `export_entity` gates it on
      // `dxfversion >= DXF2010`). Emit 290 ONLY for an R2010+ target; re-saving an
      // imported pre-R2010 (AC1015 / AC1021) drawing OMITS it so older readers do
      // not receive a newer-version group (the extraGroups 290 is dropped by the
      // synthesised-codes set, so omission is total). The from-scratch export is
      // AC1032 (ensureConformantSkeleton) → still emits 290.
      if (emitClipMode) {
        const clipMode =
          wg.clipMode ??
          legacyWipeoutFlagFromExtraGroups(entity.extraGroups, 290) ??
          0;
        pushGroup(groups, 290, clipMode);
      }
      break;
    }
  }
}

// AcDbWipeout body codes the WIPEOUT writer SYNTHESISES from the clip-boundary
// bounding box (see writeEntityGeometry case "WIPEOUT"). The parser now CONSUMES
// every one of these (they never reach extraGroups), so this drop-set is a
// defensive guard against a directly-constructed entity or a legacy re-import
// that still carries them in extraGroups: without it the generic passthrough
// would re-emit each code AFTER the synthesised body → an internally
// inconsistent, double-emitted raster-image record. The synthesised body
// (bbox-derived, AutoCAD-canonical placement) is authoritative. Covers the full
// AcDbWipeout body: placement (90/10..32/13/23), handles (340/360), display
// flags (70/280 clipping/281/282/283), clip geometry meta (71/91) and the tail
// clip-mode (290). 14/24 are the typed boundary (never parked here).
const WIPEOUT_RASTERIMAGE_SYNTHESISED_CODES: ReadonlySet<number> = new Set([
  90, 10, 20, 30, 11, 21, 31, 12, 22, 32, 13, 23, 340, 70, 280, 281, 282, 283,
  360, 71, 91, 290,
]);

/**
 * Recover a WIPEOUT boolean group (DXF codes 290–299 are 0/1 flags) that a
 * pre-typed-field parser parked in `extraGroups`. Used to reclaim the source
 * `290` clip_mode from an entity persisted BEFORE the typed `clipMode` field
 * existed (the pre-clipMode WIPEOUT parser did not model 290, so it fell through
 * to extraGroups). Returns 0 or 1, or `undefined` when the code is absent so the
 * caller's `?? default` applies. Any non-1 value normalizes to 0 (boolean flag).
 */
function legacyWipeoutFlagFromExtraGroups(
  extraGroups: readonly DxfGroup[],
  code: number,
): 0 | 1 | undefined {
  const group = extraGroups.find((entry) => entry.code === code);
  if (group === undefined) return undefined;
  return Number.parseInt(group.value, 10) === 1 ? 1 : 0;
}

// AutoCAD REVCLOUD default minimum arc chord length (group 1040). Mirrors the
// `?? 15` default the parser (`draft.numbers.arcLength ?? 15`) and encoder
// (`encodeRevcloudProps`) apply, so the writer's "is this arcLength authoritative
// / non-default?" gate uses the SAME default those two paths do.
const REVCLOUD_DEFAULT_ARC_LENGTH = 15;

/**
 * Resolve the final XData array for a REVCLOUD entity.
 *
 * The writer is the SINGLE encode point for the "RevcloudProps" XData
 * appid (AutoCAD 2021+). The typed geometry fields (revisionCloudStyle /
 * creationMode / approximateArcLength / arcLength = minimum arc chord) are
 * AUTHORITATIVE: when they encode
 * to a non-default block we DROP any existing RevcloudProps entry (which
 * may be a STALE verbatim copy from a re-imported file the caller has since
 * mutated) and replace it with the freshly re-encoded one, so a
 * parse → mutate → serialize cycle emits the mutated values and there is
 * never a double RevcloudProps block (Codex #243 round-3 F4).
 *
 * BUT when the encoder returns null — i.e. the typed geometry fields are
 * all undefined (or all equal the AutoCAD defaults) — there is nothing
 * authoritative to project. In that case we must NOT strip the existing
 * xdata: a direct dxf-io caller may carry a RevcloudProps block ONLY in
 * `entity.xdata` (typed fields undefined), and filtering it away would
 * silently lose the style/mode/arc values (Codex #243 round-4 R4-A).
 * Preserve the existing xdata verbatim instead.
 *
 * Shared by `serializeSupportedEntity` (XData emission) and
 * `entityEmittedAppNames` / `collectEmittedXDataAppids` (APPID-table synthesis)
 * so both agree on a single source of truth.
 */
function resolveRevcloudXData(entity: DxfRevcloudEntity): DxfXDataEntry[] {
  const xdata = [...(entity.xdata ?? [])];
  const { revisionCloudStyle, creationMode, approximateArcLength, arcLength } =
    entity.geometry;
  // Whether the caller has set ANY typed geometry field. This is distinct
  // from "the encoder produced a block": encodeRevcloudProps returns null in
  // TWO cases — (a) all FOUR fields undefined (no typed geometry at all), and
  // (b) fields present but equal to the AutoCAD defaults (Normal / Rectangular
  // / approx 15 / min-arc 15), which need no XData. We must strip a stale
  // verbatim block in case (b) too — a parse → mutate-to-defaults → serialize
  // must NOT re-emit the old non-default style/mode/arc. Only case (a)
  // preserves verbatim.
  //
  // @codex #487 P2 (round-2): `arcLength` (the minimum arc chord carrier) MUST
  // participate in this gate. A pure dxf-io caller can hand-build a REVCLOUD
  // whose ONLY non-default RevcloudProps value is `geometry.arcLength` (style /
  // mode / approximateArcLength all undefined). Omitting it here sent that case
  // down the else branch, which appends the DEFAULT one-1040 block (arc 15) and
  // silently loses the non-default minimum arc length on reparse — even though
  // encodeRevcloudProps({ arcChordLength }) encodes it correctly.
  //
  // Unlike the optional fields (which use `!== undefined` as the "caller set
  // it" sentinel), `arcLength` is a REQUIRED field that ALWAYS defaults to 15
  // (parser: `draft.numbers.arcLength ?? 15`). So the authoritative signal is
  // "non-default", not "defined": a caller who left it at 15 has nothing to
  // project and must NOT strip a stale/xdata-only verbatim block (R4-A). We
  // therefore contribute arcLength only when it differs from the AutoCAD
  // default 15 — the same value `encodeRevcloudProps` treats as default.
  const hasTypedFields =
    revisionCloudStyle !== undefined ||
    creationMode !== undefined ||
    approximateArcLength !== undefined ||
    (arcLength !== undefined && arcLength !== REVCLOUD_DEFAULT_ARC_LENGTH);
  if (hasTypedFields) {
    // @codex #487 P2 (round-3): an UNDEFINED sub-field here means "the caller
    // never touched this sub-field" — NOT "the caller wants the AutoCAD
    // default". A direct dxf-io caller may carry revisionCloudStyle /
    // creationMode / approximateArcLength ONLY in the existing `entity.xdata`
    // (e.g. a re-imported Calligraphy/Polygonal/33 cloud) while mutating
    // ONLY `arcLength` as a typed field. Because `arcLength` alone crosses
    // the non-default gate above, the OLD code re-encoded from
    // { style: undefined, mode: undefined, approx: undefined, arcLength }
    // and `encodeRevcloudProps` filled the undefined trio with the
    // AutoCAD defaults (Normal/Rectangular/15) — silently resetting an
    // xdata-only style/mode/approx while trying to persist the minimum arc.
    //
    // Fix: decode any existing verbatim block BEFORE stripping it, and let
    // it supply the fallback ONLY for the three OPTIONAL sub-fields that
    // have a real "caller set it" sentinel (`!== undefined`). This preserves
    // the Codex #243 F4 / R5 invariant unchanged (an EXPLICITLY set typed
    // field, even one that equals the default, still wins over stale xdata).
    const existingDecoded = decodeRevcloudProps(xdata);
    const encoded = encodeRevcloudProps({
      revisionCloudStyle: revisionCloudStyle ?? existingDecoded.revisionCloudStyle,
      creationMode: creationMode ?? existingDecoded.creationMode,
      approximateArcLength:
        approximateArcLength ?? existingDecoded.approximateArcLength,
      // @codex #487 P2 round-4: `arcLength` does NOT get the same
      // existingDecoded fallback as the three optional fields above — it has
      // no "caller set it" sentinel (it is a REQUIRED field, always some
      // concrete value), so once we are in this branch for ANY reason its
      // own value is used DIRECTLY, unconditionally. Round-3 initially
      // gated this the same way as the optional fields ("only when it
      // differs from the AutoCAD default, else fall back to the decoded
      // xdata"), but that broke the more common case: a caller who PARSED a
      // REVCLOUD (typed arcLength populated FROM the xdata's second 1040 by
      // the parser, xdata left verbatim) and then edits the minimum arc
      // length back to the default 15 — with the round-3 gate that 15 was
      // treated as "not authoritative" and the stale non-default
      // existingDecoded.arcChordLength was resurrected, making it
      // impossible to reset an imported cloud's minimum arc length to the
      // default. Once hasTypedFields is true, entity.geometry is already
      // being treated as authoritative (Codex #243 F4) — arcLength, being
      // always-concrete, is simply part of that authoritative geometry.
      arcChordLength: arcLength,
    });
    const stripped = xdata.filter((x) => x.appName !== REVCLOUD_PROPS_APPID);
    return [...stripped, encoded ?? DEFAULT_REVCLOUD_PROPS_XDATA];
  }
  // No typed geometry fields at all — preserve any existing xdata RevcloudProps
  // block verbatim; if there is none, append the DEFAULT block so the
  // discriminator is always present.
  if (xdata.some((x) => x.appName === REVCLOUD_PROPS_APPID)) return xdata;
  return [...xdata, DEFAULT_REVCLOUD_PROPS_XDATA];
}

// SL-2 (DXF R2018): a REVCLOUD is now serialized as `0 LWPOLYLINE`, so the
// `RevcloudProps` XData is the ONLY thing that distinguishes a revision cloud
// from a plain polyline on re-import — and it matches AutoCAD, whose REVCLOUD
// command ALWAYS stamps this XData. It is therefore emitted UNCONDITIONALLY
// (the prior "omit when all-default for a compact round-trip" was only safe
// while the bespoke `0 REVCLOUD` name carried the discriminator). The default
// block encodes style=Normal(0) / mode=Rectangular(1) / arcLength=15 — the same
// AutoCAD defaults `encodeRevcloudProps` uses.
const DEFAULT_REVCLOUD_PROPS_XDATA: DxfXDataEntry = {
  appName: REVCLOUD_PROPS_APPID,
  groups: [
    { code: 1070, value: "0" },
    { code: 1070, value: "1" },
    { code: 1040, value: String(REVCLOUD_DEFAULT_ARC_LENGTH) },
  ],
};

// Codes the VIEWPORT writer path emits from TYPED geometry fields
// (writeEntityGeometry → case "VIEWPORT"). Before Batch-5, codes 50/61/72 had
// no typed VIEWPORT reader and fell through to `extraGroups`; a model imported
// by that OLD code still carries 50/61/72 (and possibly the other typed codes)
// inside `extraGroups`. If we blindly appended `entity.extraGroups` after the
// typed emit, every such code that ALSO has a typed value would be written
// TWICE — the typed value AND the stale extraGroup, with possibly conflicting
// values (invalid DXF / silent data corruption).
//
// The drop-set MUST be dynamic, not a static superset of all these codes:
// a pre-Batch-5 model can carry one of these codes in `extraGroups` with NO
// corresponding typed geometry field (e.g. snapRotation undefined but
// extraGroups has a 50). In that case the typed writer emits NOTHING for it,
// so a static filter would SILENTLY LOSE the value. We therefore drop a code
// from the extraGroups passthrough ONLY when the typed writer above actually
// emitted it — i.e. only when the geometry field passed the SAME emit guard
// used in writeEntityGeometry's VIEWPORT case. A code present in BOTH
// typed-and-extraGroups → emitted once (typed); a code present ONLY in
// extraGroups (typed absent) → preserved verbatim; genuine unparsed codes →
// always preserved.
//
// IMPORTANT: each guard below must match the EXACT condition the typed emit
// uses. In particular frontClipZ/backClipZ skip a 0/absent value (group 43/44
// are only written for a non-zero clip-Z), so a 0/absent clip-Z field must NOT
// add 43/44 to the drop-set — otherwise a verbatim 43/44 extraGroup would be
// wrongly dropped.
function viewportTypedEmittedCodes(g: DxfViewportEntity["geometry"]): Set<number> {
  const dropped = new Set<number>();
  // 13/23 ← snapBase (guard: truthy point)
  if (g.snapBase) {
    dropped.add(13);
    dropped.add(23);
  }
  // 14/24 ← snapSpacing (guard: truthy point)
  if (g.snapSpacing) {
    dropped.add(14);
    dropped.add(24);
  }
  // 15/25 ← gridSpacing (guard: truthy point)
  if (g.gridSpacing) {
    dropped.add(15);
    dropped.add(25);
  }
  // 50 ← snapRotation (guard: defined)
  if (g.snapRotation !== undefined) {
    dropped.add(50);
  }
  // 61 ← gridMajor (guard: defined)
  if (g.gridMajor !== undefined) {
    dropped.add(61);
  }
  // 72 ← circleZoomPercent (guard: defined)
  if (g.circleZoomPercent !== undefined) {
    dropped.add(72);
  }
  // 43 ← frontClipZ (guard: defined AND non-zero — matches typed emit)
  if (g.frontClipZ !== undefined && g.frontClipZ !== 0) {
    dropped.add(43);
  }
  // 44 ← backClipZ (guard: defined AND non-zero — matches typed emit)
  if (g.backClipZ !== undefined && g.backClipZ !== 0) {
    dropped.add(44);
  }
  // 141 ← brightness / 142 ← contrast (guard: defined — matches typed emit).
  // S-VIEWPORT-A: before this slice 141 had no VIEWPORT reader (rode
  // extraGroups) and 142 was mis-typed as the PR-13 "cannoscale factor", so a
  // model imported by OLD code can still carry a verbatim 141 (and, for
  // documents that predate the 142 reader entirely, a verbatim 142) inside
  // extraGroups. Drop each code ONLY when the typed field actually emitted.
  if (g.brightness !== undefined) {
    dropped.add(141);
  }
  if (g.contrast !== undefined) {
    dropped.add(142);
  }
  // 170 ← shadePlotMode (guard: defined)
  if (g.shadePlotMode !== undefined) {
    dropped.add(170);
  }
  // 281 ← renderMode (guard: defined)
  if (g.renderMode !== undefined) {
    dropped.add(281);
  }
  // 331 ← frozenLayerHandles (guard: non-empty — matches typed emit). Only drop
  // when the typed array actually carried entries, so a legacy extraGroups-only
  // 331 (typed field absent) survives verbatim. (S-VIEWPORT-OVERRIDE-331.)
  if (g.frozenLayerHandles && g.frozenLayerHandles.length > 0) {
    dropped.add(331);
  }
  // 335/343/344/91 ← layerOverrideHandles. Drop only the codes the typed array
  // actually emitted (a code absent from the array but present in extraGroups
  // must survive) — mirrors the dynamic drop discipline above.
  if (g.layerOverrideHandles) {
    for (const ref of g.layerOverrideHandles) {
      dropped.add(ref.code);
    }
  }
  // 340 ← clippingBoundaryHandle (guard: defined — matches the typed emit).
  // Drop only when the typed field actually emitted, so a legacy extraGroups-only
  // 340 (typed field absent) survives verbatim. (S-VIEWPORT-CLIP-HANDLE.)
  if (g.clippingBoundaryHandle !== undefined) {
    dropped.add(340);
  }
  return dropped;
}

// Codes the MLEADER writer path emits from TYPED geometry fields
// (writeEntityGeometry → case "MLEADER" body). Symmetric to viewportTypedEmitted
// Codes: a model produced by an OLDER parser / persisted before these fields were
// typed can still carry the SAME body code in `extraGroups`, and serializeEntity
// appends extraGroups AFTER the body → the code double-emits (the typed value
// near the top AND the stale extraGroup at the tail; e.g. an UNCONDITIONAL
// `270 2` beside a legacy `270 N` → readers see DUPLICATE AcDbMLeader versions —
// Codex #432 r8 Finding 3). Drop each code from the passthrough ONLY when the
// typed writer above actually emitted it — the EXACT same emit guard — so a code
// present ONLY in extraGroups (no typed field) survives verbatim.
//
// IMPORTANT: the drop-set covers BODY (depth-0) codes only. Every in-CONTEXT_DATA
// code (the whole 300…301 tree, incl an in-context 45/170/12/22/32/340) is owned
// by the context writer (buildAllMLeaderContextGroups) via geom.contextData /
// rawContextGroups — it never reaches entity.extraGroups — so filtering the body
// duplicates here cannot touch it. Body 48/49 (legacy Edit-DWG line-spacing) are
// DELIBERATELY parked in extraGroups AND never body-emitted (P3c), so they are
// NOT in this set and round-trip verbatim. Unmodelled body codes (341 leader_
// linetype_handle, 173, 271-273, 91/92/93, 343, 44, …) have no typed emit and are
// likewise preserved.
function mleaderTypedEmittedCodes(g: DxfMLeaderEntity["geometry"]): Set<number> {
  const dropped = new Set<number>();
  const ctx = g.contextData;
  const ctxMText = ctx?.mTextData;
  // 270 ← version (UNCONDITIONAL — `g.version ?? 2` always emits).
  dropped.add(270);
  // 340 ← leaderStyleHandle (guard: truthy — the body style handle). When absent
  // the writer emits `3 styleName` instead and a legacy body 340 is preserved.
  if (g.leaderStyleHandle) {
    dropped.add(340);
  }
  // 304 ← textContents (guard: set AND the context carries no default content —
  // matches the writer's per-field gate).
  if (
    g.textContents &&
    (ctxMText?.defaultContent === undefined || ctxMText.defaultContent === "")
  ) {
    dropped.add(304);
  }
  // 40 ← textHeight (guard: defined AND context carries no char height).
  if (g.textHeight !== undefined && ctx?.charHeight === undefined) {
    dropped.add(40);
  }
  // 12/22/32 ← textLocation (guard: set AND context MTextData carries no insert).
  if (g.textLocation && ctxMText?.insert === undefined) {
    dropped.add(12);
    dropped.add(22);
    dropped.add(32);
  }
  // 170 ← leaderLineType (guard: defined).
  if (g.leaderLineType !== undefined) {
    dropped.add(170);
  }
  // 172 ← contentType (guard: defined).
  if (g.contentType !== undefined) {
    dropped.add(172);
  }
  // 290 ← landingEnabled (guard: defined).
  if (g.landingEnabled !== undefined) {
    dropped.add(290);
  }
  // 291 ← doglegEnabled (guard: defined).
  if (g.doglegEnabled !== undefined) {
    dropped.add(291);
  }
  // 41 ← doglegLength (guard: defined).
  if (g.doglegLength !== undefined) {
    dropped.add(41);
  }
  // 42 ← arrowheadSize (guard: defined).
  if (g.arrowheadSize !== undefined) {
    dropped.add(42);
  }
  // 292 ← textFrameDisplay (guard: defined).
  if (g.textFrameDisplay !== undefined) {
    dropped.add(292);
  }
  // 176 ← blockConnectionType (guard: defined).
  if (g.blockConnectionType !== undefined) {
    dropped.add(176);
  }
  // 171 ← leaderLineWeight (guard: defined).
  if (g.leaderLineWeight !== undefined) {
    dropped.add(171);
  }
  // 342 ← arrowheadBlock (guard: truthy).
  if (g.arrowheadBlock) {
    dropped.add(342);
  }
  // 90 ← propertyOverrideFlags (guard: defined).
  if (g.propertyOverrideFlags !== undefined) {
    dropped.add(90);
  }
  // 175 ← textAlignmentType (guard: defined).
  if (g.textAlignmentType !== undefined) {
    dropped.add(175);
  }
  // 344 ← blockRecordHandle OR blockName (guard: either truthy — the writer
  // emits body 344 from whichever is present).
  if (g.blockRecordHandle || g.blockName) {
    dropped.add(344);
  }
  // 45 ← blockScale (guard: defined AND ≠ 1 — matches the writer's default-elide).
  if (g.blockScale !== undefined && g.blockScale !== 1) {
    dropped.add(45);
  }
  // 43 ← blockRotation (guard: defined).
  if (g.blockRotation !== undefined) {
    dropped.add(43);
  }
  // #432 r9 E2 — 10/20/30 ← blockScaleVector (guard: defined). A legacy body
  // 10/20/30 parked in extraGroups (older parser) must not double-emit the
  // typed block_scale_vector. CONTEXT_DATA 10/20/30 ride ctxGroups (not
  // extraGroups), so this only affects a stale body copy.
  if (g.blockScaleVector) {
    dropped.add(10);
    dropped.add(20);
    dropped.add(30);
  }
  // #432 r9 E1 — 330/177/44/302 ← blockAttributeReferences (guard: non-empty).
  // The tuple is now typed-emitted, so strip any legacy extraGroups copy an older
  // parser parked (330 to ownerHandle, 177/44/302 orphaned) → no double-emit. A
  // stray 44/177/302 in a non-block-content MLEADER (empty references) is NOT in
  // the set → preserved verbatim.
  if (g.blockAttributeReferences && g.blockAttributeReferences.length > 0) {
    dropped.add(330);
    dropped.add(177);
    dropped.add(44);
    dropped.add(302);
  }
  return dropped;
}

/**
 * #432 r13 (@codex R13-1) — filter MLEADER `extraGroups` by the typed-emitted
 * drop-set, but ONLY at DEPTH 0 (the AcDbMLeader body). A group nested inside a
 * preserved `102 {…}` control block (ACAD_REACTORS reactor `330` handles,
 * ACAD_XDICTIONARY `360`, …) is kept verbatim — the drop-set targets body tuple
 * copies the typed writer re-emits, NOT control-block pointers that happen to share
 * a code (e.g. a reactor `330` vs the block-attribute `330`). A blanket
 * `filter(g => !dropped.has(g.code))` stripped the reactor `330` on a block-content
 * MLEADER (whose typed `330` is in the drop-set) → lost reactors on save.
 */
function filterMleaderBodyDropSet(
  groups: DxfGroup[],
  dropped: ReadonlySet<number>,
): DxfGroup[] {
  const out: DxfGroup[] = [];
  let depth = 0;
  for (const g of groups) {
    if (g.code === 102) {
      const tok = g.value.trim();
      if (tok.startsWith("{")) depth += 1;
      else if (tok === "}" && depth > 0) depth -= 1;
      out.push(g); // always keep the 102 control-block markers
      continue;
    }
    if (depth === 0 && dropped.has(g.code)) continue; // strip body dup only
    out.push(g);
  }
  return out;
}

function serializeSupportedEntity(
  entity: DxfSupportedEntity,
  escapeUnicode: boolean,
  emitClipMode: boolean,
): DxfGroup[] {
  const groups: DxfGroup[] = [];
  // SL-2: serialize the REAL DXF entity name (e.g. LWPOLYLINE for the bespoke
  // internal REVCLOUD discriminator); all other types serialize under their own
  // name. See DXF_ENTITY_NAME_OVERRIDE.
  pushGroup(groups, 0, DXF_ENTITY_NAME_OVERRIDE[entity.type] ?? entity.type);

  // DXF-ORDER FIX — AutoCAD-conformant R2000/AC1015 layout:
  //   0 <type>
  //   PRE_ENTITY (5 handle, 102 ext-dict, 330 owner)
  //   100 AcDbEntity
  //   ACDBENTITY (67 410 8 6 347 62 420 430 370 48 60 440 390 284)
  //   100 <specific marker(s)> + subclass bodies (owned by writeEntityGeometry)
  // The specific subclass markers (canonical-minus-AcDbEntity + any parser-seen
  // extra markers) are emitted INSIDE writeEntityGeometry at the correct
  // interleave points, so e.g. an ARC's AcDbCircle body precedes its `100
  // AcDbArc` marker. The caller no longer pushes any specific marker.
  for (const writer of PRE_ENTITY_WRITERS) {
    writer(groups, entity.common, entity.type);
  }
  pushGroup(groups, 100, "AcDbEntity");
  for (const writer of ACDBENTITY_WRITERS) {
    writer(groups, entity.common, entity.type);
  }

  // The merged markers minus the leading "AcDbEntity" (already emitted above).
  // writeEntityGeometry owns emission of these specific markers.
  const specificMarkers = mergeSubclassMarkers(
    entity.type,
    entity.subclassMarkers,
  ).slice(1);

  writeEntityGeometry(groups, entity, specificMarkers, escapeUnicode, emitClipMode);

  // Append unparsed extraGroups verbatim, but for VIEWPORT drop any code the
  // typed geometry path above ACTUALLY emitted (defends against a model imported
  // by pre-Batch-5 code that still carries 50/61/72 etc. in extraGroups → would
  // otherwise double-emit). The drop-set is built DYNAMICALLY from which typed
  // geometry fields passed their emit guard, so a code present ONLY in
  // extraGroups (no typed field) is preserved, not silently lost. See
  // viewportTypedEmittedCodes.
  let extraGroups = entity.extraGroups;
  if (entity.type === "VIEWPORT") {
    const dropped = viewportTypedEmittedCodes(entity.geometry);
    extraGroups = entity.extraGroups.filter((g) => !dropped.has(g.code));
  } else if (entity.type === "WIPEOUT") {
    // Drop the AcDbWipeout body codes the WIPEOUT writer SYNTHESISES from the
    // clip bbox, so a directly-built or legacy-re-imported wipeout carrying them
    // in extraGroups does NOT double-emit. See
    // WIPEOUT_RASTERIMAGE_SYNTHESISED_CODES.
    extraGroups = entity.extraGroups.filter(
      (g) => !WIPEOUT_RASTERIMAGE_SYNTHESISED_CODES.has(g.code),
    );
  } else if (entity.type === "MLEADER") {
    // Drop every AcDbMLeader BODY code the MLEADER writer emits from a TYPED
    // field, so a model produced by an OLDER parser that still parks that code in
    // extraGroups does NOT double-emit it after the typed body (e.g. a legacy
    // `270 N` beside the unconditional typed `270 2` → duplicate version). Built
    // DYNAMICALLY from which typed fields passed their emit guard, so a body code
    // present ONLY in extraGroups (no typed field) is preserved. See
    // mleaderTypedEmittedCodes. #432 r13 (@codex R13-1) — the drop applies ONLY to
    // DEPTH-0 body groups; a code nested inside a preserved `102 {ACAD_REACTORS …
    // 330 <owner> … 102 }` / `102 {ACAD_XDICTIONARY … 360 …}` control block (e.g. a
    // reactor 330 on a block-content MLEADER whose typed 330 is in the drop-set) is
    // NEVER stripped.
    const dropped = mleaderTypedEmittedCodes(entity.geometry);
    extraGroups = filterMleaderBodyDropSet(entity.extraGroups, dropped);
  }
  groups.push(...extraGroups);

  // REVCLOUD: the writer is the SINGLE encode point for the RevcloudProps
  // XData appid. Delegate to the shared resolver so the serialized XData and
  // the APPID-table synthesis (serializeDxfDocument) agree on exactly one
  // source of truth.
  let xdata = [...(entity.xdata ?? [])];
  if (entity.type === "REVCLOUD") {
    xdata = resolveRevcloudXData(entity);
  }

  // Write XDATA blocks if present
  for (const xdataEntry of xdata) {
    pushGroup(groups, 1001, xdataEntry.appName);
    for (const xdataGroup of xdataEntry.groups) {
      groups.push(xdataGroup);
    }
  }

  return groups;
}

/**
 * Serialize a standalone SEQEND that terminates a legacy heavy POLYLINE to its
 * canonical DXF groups, for raw-passthrough round-trip (SL-22 F1). The parser
 * classifies a heavy `POLYLINE` + its `VERTEX` children as `unsupported-entity`
 * (preserved verbatim) but the terminating `SEQEND` as a TYPED supported entity;
 * without this the terminator is dropped, leaving an unterminated sequence and
 * losing the SEQEND's own handle / owner / layer (data loss). The drawing-model
 * bridge reconstructs the SEQEND's `RawEntityGroup` from these groups (slicing the
 * leading `0 SEQEND` header) so it re-emits verbatim right after the last VERTEX.
 *
 * `forceLayerEmit` is set so the SEQEND ALWAYS carries group 8 (SL-22 F3, the
 * "write code 8 on every entity" decision) — mirroring the INSERT-terminating
 * SEQEND above, which forces group 8 for the same reason. Returns the FULL groups
 * (incl. the `0 SEQEND` header) so the caller mirrors the existing
 * `entity.groups.slice(1)` unsupported-entity handling.
 */
export function serializeSeqendPassthroughGroups(
  seqend: DxfSeqendEntity,
): DxfGroup[] {
  return serializeSupportedEntity(
    { ...seqend, common: { ...seqend.common, forceLayerEmit: true } },
    false,
    false,
  );
}

function serializeEntity(
  entity: DxfEntity,
  escapeUnicode: boolean,
  emitClipMode: boolean,
): DxfGroup[] {
  if (entity.kind === "unsupported-entity") {
    return entity.groups;
  }

  // INSERT with attached ATTRIB children: serialize the INSERT itself,
  // then each ATTRIB entity, then a SEQEND. This mirrors the AutoCAD
  // DXF structure and lets the parser roll them back up on round-trip.
  if (
    entity.type === "INSERT" &&
    entity.geometry.attribs !== undefined &&
    entity.geometry.attribs.length > 0
  ) {
    const out: DxfGroup[] = [];
    out.push(...serializeSupportedEntity(entity, escapeUnicode, emitClipMode));
    for (const attrib of entity.geometry.attribs) {
      // T9b review round 3 — an ATTRIB child is owned by the SAME BLOCK_RECORD
      // as its INSERT (ezdxf `Insert.add_attrib`: `attrib.dxf.owner =
      // self.dxf.owner`; AutoCAD writes `ATTRIB 330 <space record>`). The typed
      // model carries no per-attribute owner, so a bridge-built ATTRIB arrives
      // owner-less: inherit the INSERT's owner when the ATTRIB has none (a
      // parsed ATTRIB keeps its own 330 verbatim). Never fabricated.
      const owned =
        attrib.common.ownerHandle === undefined && entity.common.ownerHandle
          ? { ...attrib, common: { ...attrib.common, ownerHandle: entity.common.ownerHandle } }
          : attrib;
      out.push(...serializeSupportedEntity(owned, escapeUnicode, emitClipMode));
    }
    // SEQEND terminator. We keep the handle from the parsed SEQEND if
    // we had one; otherwise the writer emits it without a handle and the
    // caller is free to allocate one at a higher layer.
    const seqendCommon: DxfCommonEntityProps = {};
    if (entity.common.layer !== undefined)
      seqendCommon.layer = entity.common.layer;
    if (entity.common.space !== undefined)
      seqendCommon.space = entity.common.space;
    if (entity.common.layoutName)
      seqendCommon.layoutName = entity.common.layoutName;
    if (entity.geometry.seqendHandle)
      seqendCommon.handle = entity.geometry.seqendHandle;
    // T9b review round 3 (P1) — the SEQEND's `330` is the parent INSERT's OWN
    // handle, NOT the space BLOCK_RECORD: ezdxf 1.4.3 writes `INSERT 5 34 /
    // 330 17`, `SEQEND 5 35 / 330 34` (`Insert.new_seqend` / `add_attrib` set
    // `seqend.dxf.owner = insert.dxf.handle`), and AutoCAD files carry the
    // same (`SEQEND 260 owner 25A`, `10B6 owner 10B1` in
    // tmp_dxf/mechanical_metric_full.dxf — every owner an INSERT handle).
    // Emitted only when the INSERT has a handle; never fabricated.
    if (entity.common.handle)
      seqendCommon.ownerHandle = entity.common.handle;
    // FINDING 1+4 — the SEQEND terminating an INSERT+ATTRIB* sequence is itself an
    // ENTITIES-section record; on the conformant from-scratch path it must emit
    // group 8 like its parent INSERT (user decision: "write code 8 on every
    // entity"). Inherit `forceLayerEmit` from the parent so the SEQEND is not the
    // lone block-child record silently omitting code 8 for layer "0".
    if (entity.common.forceLayerEmit) seqendCommon.forceLayerEmit = true;
    const seqend: DxfSupportedEntity = {
      kind: "supported-entity",
      type: "SEQEND",
      common: seqendCommon,
      subclassMarkers: [],
      extraGroups: [],
      geometry: {},
    };
    out.push(...serializeSupportedEntity(seqend, escapeUnicode, emitClipMode));
    return out;
  }

  // POLYLINE with its folded VERTEX run: serialize the POLYLINE, then every
  // VERTEX, then the SEQEND — the DXF reference's "complex entity" layout,
  // and the exact inverse of the grouping pass in ./entityGrouping.
  if (entity.type === "POLYLINE") {
    const out: DxfGroup[] = [];
    out.push(...serializeSupportedEntity(entity, escapeUnicode, emitClipMode));
    for (const vertex of entity.geometry.vertices) {
      // A VERTEX is owned by its POLYLINE (ezdxf `Polyline.append_vertex`
      // sets `vertex.dxf.owner = polyline.dxf.handle`; AutoCAD writes the
      // same). A parsed VERTEX keeps its own 330 verbatim; only a
      // bridge-built one, which arrives owner-less, inherits it. Never
      // fabricated when the POLYLINE itself has no handle.
      const owned =
        vertex.common.ownerHandle === undefined && entity.common.handle
          ? {
              ...vertex,
              common: { ...vertex.common, ownerHandle: entity.common.handle },
            }
          : vertex;
      out.push(...serializeSupportedEntity(owned, escapeUnicode, emitClipMode));
    }
    // The terminating SEQEND. Unlike INSERT — which keeps only the handle and
    // rebuilds the record — POLYLINE retains the WHOLE parsed SEQEND, so its
    // own handle, owner AND layer round-trip verbatim. Only a bridge-built
    // polyline (no parsed SEQEND) gets one synthesised from the parent, and
    // then strictly from the parent's real values.
    const parsedSeqend = entity.geometry.seqend;
    const seqendCommon: DxfCommonEntityProps = parsedSeqend
      ? { ...parsedSeqend.common }
      : {};
    if (!parsedSeqend) {
      if (entity.common.layer !== undefined) seqendCommon.layer = entity.common.layer;
      if (entity.common.space !== undefined) seqendCommon.space = entity.common.space;
      if (entity.common.layoutName) seqendCommon.layoutName = entity.common.layoutName;
      if (entity.common.handle) seqendCommon.ownerHandle = entity.common.handle;
    }
    // SL-22 F3 / "write code 8 on every entity" — the terminating SEQEND is
    // itself an ENTITIES-section record and must not be the lone one omitting
    // group 8. Forced UNCONDITIONALLY, exactly as the pre-typed raw-passthrough
    // path did (`serializeSeqendPassthroughGroups` set it on every SEQEND it
    // serialized, not only on a conformant from-scratch export), so making the
    // heavy polyline typed does not quietly drop the layer group the SL-22 gate
    // pins.
    seqendCommon.forceLayerEmit = true;
    const seqend: DxfSupportedEntity = {
      kind: "supported-entity",
      type: "SEQEND",
      common: seqendCommon,
      subclassMarkers: parsedSeqend?.subclassMarkers ?? [],
      extraGroups: parsedSeqend?.extraGroups ?? [],
      geometry: {},
      ...(parsedSeqend?.xdata ? { xdata: parsedSeqend.xdata } : {}),
    };
    out.push(...serializeSupportedEntity(seqend, escapeUnicode, emitClipMode));
    return out;
  }

  return serializeSupportedEntity(entity, escapeUnicode, emitClipMode);
}

/* ── DIMVAR name→code reverse mapping for DIMSTYLE table writing ── */
// Mirror of DIMVAR_CODE_MAP in parser.ts. Must stay in sync.

const DIMVAR_NAME_TO_CODE: Record<string, number> = {
  // Real-number variables
  DIMSCALE: 40,
  DIMASZ: 41,
  DIMEXO: 42,
  DIMDLI: 43,
  DIMEXE: 44,
  DIMRND: 45,
  DIMDLE: 46,
  DIMTP: 47,
  DIMTM: 48,
  DIMTXT: 140,
  DIMCEN: 141,
  DIMTSZ: 142,
  DIMALTF: 143,
  DIMLFAC: 144,
  DIMTVP: 145,
  DIMTFAC: 146,
  DIMGAP: 147,
  DIMALTRND: 148,
  // Color indices
  DIMCLRD: 176,
  DIMCLRE: 177,
  DIMCLRT: 178,
  // Integer codes
  DIMADEC: 179,
  DIMDEC: 271,
  DIMTDEC: 272,
  DIMALTU: 273,
  DIMALTTD: 274,
  DIMAUNIT: 275,
  DIMFRAC: 276,
  DIMLUNIT: 277,
  DIMDSEP: 278,
  DIMTMOVE: 279,
  // Boolean/integer codes (0/1)
  DIMTOL: 71,
  DIMLIM: 72,
  DIMTIH: 73,
  DIMTOH: 74,
  DIMSE1: 75,
  DIMSE2: 76,
  DIMTAD: 77,
  DIMZIN: 78,
  DIMAZIN: 79,
  DIMALT: 170,
  DIMALTD: 171,
  DIMTOFL: 172,
  DIMSAH: 173,
  DIMTIX: 174,
  DIMSOXD: 175,
  DIMJUST: 280,
  DIMSD1: 281,
  DIMSD2: 282,
  DIMTOLJ: 283,
  DIMTZIN: 284,
  DIMALTZ: 285,
  DIMALTTZ: 286,
  DIMUPT: 288,
  DIMATFIT: 289,
  // Line weight integers
  DIMLWD: 371,
  DIMLWE: 372,
  // String codes
  DIMPOST: 3,
  DIMAPOST: 4,
  DIMTXSTY: 340,
  DIMLDRBLK: 341,
  DIMBLK: 342,
  DIMBLK1: 343,
  DIMBLK2: 344,
};

function serializeTextStyleEntry(entry: DxfTextStyleEntry): DxfGroup[] {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "STYLE");
  if (entry.handle) pushGroup(groups, 5, entry.handle);
  // T9b — the owning STYLE table-head handle sits between the record handle
  // and the subclass markers (Autodesk DXF reference common symbol-table
  // record layout; ezdxf writes `5 / 330 / 100 / 100`).
  if (entry.ownerHandle) pushGroup(groups, 330, entry.ownerHandle);
  // ROOT E — emit the AcDbSymbolTableRecord + AcDbTextStyleTableRecord subclass
  // markers ONLY when the source carried them (R13+), mirroring
  // serializeLinetypeEntry's `hadSubclassMarker !== false` guard. A parsed R12 /
  // marker-less record (`false`) must NOT gain synthesised 100/ groups — that
  // broke byte-faithful carry-forward on ALL export paths (incl. default-OFF).
  // `undefined` (a freshly-built / conformant-skeleton record) emits them, the
  // R13+ default — so the skeleton stays conformant while imports stay faithful.
  if (entry.hadSubclassMarker !== false) {
    pushGroup(groups, 100, "AcDbSymbolTableRecord");
    pushGroup(groups, 100, "AcDbTextStyleTableRecord");
  }
  pushGroup(groups, 2, entry.name);
  if (entry.flags !== undefined) pushGroup(groups, 70, entry.flags);
  if (entry.height !== undefined) pushGroup(groups, 40, entry.height);
  if (entry.widthFactor !== undefined) pushGroup(groups, 41, entry.widthFactor);
  if (entry.obliqueAngle !== undefined)
    pushGroup(groups, 50, entry.obliqueAngle);
  if (entry.generationFlags !== undefined)
    pushGroup(groups, 71, entry.generationFlags);
  if (entry.lastHeight !== undefined) pushGroup(groups, 42, entry.lastHeight);
  if (entry.fontName !== undefined) pushGroup(groups, 3, entry.fontName);
  if (entry.bigFontFile !== undefined) pushGroup(groups, 4, entry.bigFontFile);
  if (entry.fontCharset !== undefined)
    pushGroup(groups, 1071, entry.fontCharset);
  return groups;
}

function serializeDimStyleEntry(entry: DxfDimensionStyleEntry): DxfGroup[] {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "DIMSTYLE");
  // SL-4 — the DIMSTYLE symbol-table RECORD handle uses group code 105, NOT 5.
  // DIMSTYLE is the SOLE table whose record handle is emitted at 105 (an R13+
  // DXF quirk: code 5 in a DIMSTYLE record is the obsolete DIMBLK object-id, so
  // the actual record handle moved to 105). Every OTHER symbol-table record
  // (LAYER/STYLE/LTYPE/BLOCK_RECORD) keeps 5. Verified against Autodesk R2018
  // DIMSTYLE (GUID-F2FAD36F) + ezdxf `dxfentity.py`
  // (`_handle_code = 105 if dxftype=="DIMSTYLE" else 5`). The table-level HEAD
  // handle (serialized by `pushSymbolTableHead`) is unaffected — it stays 5.
  //
  // SL-4 P2 (@codex #425) — the handle group code MUST match the record SHAPE
  // emitted just below (the subclass-marker guard uses the SAME `!== false`
  // discriminant). A parsed marker-less / R12 record (`hadSubclassMarker ===
  // false`) keeps the minimal R12 shape (no `100/` subclass markers) and its
  // record handle is group 5, NOT 105 — the 105 quirk is an R13+ feature, so
  // forcing 105 onto an R12 record would break that shape. A R13+ / fresh /
  // skeleton record (`hadSubclassMarker !== false`, i.e. `true` or `undefined`,
  // which DOES emit AcDbSymbolTableRecord/AcDbDimStyleTableRecord below) uses 105.
  if (entry.handle)
    pushGroup(groups, entry.hadSubclassMarker === false ? 5 : 105, entry.handle);
  // T9b — owning DIMSTYLE table-head handle (see serializeTextStyleEntry).
  if (entry.ownerHandle) pushGroup(groups, 330, entry.ownerHandle);
  // ROOT E — emit the subclass markers ONLY when the source carried them (see
  // serializeTextStyleEntry / serializeLinetypeEntry). `false` (parsed R12)
  // suppresses; `undefined` (fresh / skeleton) emits the R13+ default pair.
  if (entry.hadSubclassMarker !== false) {
    pushGroup(groups, 100, "AcDbSymbolTableRecord");
    pushGroup(groups, 100, "AcDbDimStyleTableRecord");
  }
  pushGroup(groups, 2, entry.name);
  // DIMSTYLE flag byte (group 70) — bit 8 (value 16) is the annotative
  // table-entry flag. Fold the normalized DIMANNO dimvar back into the
  // flag byte on write so the DXF round-trips through AutoCAD without
  // losing the annotative attribute. Other bits are not currently
  // round-tripped (see DxfDimensionStyleEntry type).
  const dimAnno = entry.dimVars.DIMANNO;
  const annoBit =
    typeof dimAnno === "number" && dimAnno !== 0
      ? 16
      : typeof dimAnno === "string" && Number(dimAnno) !== 0
        ? 16
        : 0;
  // T9b — an R13+ DIMSTYLE record (subclass markers emitted) ALWAYS carries
  // its flag byte: AutoCAD and ezdxf write `70 0` on a non-annotative style
  // (Autodesk DIMSTYLE group codes: 70 "Standard flag values"). A parsed
  // marker-less R12 record keeps the previous shape (70 only when annotative)
  // so its byte-faithful carry-forward is unchanged.
  if (annoBit !== 0 || entry.hadSubclassMarker !== false)
    pushGroup(groups, 70, annoBit);
  for (const [varName, varValue] of Object.entries(entry.dimVars)) {
    // DIMANNO is serialized via group 70 bit 8 above — don't double-emit
    // it as a regular DIMVAR. There is no dedicated DIMVAR code for
    // DIMANNO in the DXF reference.
    if (varName === "DIMANNO") continue;
    const code = DIMVAR_NAME_TO_CODE[varName];
    if (code !== undefined) {
      pushGroup(groups, code, varValue);
    }
  }
  return groups;
}

function serializeLayerEntry(entry: DxfLayerEntry): DxfGroup[] {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "LAYER");
  if (entry.handle) pushGroup(groups, 5, entry.handle);
  // T9b — owning LAYER table-head handle (see serializeTextStyleEntry).
  if (entry.ownerHandle) pushGroup(groups, 330, entry.ownerHandle);
  pushGroup(groups, 100, "AcDbSymbolTableRecord");
  pushGroup(groups, 100, "AcDbLayerTableRecord");
  pushGroup(groups, 2, entry.name);
  pushGroup(groups, 70, entry.flags);
  pushGroup(groups, 62, entry.color);
  pushGroup(groups, 6, entry.linetype);
  if (entry.plottable === false) pushGroup(groups, 290, 0);
  if (entry.lineweight !== -3) pushGroup(groups, 370, entry.lineweight);
  // T9b — PlotStyleName (390, R2000+) then MATERIAL (347, R2007+) pointers, in
  // the ezdxf / AutoCAD order `… 6 [290] 370 390 347`. Emitted only when the
  // bridge resolved a real target object (never a fabricated handle).
  if (entry.plotStyleHandle) pushGroup(groups, 390, entry.plotStyleHandle);
  if (entry.materialHandle) pushGroup(groups, 347, entry.materialHandle);
  return groups;
}

function serializeLinetypeElement(el: DxfLinetypeElement): DxfGroup[] {
  const groups: DxfGroup[] = [];
  // group 49 — the signed dash/gap/dot length (preserved verbatim).
  pushGroup(groups, 49, el.length);
  // group 74 — element type bitfield. FIX-6 (Codex#2 P2): some older/simple DXF
  // writers OMIT 74 after 49 for a plain dash/gap/dot element; `parseLinetypeEntry`
  // leaves `elementType` undefined for those. Always re-emitting 74 would make a
  // simple element GAIN a 74 it never had (not byte-identical). Emit 74 only when:
  //   (a) the element actually carried one on import (`elementType` defined), OR
  //   (b) it is a COMPLEX element that REQUIRES 74 (text → bit2, shape → bit4):
  //       detectable here by a present `text` (group 9) or `shapeNumber` (group 75).
  // A simple element whose `elementType` was undefined emits NO 74.
  const isComplex = el.text !== undefined || el.shapeNumber !== undefined;
  if (el.elementType !== undefined) {
    pushGroup(groups, 74, el.elementType);
  } else if (isComplex) {
    // Defensive: a complex element with no explicit type still needs a 74
    // (text=2 / shape=4). The bridge always sets elementType for complex
    // elements, so this branch is a safety net for hand-built entries.
    pushGroup(groups, 74, el.text !== undefined ? 2 : 4);
  }
  // Complex (text/shape) codes — emitted only when present, in the
  // canonical DXF order 75 / 340 / 46 / 50 / 44 / 45 / 9.
  if (el.shapeNumber !== undefined) pushGroup(groups, 75, el.shapeNumber);
  if (el.styleHandle !== undefined) pushGroup(groups, 340, el.styleHandle);
  if (el.scale !== undefined) pushGroup(groups, 46, el.scale);
  if (el.rotation !== undefined) pushGroup(groups, 50, el.rotation);
  if (el.offsetX !== undefined) pushGroup(groups, 44, el.offsetX);
  if (el.offsetY !== undefined) pushGroup(groups, 45, el.offsetY);
  if (el.text !== undefined) pushGroup(groups, 9, el.text);
  return groups;
}

function serializeLinetypeEntry(entry: DxfLinetypeEntry): DxfGroup[] {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "LTYPE");
  if (entry.handle) pushGroup(groups, 5, entry.handle);
  if (entry.ownerHandle) pushGroup(groups, 330, entry.ownerHandle);
  // FIX-B (Codex#2 PR-LT2 P2) — emit the subclass markers only when the source
  // record carried them. R12 / minimal DXFs omit `100/AcDbLinetypeTableRecord`
  // (and the paired `100/AcDbSymbolTableRecord`); synthesising them broke byte
  // parity for those files. `hadSubclassMarker === false` (a parsed R12 record)
  // suppresses them; `undefined` (a freshly built / seeded entry) defaults to
  // emit, since AutoCAD-R13+ output always carries both markers.
  if (entry.hadSubclassMarker !== false) {
    pushGroup(groups, 100, "AcDbSymbolTableRecord");
    pushGroup(groups, 100, "AcDbLinetypeTableRecord");
  }
  pushGroup(groups, 2, entry.name);
  pushGroup(groups, 70, entry.flags ?? 0);
  pushGroup(groups, 3, entry.description ?? "");
  pushGroup(groups, 72, entry.alignment ?? 65);
  // group 73 — element count (RAW preferred so a plain round-trip re-emits
  // the file's original even if it disagrees with elements.length).
  pushGroup(groups, 73, entry.elementCount ?? entry.elements.length);
  // group 40 — total pattern length (RAW preferred).
  pushGroup(
    groups,
    40,
    entry.patternLength ??
      entry.elements.reduce((sum, el) => sum + Math.abs(el.length), 0),
  );
  for (const el of entry.elements) {
    groups.push(...serializeLinetypeElement(el));
  }
  // Vendor / unknown group codes preserved at parse time, re-emitted at the
  // RECORD END (after all pattern elements).
  //
  // FIX-C (Codex#2 PR-LT2 P2 — DOCUMENT-DEFER): `extraGroups` captures vendor
  // group VALUES but NOT their POSITION relative to the pattern elements. When a
  // source file interleaves vendor groups BEFORE or BETWEEN the 49/74/… element
  // codes, this loop re-emits them all grouped at the record end → not
  // byte-identical for those (rare) files. Preserving exact interleaving means
  // recording a per-extra-group position index in `parseLinetypeEntry` (which
  // accumulates elements in a single forward pass), adding a positional shape to
  // both `DxfLinetypeEntry.extraGroups` and the drawing-model `Linetype.`
  // `extraGroups` schema, and interleaving here — broad plumbing across dxf-io +
  // drawing-model for a corner case AutoCAD itself does not produce (it emits
  // vendor/XData groups at the record end, exactly where this loop puts them).
  // TODO(PR-LTx): preserve exact extraGroups interleaving if a real-world file
  // is found that places vendor groups between pattern elements. Until then the
  // grouped-at-end re-emission is the documented, AutoCAD-consistent behaviour.
  if (entry.extraGroups) {
    for (const g of entry.extraGroups) groups.push(g);
  }
  return groups;
}

function serializeBlockRecordEntry(entry: DxfBlockRecordEntry): DxfGroup[] {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "BLOCK_RECORD");
  if (entry.handle) pushGroup(groups, 5, entry.handle);
  // DYN-4 Phase 6 — emit the ACAD_XDICTIONARY block BEFORE 330, matching
  // the AutoCAD canonical order (parser at sectionParsers.ts:400-410 reads
  // this nested 102/360 pair to populate extensionDictionaryHandle). Without
  // this emission, a round-tripped BLOCK_RECORD loses its link to
  // ACAD_ENHANCEDBLOCK → ACAD_EVALUATION_GRAPH, which in turn breaks
  // `buildBlockToEvalGraph` on re-import and drops every constraint-based
  // block's typed dynamicMetadata.
  if (entry.extensionDictionaryHandle) {
    pushGroup(groups, 102, "{ACAD_XDICTIONARY");
    pushGroup(groups, 360, entry.extensionDictionaryHandle);
    pushGroup(groups, 102, "}");
  }
  // Batch-6 S-BLOCKRECORD-REACTORS — re-emit the persistent-reactor block
  // (parsed into `entry.reactorHandles` but previously never written back).
  // Emitted after the ext-dict block and before 330, matching AutoCAD's
  // canonical 102-block ordering. No double-emit: parser routes 330s inside
  // the reactor block into `reactorHandles`, not `ownerHandle`/extraGroups.
  if (entry.reactorHandles && entry.reactorHandles.length > 0) {
    pushGroup(groups, 102, "{ACAD_REACTORS");
    for (const h of entry.reactorHandles) pushGroup(groups, 330, h);
    pushGroup(groups, 102, "}");
  }
  if (entry.ownerHandle) pushGroup(groups, 330, entry.ownerHandle);
  pushGroup(groups, 100, "AcDbSymbolTableRecord");
  pushGroup(groups, 100, "AcDbBlockTableRecord");
  pushGroup(groups, 2, entry.name);
  if (entry.layoutHandle) pushGroup(groups, 340, entry.layoutHandle);
  if (entry.insertionUnits !== undefined)
    pushGroup(groups, 70, entry.insertionUnits);
  if (entry.explodability !== undefined)
    pushGroup(groups, 280, entry.explodability);
  if (entry.scalability !== undefined)
    pushGroup(groups, 281, entry.scalability);
  // Vendor / unknown group codes
  if (entry.extraGroups) {
    for (const g of entry.extraGroups) groups.push(g);
  }
  // XData — AcDbBlockRepresentation (annotative flag etc.)
  if (entry.xdata) {
    for (const xd of entry.xdata) {
      pushGroup(groups, 1001, xd.appName);
      for (const g of xd.groups) groups.push(g);
    }
  } else if (entry.isAnnotative) {
    // Synthesize a minimal annotative XData record when the caller
    // toggled isAnnotative without an existing XData payload.
    pushGroup(groups, 1001, "AcDbBlockRepresentation");
    pushGroup(groups, 1000, "AcDbBlockRepresentation");
    pushGroup(groups, 1070, 1);
  }
  // PR-5 — AcadAnnotativeDecomposition XData synthesis. When the typed
  // `annotativeDecompositions` map is populated (per-scale anonymous
  // block handles), emit the XData group sequence AutoCAD writes for it.
  // We only synthesise here when the existing `xdata` array does NOT
  // already contain an `AcadAnnotativeDecomposition` entry — that path
  // means the importer captured the XData verbatim and round-tripping
  // it through `entry.xdata` above already covered it.
  if (entry.annotativeDecompositions) {
    const existingHasDecomposition = entry.xdata?.some(
      (xd) => xd.appName === "AcadAnnotativeDecomposition",
    );
    if (!existingHasDecomposition) {
      const decompGroups = serializeAcadAnnotativeDecomposition(
        entry.annotativeDecompositions,
      );
      for (const g of decompGroups) groups.push(g);
    }
  }
  return groups;
}

function serializeTablesSection(section: DxfTablesSection): DxfGroup[] {
  const groups: DxfGroup[] = [];

  // Write raw table groups first (LTYPE, etc. — preserved verbatim)
  // NOTE: use for-loop instead of spread for the same reason as below
  // at serializeObjectsSection — pathological DXF files with huge custom
  // TABLE entries would overflow V8's argument-count limit on
  // `push(...huge)`.
  for (const g of section.rawTableGroups) groups.push(g);

  // Write LTYPE table (PR-LT2). Promoted out of `rawTableGroups`, so it is
  // serialized HERE, exactly once — the raw passthrough above no longer
  // carries any LTYPE groups (the parser routed them into the typed array).
  // AutoCAD emits LTYPE before LAYER (layers reference linetypes).
  //
  // FIX-A (Codex#2 PR-LT2 P2 — preserve an EMPTY LTYPE table): a DXF whose
  // LTYPE table carries ONLY table-level header groups + ZERO `0/LTYPE` records
  // (e.g. `70/0`) is captured by the parser as a `linetypeTableHeader` with an
  // empty `section.linetypes`. The old guard (`length > 0`) skipped the table
  // entirely → a parse→serialize round-trip DROPPED the whole LTYPE table that
  // previously passed through raw. Emit the table when there are typed records
  // OR when a header was captured: a header-only table re-emits the corrected
  // `70/0` and zero records, preserving the explicitly-empty state. The SEEDED
  // model path (non-empty `section.linetypes`) is unchanged — that path always
  // has records and is governed by the `length > 0` branch.
  const hasLinetypeRecords = !!(
    section.linetypes && section.linetypes.length > 0
  );
  const hasLinetypeHeader = !!(
    section.linetypeTableHeader && section.linetypeTableHeader.length > 0
  );
  if (hasLinetypeRecords || hasLinetypeHeader) {
    const linetypes = section.linetypes ?? [];
    pushGroup(groups, 0, "TABLE");
    pushGroup(groups, 2, "LTYPE");
    // Re-emit the captured table-LEVEL header (table handle 5, owner 330,
    // subclass 100/AcDbSymbolTable, vendor codes) verbatim when the importer
    // preserved it (Codex#2 P2). Otherwise synthesise a minimal header.
    //
    // FIX-5 (Codex#2 P2) — the persisted header's group 70 is the ORIGINAL
    // file's entry count (e.g. 41). But the typed export FILTERS records (FIX-4
    // drops unused synthetic-seed linetypes), so the emitted record count
    // (`linetypes.length`) may differ. Re-emitting the stale 70 would produce a
    // malformed table claiming N records while emitting M≠N. STRIP the persisted
    // 70 and emit a single correct 70/<actual count> ourselves — identical to
    // the no-header fallback — so the emitted 70 ALWAYS equals the emitted
    // record count, regardless of filtering (FIX-A: count is 0 for an empty
    // header-only table).
    if (hasLinetypeHeader) {
      for (const g of section.linetypeTableHeader!) {
        if (g.code === 70) continue; // drop stale count; re-emitted below
        groups.push(g);
      }
    }
    pushGroup(groups, 70, linetypes.length);
    for (const lt of linetypes) {
      groups.push(...serializeLinetypeEntry(lt));
    }
    pushGroup(groups, 0, "ENDTAB");
  }

  // Write LAYER table.
  //
  // FINDING 3 (T9 round-2 P3) — emit the table even when the typed record array
  // is EMPTY but the parser captured an explicit table HEAD (`70/0` head-only
  // table). The old `length > 0` gate skipped a header-only table entirely, so a
  // `serializeDxfDocument(parseDxfDocument(...))` round-trip DROPPED the whole
  // explicit table (data loss). Mirrors the LTYPE path above
  // (`hasLinetypeRecords || hasLinetypeHeader`): emit the head + `0/ENDTAB` with
  // `pushSymbolTableHead` re-emitting `70/0` in canonical position. The SEEDED
  // model path (non-empty array) is unchanged.
  if (section.layers.length > 0 || hasSymbolTableHeader(section, "LAYER")) {
    pushGroup(groups, 0, "TABLE");
    pushGroup(groups, 2, "LAYER");
    pushSymbolTableHead(groups, section, "LAYER", section.layers.length);
    for (const layer of section.layers) {
      groups.push(...serializeLayerEntry(layer));
    }
    pushGroup(groups, 0, "ENDTAB");
  }

  // Write STYLE table (FINDING 3 — preserve a header-only table; see LAYER above).
  if (
    section.textStyles.length > 0 ||
    hasSymbolTableHeader(section, "STYLE")
  ) {
    pushGroup(groups, 0, "TABLE");
    pushGroup(groups, 2, "STYLE");
    pushSymbolTableHead(groups, section, "STYLE", section.textStyles.length);
    for (const style of section.textStyles) {
      groups.push(...serializeTextStyleEntry(style));
    }
    pushGroup(groups, 0, "ENDTAB");
  }

  // Write DIMSTYLE table (FINDING 3 — preserve a header-only table; see LAYER above).
  if (
    section.dimensionStyles.length > 0 ||
    hasSymbolTableHeader(section, "DIMSTYLE")
  ) {
    pushGroup(groups, 0, "TABLE");
    pushGroup(groups, 2, "DIMSTYLE");
    pushSymbolTableHead(
      groups,
      section,
      "DIMSTYLE",
      section.dimensionStyles.length,
    );
    for (const style of section.dimensionStyles) {
      groups.push(...serializeDimStyleEntry(style));
    }
    pushGroup(groups, 0, "ENDTAB");
  }

  // Write BLOCK_RECORD table. AutoCAD requires this table to be present
  // because every BLOCK entry in the BLOCKS section needs a matching
  // BLOCK_RECORD in the TABLES section. (FINDING 3 — preserve a header-only
  // table; see LAYER above.)
  if (
    section.blockRecords.length > 0 ||
    hasSymbolTableHeader(section, "BLOCK_RECORD")
  ) {
    pushGroup(groups, 0, "TABLE");
    pushGroup(groups, 2, "BLOCK_RECORD");
    pushSymbolTableHead(
      groups,
      section,
      "BLOCK_RECORD",
      section.blockRecords.length,
    );
    for (const entry of section.blockRecords) {
      groups.push(...serializeBlockRecordEntry(entry));
    }
    pushGroup(groups, 0, "ENDTAB");
  }

  return groups;
}

/**
 * FINDING 3 (T9 round-2 P3) — whether the parser captured an explicit table HEAD
 * for `tableName` (a non-empty `symbolTableHeaders[tableName]`). Mirrors the
 * `hasLinetypeHeader` predicate the LTYPE path uses so a header-only symbol table
 * (head + zero records) is re-emitted instead of dropped on a round-trip.
 */
function hasSymbolTableHeader(
  section: DxfTablesSection,
  tableName: string,
): boolean {
  const head = section.symbolTableHeaders?.[tableName];
  return !!(head && head.length > 0);
}

/**
 * Emit the table-LEVEL head (`5/<handle> 330/0 100/AcDbSymbolTable 70/<count>`)
 * for a typed symbol table (LAYER / STYLE / DIMSTYLE / BLOCK_RECORD), between the
 * `2/<name>` marker (caller-emitted) and the first record.
 *
 * FIX-110 — these four typed-table writers previously emitted NO head, so a
 * head-less table reached ezdxf, whose `audit()` SILENTLY repaired it ("Fixed
 * invalid table head handle in table …") — i.e. AutoCAD would invoke recovery.
 *
 * When the parser captured the real head (`section.symbolTableHeaders[name]`,
 * persisted across the model round-trip via the document field), re-emit it
 * PRESERVING the original group order EXCEPT the stale `70` count: typed export
 * may filter records, so the persisted count can diverge from `recordCount`; we
 * replace the captured `70`'s VALUE with `recordCount` IN PLACE (where the
 * original `70` was). A captured head ALWAYS carries a `5/<handle>` — that is the
 * whole point of FIX-110 — so no synthesised handle is needed on this path.
 *
 * CANONICAL POSITION (Codex P2-#2/#3) — the `70` count belongs to the
 * `AcDbSymbolTable` subclass scope and MUST precede any secondary subclass
 * marker. ezdxf `ezdxf.new("R2000")` emits a DIMSTYLE head as
 * `0/TABLE 2/DIMSTYLE 5/<h> 330/0 100/AcDbSymbolTable 70/<count> 100/AcDbDimStyleTable`
 * — i.e. the `70` sits BETWEEN `100/AcDbSymbolTable` and `100/AcDbDimStyleTable`,
 * NOT after the secondary marker. Re-appending the count at the END of the head
 * (the old behaviour) produced `… 100/AcDbSymbolTable 100/AcDbDimStyleTable 70`,
 * which is non-canonical. Two corrections:
 *  (a) replace the `70` VALUE in place (preserving order) instead of stripping +
 *      re-appending;
 *  (b) if the captured head has NO `70` at all, INSERT `70/<recordCount>`
 *      immediately after the `100/AcDbSymbolTable` marker so a `70` is ALWAYS
 *      present in canonical position (matching the LTYPE path which always emits
 *      a `70`, regardless of whether the captured head carried one).
 */
function pushSymbolTableHead(
  groups: DxfGroup[],
  section: DxfTablesSection,
  tableName: string,
  recordCount: number,
): void {
  const head = section.symbolTableHeaders?.[tableName];
  if (!head || head.length === 0) return;
  const sawCount = head.some((g) => g.code === 70);
  for (const g of head) {
    if (g.code === 70) {
      // (a) replace the stale count with the recomputed one IN PLACE, so the
      // `70` keeps its original (canonical) position inside the head.
      pushGroup(groups, 70, recordCount);
      continue;
    }
    groups.push(g);
    // (b) the captured head had no `70`: insert it immediately after the
    // `100/AcDbSymbolTable` marker so a `70` is ALWAYS present in canonical
    // position (before any secondary `100/AcDb<X>Table` subclass marker).
    if (!sawCount && g.code === 100 && g.value === "AcDbSymbolTable") {
      pushGroup(groups, 70, recordCount);
    }
  }
}

function serializeDictionaryEntry(entry: DxfDictionaryEntry): DxfGroup[] {
  const groups: DxfGroup[] = [];
  // Batch-6 S-OBJECTS-DICT-ROUNDTRIP — re-emit the correct 0-marker so an
  // ACDBDICTIONARYWDFLT round-trips as itself (not a plain DICTIONARY).
  pushGroup(groups, 0, entry.isWithDefault ? "ACDBDICTIONARYWDFLT" : "DICTIONARY");
  if (entry.handle) pushGroup(groups, 5, entry.handle);
  // Nested extension-dictionary / reactor 102 blocks (mirror BLOCK_RECORD).
  // Emitted before 330 to match AutoCAD's canonical ordering.
  if (entry.extensionDictionaryHandle) {
    pushGroup(groups, 102, "{ACAD_XDICTIONARY");
    pushGroup(groups, 360, entry.extensionDictionaryHandle);
    pushGroup(groups, 102, "}");
  }
  if (entry.reactorHandles && entry.reactorHandles.length > 0) {
    pushGroup(groups, 102, "{ACAD_REACTORS");
    for (const h of entry.reactorHandles) pushGroup(groups, 330, h);
    pushGroup(groups, 102, "}");
  }
  if (entry.ownerHandle) pushGroup(groups, 330, entry.ownerHandle);
  pushGroup(groups, 100, "AcDbDictionary");
  pushGroup(groups, 280, entry.hardOwned ? 1 : 0);
  pushGroup(groups, 281, entry.cloning);
  for (const ref of entry.entries) {
    pushGroup(groups, 3, ref.name);
    pushGroup(groups, ref.isHard ? 360 : 350, ref.handle);
  }
  // ACDBDICTIONARYWDFLT default-entry subclass + handle (group 340).
  if (entry.isWithDefault || entry.defaultHandle !== undefined) {
    pushGroup(groups, 100, "AcDbDictionaryWithDefault");
    if (entry.defaultHandle !== undefined) {
      pushGroup(groups, 340, entry.defaultHandle);
    }
  }
  // Vendor / unknown group codes preserved verbatim.
  if (entry.extraGroups) {
    for (const g of entry.extraGroups) groups.push(g);
  }
  // XData (1001-1071) preserved verbatim.
  if (entry.xdata) {
    for (const xd of entry.xdata) {
      pushGroup(groups, 1001, xd.appName);
      for (const g of xd.groups) groups.push(g);
    }
  }
  return groups;
}

function serializeMultileaderStyleEntry(
  entry: DxfMultileaderStyleEntry,
): DxfGroup[] {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "MLEADERSTYLE");
  if (entry.handle) pushGroup(groups, 5, entry.handle);
  if (entry.ownerHandle) pushGroup(groups, 330, entry.ownerHandle);
  pushGroup(groups, 100, "AcDbMLeaderStyle");
  if (entry.name) pushGroup(groups, 2, entry.name);
  if (entry.description !== undefined) pushGroup(groups, 3, entry.description);
  if (entry.contentType !== undefined)
    pushGroup(groups, 170, entry.contentType);
  // SL-5 F2/F4 (@codex round-4) — leader_type is ezdxf `acdb_mleader_style`
  // group 173 ({0=invisible,1=straight,2=spline}), NOT 171 (that is
  // draw_mleader_order_type, which has no typed field and round-trips verbatim
  // via extraGroups). Verified against ezdxf 1.4.3 mleader.py + `new()` output.
  if (entry.leaderType !== undefined) pushGroup(groups, 173, entry.leaderType);
  if (entry.textLeftAttachmentTypeDeprecated !== undefined)
    pushGroup(groups, 172, entry.textLeftAttachmentTypeDeprecated);
  // SL-5 F2/F4 — text_right_attachment_type is ezdxf group 178, NOT 173 (173 is
  // leader_type). Was colliding with leaderType on 173 under the old inverted map.
  if (entry.textRightAttachmentType !== undefined)
    pushGroup(groups, 178, entry.textRightAttachmentType);
  if (entry.textLeftAttachmentType !== undefined)
    pushGroup(groups, 174, entry.textLeftAttachmentType);
  if (entry.textAngleType !== undefined)
    pushGroup(groups, 175, entry.textAngleType);
  if (entry.textAlignmentType !== undefined)
    pushGroup(groups, 176, entry.textAlignmentType);
  if (entry.blockContentConnectionType !== undefined)
    pushGroup(groups, 177, entry.blockContentConnectionType);
  if (entry.maxLeaderSegmentPointsNumber !== undefined)
    pushGroup(groups, 90, entry.maxLeaderSegmentPointsNumber);
  if (entry.firstSegmentAngleConstraint !== undefined)
    pushGroup(groups, 40, entry.firstSegmentAngleConstraint);
  if (entry.secondSegmentAngleConstraint !== undefined)
    pushGroup(groups, 41, entry.secondSegmentAngleConstraint);
  if (entry.landingDistance !== undefined)
    pushGroup(groups, 42, entry.landingDistance); // 42 = landing_gap_size
  // SL-5 P3b — 43 = dogleg_length (ezdxf acdb_mleader_style). Previously never
  // emitted (no typed field), so a non-default dogleg was lost on round-trip.
  if (entry.doglegLength !== undefined)
    pushGroup(groups, 43, entry.doglegLength);
  if (entry.arrowheadSize !== undefined)
    pushGroup(groups, 44, entry.arrowheadSize);
  if (entry.textHeight !== undefined) pushGroup(groups, 45, entry.textHeight);
  if (entry.alignSpace !== undefined) pushGroup(groups, 46, entry.alignSpace);
  if (entry.blockContentScaleX !== undefined)
    pushGroup(groups, 47, entry.blockContentScaleX);
  if (entry.blockContentScaleY !== undefined)
    pushGroup(groups, 49, entry.blockContentScaleY);
  if (entry.blockContentScaleZ !== undefined)
    pushGroup(groups, 140, entry.blockContentScaleZ);
  if (entry.blockContentRotation !== undefined)
    pushGroup(groups, 141, entry.blockContentRotation);
  if (entry.scaleOverall !== undefined)
    pushGroup(groups, 142, entry.scaleOverall);
  if (entry.breakGapSize !== undefined)
    pushGroup(groups, 143, entry.breakGapSize);
  if (entry.leaderLineColor !== undefined)
    pushGroup(groups, 91, entry.leaderLineColor);
  if (entry.leaderLineWeight !== undefined)
    pushGroup(groups, 92, entry.leaderLineWeight);
  if (entry.textColor !== undefined) pushGroup(groups, 93, entry.textColor);
  if (entry.blockContentColor !== undefined)
    pushGroup(groups, 94, entry.blockContentColor);
  if (entry.enableLanding !== undefined)
    pushGroup(groups, 290, entry.enableLanding ? 1 : 0);
  if (entry.enableDogleg !== undefined)
    pushGroup(groups, 291, entry.enableDogleg ? 1 : 0);
  if (entry.textFrameEnabled !== undefined)
    pushGroup(groups, 292, entry.textFrameEnabled ? 1 : 0);
  if (entry.enableBlockContentScale !== undefined)
    pushGroup(groups, 293, entry.enableBlockContentScale ? 1 : 0);
  if (entry.enableBlockContentRotation !== undefined)
    pushGroup(groups, 294, entry.enableBlockContentRotation ? 1 : 0);
  if (entry.enableAnnotationScale !== undefined)
    pushGroup(groups, 296, entry.enableAnnotationScale ? 1 : 0);
  if (entry.textAlignAlwaysLeft !== undefined)
    pushGroup(groups, 297, entry.textAlignAlwaysLeft ? 1 : 0);
  if (entry.textAttachmentDirection !== undefined)
    pushGroup(groups, 271, entry.textAttachmentDirection);
  // #432 r9 E3 — 272 text_bottom_attachment_type / 273 text_top_attachment_type
  // (ezdxf acdb_mleader_style, both default 9). The parser reads them but the
  // writer emitted only 271 → they re-defaulted to 9 on round-trip. Emit after
  // 271, from the typed fields (source-preserved).
  if (entry.textBottomAttachmentType !== undefined)
    pushGroup(groups, 272, entry.textBottomAttachmentType);
  if (entry.textTopAttachmentType !== undefined)
    pushGroup(groups, 273, entry.textTopAttachmentType);
  if (entry.textDefault !== undefined)
    pushGroup(groups, 300, entry.textDefault);
  // SL-5 F2/F4 (@codex round-4) — CORRECTED ref-handle group codes per ezdxf
  // 1.4.3 `acdb_mleader_style` (verified against `new()` tag output):
  //   340 = leader_linetype_handle · 341 = arrow_head_handle
  //   342 = text_style_handle      · 343 = block_record_handle
  // The prior map was INVERTED (textStyle→340 / leaderLinetype→341 /
  // arrowhead→342). Inert while the serializer was dead + round-trip-only
  // (self-consistent inverse), but WRONG once authored/name-resolved styles emit
  // real refs: AutoCAD/ezdxf would read a text-style handle as a leader linetype.
  // Emitted in ascending code order to mirror ezdxf's canonical layout.
  if (entry.leaderLineTypeHandle !== undefined)
    pushGroup(groups, 340, entry.leaderLineTypeHandle);
  if (entry.arrowheadBlockHandle !== undefined)
    pushGroup(groups, 341, entry.arrowheadBlockHandle);
  if (entry.textStyleHandle !== undefined)
    pushGroup(groups, 342, entry.textStyleHandle);
  if (entry.blockContentHandle !== undefined)
    pushGroup(groups, 343, entry.blockContentHandle);
  // Unknown / vendor-specific group codes are written back verbatim
  for (const [code, value] of Object.entries(entry.extraGroups)) {
    const numericCode = Number.parseInt(code, 10);
    if (Number.isFinite(numericCode)) {
      pushGroup(groups, numericCode, value);
    }
  }
  return groups;
}

function serializeLayoutEntry(entry: DxfLayoutEntry): DxfGroup[] {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "LAYOUT");
  if (entry.handle) pushGroup(groups, 5, entry.handle);
  // ROOT C — the LAYOUT's OWNER soft pointer here is the ACAD_LAYOUT DICTIONARY
  // (not the BLOCK_RECORD). Emit the dictionary owner if known; the BLOCK_RECORD
  // back-reference is emitted as a 330 INSIDE the AcDbLayout subclass below
  // (that is the 330 ezdxf reads to pair LAYOUT↔BLOCK_RECORD). Fall back to
  // `ownerHandle` for legacy entries that carry only one 330 so existing
  // single-tab round-trips are byte-unchanged.
  if (entry.dictionaryOwnerHandle) {
    pushGroup(groups, 330, entry.dictionaryOwnerHandle);
  } else if (entry.ownerHandle) {
    pushGroup(groups, 330, entry.ownerHandle);
  }
  // FIX E (round-9 #4) — re-emit any HEADER-slot nested `102` reactor /
  // xdictionary block in its ORIGINAL position (after the 5/330 owner, BEFORE
  // the `100 AcDbPlotSettings` marker) so an import→export of a LAYOUT carrying
  // a header reactor block is byte-faithful. The parser routed these groups into
  // `headerExtraGroups` (distinct from the tail `extraGroups` emitted at the END
  // below) precisely so this slot is preserved.
  if (entry.headerExtraGroups) {
    for (const g of entry.headerExtraGroups) groups.push(g);
  }
  // ── AcDbPlotSettings sub-object (PDF-6.1) ──
  // Emitted in AutoCAD's canonical group order so a writer-produced LAYOUT
  // re-parses identically and matches the structure AutoCAD authors.
  pushGroup(groups, 100, "AcDbPlotSettings");
  if (entry.pageSetupName !== undefined)
    pushGroup(groups, 1, entry.pageSetupName);
  if (entry.paperSize !== undefined) pushGroup(groups, 4, entry.paperSize);
  if (entry.plotViewName !== undefined)
    pushGroup(groups, 6, entry.plotViewName);
  if (entry.plotMargins) {
    pushGroup(groups, 40, entry.plotMargins.left);
    pushGroup(groups, 41, entry.plotMargins.bottom);
    pushGroup(groups, 42, entry.plotMargins.right);
    pushGroup(groups, 43, entry.plotMargins.top);
  }
  if (entry.paperWidthMm !== undefined)
    pushGroup(groups, 44, entry.paperWidthMm);
  if (entry.paperHeightMm !== undefined)
    pushGroup(groups, 45, entry.paperHeightMm);
  if (entry.plotOrigin) {
    pushGroup(groups, 46, entry.plotOrigin.x);
    pushGroup(groups, 47, entry.plotOrigin.y);
  }
  if (entry.plotWindow) {
    pushGroup(groups, 48, entry.plotWindow.lowerLeftX);
    pushGroup(groups, 49, entry.plotWindow.lowerLeftY);
    pushGroup(groups, 140, entry.plotWindow.upperRightX);
    pushGroup(groups, 141, entry.plotWindow.upperRightY);
  }
  if (entry.plotScale) {
    pushGroup(groups, 142, entry.plotScale.numerator);
    pushGroup(groups, 143, entry.plotScale.denominator);
  }
  if (entry.plotLayoutFlags !== undefined)
    pushGroup(groups, 70, entry.plotLayoutFlags);
  if (entry.plotPaperUnits !== undefined)
    pushGroup(groups, 72, entry.plotPaperUnits);
  if (entry.plotRotation !== undefined)
    pushGroup(groups, 73, entry.plotRotation);
  if (entry.plotAreaType !== undefined)
    pushGroup(groups, 74, entry.plotAreaType);
  if (entry.plotStyleName !== undefined)
    pushGroup(groups, 7, entry.plotStyleName);
  pushGroup(groups, 100, "AcDbLayout");
  pushGroup(groups, 1, entry.name);
  // ROOT C — the AcDbLayout 330 hard pointer back to the owning BLOCK_RECORD.
  // ezdxf (and AutoCAD) pair each LAYOUT with its paper/model BLOCK_RECORD by
  // THIS 330; without it a multi-tab document's extra `*Paper_Space<N>` tabs are
  // orphaned and their entities collapse onto the first paper layout. Emitted
  // ONLY when a `dictionaryOwnerHandle` was emitted above (so a legacy entry
  // carrying a single 330 — already written before AcDbPlotSettings — is not
  // double-emitted here).
  if (entry.dictionaryOwnerHandle && entry.ownerHandle) {
    pushGroup(groups, 330, entry.ownerHandle);
  }
  if (entry.tabOrder !== undefined) pushGroup(groups, 71, entry.tabOrder);
  if (entry.minimumLimits) {
    pushGroup(groups, 10, entry.minimumLimits.x);
    pushGroup(groups, 20, entry.minimumLimits.y);
  }
  if (entry.maximumLimits) {
    pushGroup(groups, 11, entry.maximumLimits.x);
    pushGroup(groups, 21, entry.maximumLimits.y);
  }
  if (entry.insertionBase) {
    pushGroup(groups, 12, entry.insertionBase.x);
    pushGroup(groups, 22, entry.insertionBase.y);
    pushGroup(groups, 32, entry.insertionBase.z);
  }
  if (entry.minimumExtents) {
    pushGroup(groups, 14, entry.minimumExtents.x);
    pushGroup(groups, 24, entry.minimumExtents.y);
    pushGroup(groups, 34, entry.minimumExtents.z);
  }
  if (entry.maximumExtents) {
    pushGroup(groups, 15, entry.maximumExtents.x);
    pushGroup(groups, 25, entry.maximumExtents.y);
    pushGroup(groups, 35, entry.maximumExtents.z);
  }
  if (entry.elevation !== undefined) pushGroup(groups, 146, entry.elevation);
  if (entry.orthographicViewType !== undefined)
    pushGroup(groups, 76, entry.orthographicViewType);
  // Round-trip any vendor / unrecognised group codes.
  if (entry.extraGroups) {
    for (const g of entry.extraGroups) groups.push(g);
  }
  return groups;
}

function serializeDynamicBlockObject(obj: DxfDynamicBlockObject): DxfGroup[] {
  // Dynamic block authoring objects round-trip through their raw group
  // list. The bridge layer (dxfBridge.dynamic.ts) is responsible for
  // reconstituting `rawGroups` whenever the metadata shape changes; on
  // a pure read/write round-trip we preserve every byte verbatim.
  return obj.rawGroups.slice();
}

/**
 * Hoist the OBJECTS-section ROOT DICTIONARY (the "named object dictionary") to
 * the FIRST record position of the serialized group stream.
 *
 * AutoCAD/ezdxf bootstrap the OBJECTS graph by taking the FIRST object in the
 * section as the root dictionary (`ObjectsSection.rootdict` =
 * `entity_space[0]`). A strict `ezdxf.readfile` raises
 * `DXFStructureError: invalid root dictionary entity <X>` when that first
 * record is NOT a DICTIONARY. The carry-forward export path emits SYNTHESIZED
 * typed records (layouts, dynamic-block objects, annotative scales) BEFORE the
 * `rawObjectGroups` catch-all, and the root DICTIONARY lives in
 * `rawObjectGroups` — so a doc with even one LAYOUT used to emit
 * `LAYOUT(...)` first and fail strict readfile. This re-orders the assembled
 * stream so the root DICTIONARY leads, satisfying the bootstrap requirement
 * for EVERY export (imported docs too). All other records keep their relative
 * order; handle-based references are order-independent for the rest of the
 * graph, so only the lead position matters.
 *
 * The root dictionary is the DICTIONARY / ACDBDICTIONARYWDFLT record whose
 * owner soft-pointer (group 330) is `"0"` (owned by the drawing root). When no
 * such record is present (a section with no carried OBJECTS graph, or an
 * already-leading root) the stream is returned unchanged.
 */
function hoistRootDictionaryFirst(groups: DxfGroup[]): DxfGroup[] {
  // Identify record boundaries: each record starts at a group with code 0 and
  // runs until the next code-0 group (or end of stream).
  const recordStarts: number[] = [];
  for (let i = 0; i < groups.length; i += 1) {
    if (groups[i].code === 0) recordStarts.push(i);
  }
  if (recordStarts.length === 0) return groups;

  const isRootDictRecord = (start: number, end: number): boolean => {
    const typeValue = groups[start].value;
    if (typeValue !== "DICTIONARY" && typeValue !== "ACDBDICTIONARYWDFLT") {
      return false;
    }
    // ROOT F — the owner soft-pointer (330) is the FIRST top-level 330, i.e. one
    // emitted OUTSIDE any `102 {...}` nested block. `serializeDictionaryEntry`
    // emits reactor handles as `102 {ACAD_REACTORS / 330 <reactorHandle> / 102 }`
    // (and an xdict 360) BEFORE the owner `330 0`, so a reactor-bearing root dict
    // would otherwise be mis-identified by the FIRST 330 (a reactor handle ≠ "0")
    // → never hoisted → strict readfile fails. Track 102 `{...}` / `}` nesting
    // depth and accept a 330 as the owner pointer ONLY at depth 0 (mirrors the
    // parser's depth-0 owner logic).
    let depth = 0;
    for (let i = start + 1; i < end; i += 1) {
      const g = groups[i];
      if (g.code === 102) {
        const tok = g.value.trim();
        if (tok.startsWith("{")) depth += 1;
        else if (tok === "}" && depth > 0) depth -= 1;
        continue;
      }
      // First TOP-LEVEL owner soft-pointer. The root dictionary is owned by the
      // drawing root, encoded as handle "0". Reactor/xdict 330s inside an open
      // 102 block (depth > 0) are ignored.
      if (g.code === 330 && depth === 0) return g.value === "0";
    }
    return false;
  };

  for (let r = 0; r < recordStarts.length; r += 1) {
    const start = recordStarts[r];
    const end =
      r + 1 < recordStarts.length ? recordStarts[r + 1] : groups.length;
    if (!isRootDictRecord(start, end)) continue;
    // Already first — nothing to move.
    if (start === 0) return groups;
    const rootRecord = groups.slice(start, end);
    const rest = [...groups.slice(0, start), ...groups.slice(end)];
    return [...rootRecord, ...rest];
  }
  return groups;
}

function serializeObjectsSection(section: DxfObjectsSection): DxfGroup[] {
  const groups: DxfGroup[] = [];
  for (const dict of section.dictionaries) {
    const gs = serializeDictionaryEntry(dict);
    for (const g of gs) groups.push(g);
  }
  for (const style of section.multileaderStyles) {
    const gs = serializeMultileaderStyleEntry(style);
    for (const g of gs) groups.push(g);
  }
  for (const layout of section.layouts) {
    const gs = serializeLayoutEntry(layout);
    for (const g of gs) groups.push(g);
  }
  for (const dyn of section.dynamicBlockObjects) {
    const gs = serializeDynamicBlockObject(dyn);
    for (const g of gs) groups.push(g);
  }
  // N9 AW-0-OBJECTS-BUCKET-ORDER — Batch-8 S-ARRAYASSOC-WRITE AcDbAssocArray
  // graph records used to be emitted here, UNCONDITIONALLY ahead of the
  // rawObjectGroups catch-all — hoisting every array graph out of its true
  // interleaved position (the byte-position residual AW-0 explicitly
  // deferred). Mirrors the SPATIAL_FILTER fix (Codex #319 P2, finding 7)
  // exactly: the parser now leaves a PLACEHOLDER SENTINEL at each record's
  // position, and the catch-all loop below substitutes the re-synthesised
  // record for the sentinel — see the `ASSOC_ARRAY_PLACEHOLDER_CODE` branch
  // and the sentinel-less flush after the loop.
  // PR-5 — annotative scale catalogue + per-scale context-data records.
  // Emitted BEFORE the rawObjectGroups catch-all so they stay grouped
  // alongside the dictionaries that own them. The bridge layer (PR-7)
  // is responsible for ensuring the OBJECTS section's typed fields are
  // populated; when absent (legacy DXF without annotative data) these
  // loops are no-ops.
  if (section.acadScales) {
    for (const scale of section.acadScales) {
      const gs = serializeAcDbScale(scale);
      for (const g of gs) groups.push(g);
    }
  }
  if (section.annotScaleContexts) {
    for (const rec of section.annotScaleContexts) {
      const gs = serializeAnnotScaleContextRecord(rec);
      for (const g of gs) groups.push(g);
    }
  }
  // ACAD_SCALELIST dictionary — emitted only when the typed catalogue
  // entry is present AND the dictionary is not already represented in
  // `section.dictionaries` under any entry whose body lists ACAD_SCALELIST.
  // Today the importer captures ACAD_SCALELIST inside `dictionaries[]`
  // (it is just a regular DICTIONARY object), so the typed `acadScaleListDict`
  // is the round-trip channel only when the bridge synthesises a fresh one
  // (e.g. on first save of a DXF that lacked the dictionary entirely).
  if (
    section.acadScaleListDict &&
    !alreadyInDictionaries(section, section.acadScaleListDict.handle)
  ) {
    const gs = serializeAcDbScaleListDict(section.acadScaleListDict);
    for (const g of gs) groups.push(g);
  }
  // EPIC-VIEWPORT-CLIP SLICE 2 — XCLIP / SPATIAL_FILTER records. Codex #319 P2,
  // finding 7: a PARSED SPATIAL_FILTER can be INTERLEAVED among unsupported
  // OBJECTS records, so the parser left a PLACEHOLDER SENTINEL inside
  // rawObjectGroups at the record's position. The catch-all loop below
  // substitutes the re-synthesised record for the sentinel so its relative order
  // is preserved (vs hoisting all spatialFilters[] ahead of the catch-all, which
  // lost interleaved position). A typed filter with NO matching sentinel
  // (synthetic / bridge-built XCLIP carrying the typed list but no position
  // marker) is FLUSHED after the catch-all so it is never silently dropped — the
  // sentinel loop is no longer the only serialization path (finding 7).
  //
  // Unknown / unsupported OBJECTS entries round-trip verbatim. They are
  // emitted last so named MLEADERSTYLE records stay next to their
  // owning DICTIONARY.
  // NOTE: avoid `groups.push(...section.rawObjectGroups)` — real-world
  // DXF files (e.g. АБИ1.dxf) can carry 500k+ raw object groups, and
  // `Array.push(...huge)` overflows the Node call stack.
  // Resolve SPATIAL_FILTER sentinels by the record's STABLE handle (DXF group
  // 5) — index-independent, so a consumer that reordered/filtered
  // section.spatialFilters[] cannot misalign the substitution (Codex pre-PR
  // MEDIUM). Built once. A handle-less (malformed/synthetic) record's sentinel
  // carries a prefixed positional index instead.
  const spatialFiltersByHandle = new Map<string, DxfSpatialFilter>();
  for (const f of section.spatialFilters ?? []) {
    if (f.handle !== "") spatialFiltersByHandle.set(f.handle, f);
  }
  // N9 — same handle-keyed resolution map for AcDbAssocArray graph records
  // (mirrors spatialFiltersByHandle exactly).
  const assocArraysByHandle = new Map<string, DxfAssocArrayObject>();
  for (const a of section.assocArrays ?? []) {
    if (a.handle !== "") assocArraysByHandle.set(a.handle, a);
  }
  // Track which typed filters a sentinel actually re-emits so the sentinel-less
  // remainder (synthetic / bridge-built records carrying no rawObjectGroups
  // sentinel) can be flushed AFTER the catch-all (Codex #319 P2, finding 7). A
  // caller that builds a DxfObjectsSection with `spatialFilters` but no sentinel
  // (a new XCLIP, or any consumer carrying the typed list) must NOT have its
  // records silently dropped — the sentinel loop is no longer the ONLY path.
  const emittedFilters = new Set<DxfSpatialFilter>();
  // N9 — same tracking set for AcDbAssocArray graph records.
  const emittedAssocArrays = new Set<DxfAssocArrayObject>();
  for (const g of section.rawObjectGroups) {
    if (g.code === SPATIAL_FILTER_PLACEHOLDER_CODE) {
      // Substitute the SPATIAL_FILTER record at this position. `value` is the
      // record's stable handle, or `${PREFIX}${index}` for a handle-less
      // record. Defensive: if the filter cannot be resolved, SKIP the sentinel
      // entirely (never leak a -1 group into the output).
      const filter = g.value.startsWith(SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX)
        ? section.spatialFilters?.[
            Number(
              g.value.slice(SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX.length),
            )
          ]
        : spatialFiltersByHandle.get(g.value);
      if (filter) {
        emittedFilters.add(filter);
        for (const sg of serializeSpatialFilter(filter)) groups.push(sg);
      }
      continue;
    }
    if (g.code === ASSOC_ARRAY_PLACEHOLDER_CODE) {
      // N9 — substitute the AcDbAssocArray graph record at this position.
      // Same resolution + defensive-skip contract as the SPATIAL_FILTER
      // branch above: `value` is the record's stable handle, or
      // `${PREFIX}${index}` for a handle-less record; an unresolvable
      // sentinel is DROPPED rather than leaking a placeholder group into the
      // output.
      //
      // @codex (PR #551 review history) — round 5 tried the opposite
      // (pass an unresolvable match through verbatim) to guard against a
      // malformed/adversarial raw group coincidentally sharing
      // `ASSOC_ARRAY_PLACEHOLDER_CODE`'s numeric value; round 6 then found
      // THAT broke the handle-based-lookup design contract the SPATIAL_FILTER
      // precedent documents immediately above (a caller filtering/removing an
      // entry from `section.assocArrays` after parsing is a SUPPORTED
      // scenario — the filtered entry's now-orphaned sentinel must be
      // DROPPED, not leaked as a corrupted group). Distinguishing "genuinely
      // orphaned sentinel" from "coincidental unrelated data sharing the same
      // code" by construction would need out-of-band ordinal tracking (real,
      // but a materially bigger change than this slice's scope, and a
      // divergence from the SPATIAL_FILTER precedent N9 is chartered to
      // mirror exactly, not improve on unilaterally). Reverted to DROP,
      // restoring 1:1 parity with SPATIAL_FILTER's already-shipped,
      // already-accepted behaviour. The residual "malformed/adversarial file
      // literally carries the exact magic code" risk stays exactly as small
      // as SPATIAL_FILTER's own equivalent `-1` residual risk — see
      // `ASSOC_ARRAY_PLACEHOLDER_CODE`'s doc (assocArrayPlaceholder.ts) for
      // why `-1000000` keeps that risk vanishingly small without needing the
      // pass-through escape hatch at all.
      const result = serializeAssocArrayPlaceholder(
        g,
        section.assocArrays,
        assocArraysByHandle,
      );
      if (result?.object) {
        emittedAssocArrays.add(result.object);
        for (const sg of result.groups) groups.push(sg);
      }
      continue;
    }
    groups.push(g);
  }
  // Flush any typed SPATIAL_FILTER that no sentinel claimed (finding 7). These
  // are records carried on `section.spatialFilters` without a matching position
  // marker in `rawObjectGroups` — a synthetically built XCLIP, or a bridge that
  // re-attaches the typed list. Emitted last (after the catch-all) so they never
  // vanish; their relative order matches the typed array.
  for (const f of section.spatialFilters ?? []) {
    if (emittedFilters.has(f)) continue;
    for (const sg of serializeSpatialFilter(f)) groups.push(sg);
  }
  // N9 — same sentinel-less flush for AcDbAssocArray graph records: a
  // synthetic array (built in code, no source position) or any caller that
  // carries `assocArrays` without a matching sentinel must not be silently
  // dropped.
  for (const a of section.assocArrays ?? []) {
    if (emittedAssocArrays.has(a)) continue;
    for (const sg of serializeAssocArrayObject(a)) groups.push(sg);
  }
  // Ensure the root DICTIONARY leads the OBJECTS section so AutoCAD/ezdxf can
  // bootstrap the named-object graph (entity_space[0] must be a DICTIONARY).
  return hoistRootDictionaryFirst(groups);
}

/**
 * PR-5 helper — true when an OBJECTS section already contains a
 * DICTIONARY entry whose handle matches the supplied one. Used to
 * prevent duplicate emission of the ACAD_SCALELIST dictionary when the
 * importer captured it through the regular `dictionaries[]` channel
 * AND the bridge layer also surfaced a typed `acadScaleListDict`.
 */
function alreadyInDictionaries(
  section: DxfObjectsSection,
  handle: string,
): boolean {
  if (!handle) return false;
  for (const dict of section.dictionaries) {
    if (dict.handle === handle) return true;
  }
  return false;
}

function serializeBlockDefinition(
  block: DxfBlockDefinition,
  // T9 PR-3 — propagated to block child entities so MTEXT inside a block
  // escape-then-chunks on the legacy write path. Block NAMES (group 2/3) are
  // escaped at the document chokepoint. Defaults to `false` for the document's
  // dimension *D### stub blocks (always pure-ASCII names + no MTEXT).
  escapeUnicode = false,
  // Version-gate for a WIPEOUT/IMAGE `clip_mode` (290) child of a block.
  // Defaults to `true` (the canonical R2018 target) for the dimension *D### stub
  // blocks, which never contain a WIPEOUT so the value is inert there.
  emitClipMode = true,
): DxfGroup[] {
  const groups: DxfGroup[] = [];
  // ── BLOCK header ──
  pushGroup(groups, 0, "BLOCK");
  if (block.handle) pushGroup(groups, 5, block.handle);
  if (block.ownerHandle) pushGroup(groups, 330, block.ownerHandle);
  pushGroup(groups, 100, "AcDbEntity");
  pushGroup(groups, 8, block.layer || "0");
  pushGroup(groups, 100, "AcDbBlockBegin");
  pushGroup(groups, 2, block.name);
  pushGroup(groups, 70, block.flags);
  pushGroup(groups, 10, block.basePoint.x);
  pushGroup(groups, 20, block.basePoint.y);
  pushGroup(groups, 30, block.basePoint.z);
  pushGroup(groups, 3, block.name);
  pushGroup(groups, 1, block.xrefPathName);
  if (block.description) pushGroup(groups, 4, block.description);

  // ── Child entities ──
  for (const entity of block.entities) {
    const entityGroups = serializeEntity(entity, escapeUnicode, emitClipMode);
    for (const g of entityGroups) groups.push(g);
  }

  // ── ENDBLK tail ──
  if (block.endblkGroups.length > 0) {
    for (const g of block.endblkGroups) groups.push(g);
  } else {
    // Reconstruct a minimal ENDBLK if the source file had none.
    //
    // T9b — an R13+ ENDBLK is an entity-like record: it carries its OWN
    // group-5 handle and a `330` owner = the BLOCK's owning BLOCK_RECORD (the
    // same value as the BLOCK's `330`; Autodesk DXF reference ENDBLK "Common
    // entity group codes", ezdxf BlockLayout writes `0 ENDBLK / 5 / 330 / 100
    // AcDbEntity / 8 / 100 AcDbBlockEnd`). Both are emitted only when known —
    // the conformant skeleton mints `endblkHandle` for every from-scratch block.
    pushGroup(groups, 0, "ENDBLK");
    if (block.endblkHandle) pushGroup(groups, 5, block.endblkHandle);
    if (block.ownerHandle) pushGroup(groups, 330, block.ownerHandle);
    pushGroup(groups, 100, "AcDbEntity");
    pushGroup(groups, 8, block.layer || "0");
    pushGroup(groups, 100, "AcDbBlockEnd");
  }

  return groups;
}

function serializeBlocksSection(
  section: DxfBlocksSection,
  escapeUnicode: boolean,
  emitClipMode: boolean,
): DxfGroup[] {
  const groups: DxfGroup[] = [];
  // Preserve any stray groups outside BLOCK/ENDBLK pairs first.
  for (const g of section.rawBlockGroups) groups.push(g);
  for (const block of section.blocks) {
    const blockGroups = serializeBlockDefinition(block, escapeUnicode, emitClipMode);
    for (const g of blockGroups) groups.push(g);
  }
  return groups;
}

function serializeSection(
  section: DxfSection,
  escapeUnicode: boolean,
  emitClipMode: boolean,
): DxfGroup[] {
  if (section.kind === "tables-section") {
    return [
      { code: 0, value: "SECTION" },
      { code: 2, value: "TABLES" },
      ...serializeTablesSection(section),
      { code: 0, value: "ENDSEC" },
    ];
  }

  if (section.kind === "objects-section") {
    return [
      { code: 0, value: "SECTION" },
      { code: 2, value: "OBJECTS" },
      ...serializeObjectsSection(section),
      { code: 0, value: "ENDSEC" },
    ];
  }

  if (section.kind === "blocks-section") {
    return [
      { code: 0, value: "SECTION" },
      { code: 2, value: "BLOCKS" },
      ...serializeBlocksSection(section, escapeUnicode, emitClipMode),
      { code: 0, value: "ENDSEC" },
    ];
  }

  const sectionName =
    section.kind === "raw-section" ? section.name : "ENTITIES";
  const sectionGroups =
    section.kind === "raw-section"
      ? section.groups
      : section.entities.flatMap((entity) =>
          serializeEntity(entity, escapeUnicode, emitClipMode),
        );

  return [
    { code: 0, value: "SECTION" },
    { code: 2, value: sectionName },
    ...sectionGroups,
    { code: 0, value: "ENDSEC" },
  ];
}

export function createRawSection(
  name: string,
  groups: DxfGroup[],
): DxfRawSection {
  return {
    kind: "raw-section",
    name,
    groups,
  };
}

/**
 * Pre-process the document so that every DIMENSION entity has a non-empty
 * `anonymousBlockName`. AutoCAD's DIMENSION entity is required to reference
 * a block (group code 2) that holds the graphical substitution. Modern
 * viewers recompute the block contents from the DIMENSION fields when the
 * referenced block is missing or empty, so we satisfy the spec by
 * generating unique `*D{n}` names without producing geometry inside the
 * BLOCK record.
 *
 * Returns the names assigned (so the caller can emit matching BLOCK
 * stubs) and a new document with the dimensions tagged.
 */
function assignAnonymousDimensionBlockNames(document: DxfDocument): {
  document: DxfDocument;
  assignedNames: string[];
} {
  const assignedNames: string[] = [];
  const used = new Set<string>();

  const isDimension = (
    entity: DxfEntity,
  ): entity is Extract<
    DxfSupportedEntity,
    { type: "DIMENSION" }
  > =>
    entity.kind === "supported-entity" && entity.type === "DIMENSION";

  // Collect existing names so we don't reassign duplicates. Scan BOTH the
  // ENTITIES section and the BLOCKS-section block definitions: Batch-7
  // admitted DIMENSION as a typed block-child, so a block-definition
  // dimension can already carry (or need) a `*D###` reference. Sharing one
  // `used` set across both scans prevents an entities-section dimension and
  // a block-child dimension from minting the SAME `*D###` name.
  //
  // CRITICAL (Codex #250 P2): also seed `used` with the names of EXISTING
  // BLOCK DEFINITIONS in the BLOCKS section, even when NO dimension
  // references them. Otherwise the minter can hand a freshly-empty
  // dimension a `*D{n}` that already names an unrelated existing block;
  // the later stub merge then sees the name exists, skips the stub, and the
  // new dimension silently points at that unrelated block. Seeding all
  // existing block names guarantees a minted `*D{n}` is genuinely unused.
  for (const section of document.sections) {
    if (section.kind === "entities-section") {
      for (const entity of section.entities) {
        if (isDimension(entity) && entity.geometry.anonymousBlockName) {
          used.add(entity.geometry.anonymousBlockName);
        }
      }
    } else if (section.kind === "blocks-section") {
      // Every existing structured block definition's name.
      for (const block of section.blocks) {
        if (block.name) used.add(block.name);
        for (const entity of block.entities) {
          if (isDimension(entity) && entity.geometry.anonymousBlockName) {
            used.add(entity.geometry.anonymousBlockName);
          }
        }
      }
      // Block names carried only in passthrough `rawBlockGroups` (group
      // code 2 within the verbatim block stream) — preserved for unusual
      // files that round-trip outside the structured `blocks[]` list.
      for (const g of section.rawBlockGroups) {
        if (g.code === 2 && typeof g.value === "string" && g.value) {
          used.add(g.value);
        }
      }
    } else if (section.kind === "raw-section" && section.name === "BLOCKS") {
      // Legacy raw BLOCKS section — block names appear as group-code-2
      // values inside the verbatim group stream.
      for (const g of section.groups) {
        if (g.code === 2 && typeof g.value === "string" && g.value) {
          used.add(g.value);
        }
      }
    }
  }

  let counter = 1;
  function nextBlockName(): string {
    while (used.has(`*D${counter}`)) counter++;
    const name = `*D${counter}`;
    used.add(name);
    counter++;
    return name;
  }

  // Assign `*D###` names + collect for stub emission across one entity list.
  // Applied identically to the ENTITIES section and to each block
  // definition's children, so a DIMENSION inside a BLOCK gets the same
  // valid group-2 reference + stub block an entities-section dimension does.
  // An already-set `anonymousBlockName` is preserved (never overwritten).
  function assignOverEntities(entities: DxfEntity[]): DxfEntity[] {
    return entities.map((entity) => {
      if (!isDimension(entity)) return entity;
      if (!entity.geometry.anonymousBlockName) {
        const name = nextBlockName();
        assignedNames.push(name);
        return {
          ...entity,
          geometry: { ...entity.geometry, anonymousBlockName: name },
        };
      }
      // Already named — also collect for stub emission so the BLOCKS
      // section is in sync with referenced blocks.
      assignedNames.push(entity.geometry.anonymousBlockName);
      return entity;
    });
  }

  const nextSections: DxfSection[] = document.sections.map((section) => {
    if (section.kind === "entities-section") {
      return { ...section, entities: assignOverEntities(section.entities) };
    }
    if (section.kind === "blocks-section") {
      return {
        ...section,
        blocks: section.blocks.map((block) => ({
          ...block,
          entities: assignOverEntities(block.entities),
        })),
      };
    }
    return section;
  });

  return { document: { ...document, sections: nextSections }, assignedNames };
}

/**
 * Build a stub BLOCKS section containing one empty BLOCK per anonymous
 * dimension block name. The BLOCK has the standard `*D###` shape with
 * flag 1 (anonymous) but no entities inside — modern viewers recompute
 * the visual representation from the DIMENSION fields and only need the
 * BLOCK record to exist for spec compliance.
 */
function buildStubBlockDefinition(
  name: string,
  minter: HandSeedMinter | undefined,
): DxfBlockDefinition {
  // T9b — mint the stub BLOCK's and its ENDBLK's own group-5 off the HEADER
  // `$HANDSEED` when the document has one (R13+ requires both; a handle-less
  // stub is silently re-minted by ezdxf). No `$HANDSEED` → legacy handle-less
  // shape (bare fixtures). The conformant bridge path materialises the `*D<n>`
  // block in the typed model instead, so this stub is the direct-caller fallback.
  return {
    name,
    handle: minter ? minter.mint() : "",
    layer: "0",
    flags: 1, // anonymous
    basePoint: { x: 0, y: 0, z: 0 },
    xrefPathName: "",
    description: "",
    entities: [],
    endblkGroups: [],
    ...(minter ? { endblkHandle: minter.mint() } : {}),
  };
}

function buildBlocksSectionStub(
  blockNames: string[],
  minter: HandSeedMinter | undefined,
): DxfBlocksSection | null {
  if (blockNames.length === 0) return null;

  const seen = new Set<string>();
  const blocks: DxfBlockDefinition[] = [];
  for (const name of blockNames) {
    if (seen.has(name)) continue;
    seen.add(name);
    blocks.push(buildStubBlockDefinition(name, minter));
  }

  return { kind: "blocks-section", blocks, rawBlockGroups: [] };
}

// ── Custom-XDATA APPID registration (SL-12) ──────────────────────────────────
//
// Every XData appid (group 1001) the writer emits MUST have a matching APPID
// symbol-table record, or a strict reader (ezdxf `readfile`, AutoCAD) flags or
// rejects the XData / silently attaches it to a synthesized appid. The exporter
// MINTS several custom appids that a from-scratch (`createDxfDocument`) or
// TABLES-less document would otherwise emit UNREGISTERED — e.g. RevcloudProps
// (REVCLOUD typed props), EDIT_DWG_VP_OVERRIDES (per-viewport override),
// EDIT_DWG_ARRAY_GROUP (array-group membership), AcDbBlockRepresentation /
// AcadAnnotativeDecomposition (BLOCK_RECORD annotative synthesis) — plus any
// imported / bridge-built vendor appid carried verbatim on an entity, an ATTRIB
// child, a table record or header, a DICTIONARY / LAYOUT / MLEADERSTYLE object,
// via a STRUCTURED `xdata` array OR the `extraGroups` passthrough.
//
// The gate below derives the emitted-appid set BY CONSTRUCTION from the ACTUAL
// serializer OUTPUT (`collectEmittedXDataAppids` runs the real serializers and
// scans every `1001` they emit) rather than a hand-mirrored walk that has to be
// kept in sync with every emission site (which leaked one site per @codex round).
// It then registers every appid not already declared in the APPID table.
// Idempotent: an appid already in the table (an imported vendor appid, or a
// re-run) is never duplicated — the existing-name set is DECODED first so an
// escaped `\U+XXXX` record matches its decoded emitted form (no false-absent).

/**
 * Collect the DISTINCT set of `1001` appids the document WILL emit, derived BY
 * CONSTRUCTION from what is ACTUALLY SERIALIZED: run the SAME serializers
 * `serializeDxfDocument` uses and scan their emitted `DxfGroup[]` for every
 * `1001` marker. A group code of 1001 in DXF ALWAYS starts an application-defined
 * XDATA run (there is no other meaning), so every `1001` in the emitted stream is
 * an appid that needs a matching APPID table record.
 *
 * This REPLACES the earlier hand-mirrored walk that RE-DERIVED the serializer's
 * field-by-field traversal and so kept MISSING emission sites one @codex-round at
 * a time (round-1: entity / LTYPE `extraGroups`; round-2: an INSERT's attached
 * ATTRIB children + the promoted table-level `linetypeTableHeader` /
 * `symbolTableHeaders`). Scanning the serializer OUTPUT cannot drift: any new
 * `1001` emission site added to ANY serializer is captured automatically, with no
 * parallel census to keep in sync.
 *
 * Completeness by construction — every `1001`-bearing site flows through one of:
 *   • entities-section → `serializeEntity` per entity (so an INSERT's attached
 *     ATTRIB children + terminating SEQEND, and unsupported-entity verbatim
 *     groups, are all covered).
 *   • blocks-section → the section's `rawBlockGroups` + `serializeBlockDefinition`
 *     per block (a block-child INSERT+ATTRIB is covered, top-level AND nested).
 *   • tables-section → `serializeTablesSection` (raw APPID/other tables verbatim,
 *     the promoted `linetypeTableHeader` + `symbolTableHeaders`, and every typed
 *     LTYPE / LAYER / STYLE / DIMSTYLE / BLOCK_RECORD record incl. the synthesized
 *     `AcDbBlockRepresentation` / `AcadAnnotativeDecomposition` XDATA).
 *   • objects-section → `serializeObjectsSection` (DICTIONARY / LAYOUT /
 *     MLEADERSTYLE / dynamic-block / assoc-array / spatial-filter / raw objects).
 *   • raw-section (HEADER / CLASSES / trailers) → its verbatim `groups` (they
 *     carry no `1001` in practice, but are scanned so the derivation stays total).
 *
 * A raw-passthrough `1001` (imported entity/table/object bytes) is scanned too:
 * a well-formed imported file already declares it in its APPID table, so the
 * `registered`/`missing` dedup in `ensureCustomXDataAppids` excludes it (no
 * output change); a malformed source missing the registration gets it added,
 * which is strictly MORE conformant (ezdxf/AutoCAD reject an unregistered appid).
 *
 * Memory: the bulk sections are scanned per-entity / per-block and the transient
 * groups discarded, so peak stays bounded to a single serialized unit; the small
 * TABLES section and the OBJECTS section (which `serializeDxfDocument` already
 * materializes in full downstream) are scanned whole. The census therefore never
 * raises the writer's existing memory ceiling. `escapeUnicode` / `emitClipMode`
 * are passed `false` because NEITHER changes WHICH `1001` groups are emitted (a
 * `1001` value lives verbatim in its `DxfGroup`; `\U+` escaping happens only at
 * the final string-emission step, and no entity is added or dropped by these
 * flags). Each scanned value is DECODED (`decodeDxfUnicode`, after trim) so it
 * canonicalises identically to the (also-decoded) registered-name set in
 * `rawTablesRegisteredAppids` — preserving the decode-dedup invariant (round-1
 * R3) with no separate structured-vs-verbatim handling.
 */
function collectEmittedXDataAppids(document: DxfDocument): Set<string> {
  const appids = new Set<string>();
  const scan = (groups: readonly DxfGroup[]): void => {
    for (const g of groups) {
      if (g.code === 1001) appids.add(decodeDxfUnicode(g.value.trim()));
    }
  };
  for (const section of document.sections) {
    switch (section.kind) {
      case "entities-section":
        // `serializeEntity` (not `serializeSupportedEntity`) so an INSERT's
        // attached ATTRIB children + the terminating SEQEND are included.
        for (const entity of section.entities) {
          scan(serializeEntity(entity, false, false));
        }
        break;
      case "blocks-section":
        scan(section.rawBlockGroups);
        for (const block of section.blocks) {
          scan(serializeBlockDefinition(block, false, false));
        }
        break;
      case "tables-section":
        scan(serializeTablesSection(section));
        break;
      case "objects-section":
        scan(serializeObjectsSection(section));
        break;
      case "raw-section":
        scan(section.groups);
        break;
    }
  }
  return appids;
}

// Groups for a single APPID (registered-application) table record. Per the DXF
// spec every XData appid (1001) must have a matching APPID table record or
// downstream readers may reject/ignore the XData (Codex #243 round-4 R4-B).
// T9b — carries its own group-5 (minted off the HEADER `$HANDSEED`) and the
// APPID table-head `330` owner whenever the document provides them: R13+
// requires both on every symbol-table record (ezdxf `acdb_appid` writes
// `5 / 330 / 100 / 100 / 2 / 70`); a handle-less record was silently re-minted
// by ezdxf on load. A document without `$HANDSEED` / table head keeps the
// legacy shape. Group codes verified against ezdxf 1.4.3 `acdb_appid` (2 name,
// 70 flags) + Autodesk R2018 APPID (`AcDbSymbolTableRecord` / `AcDbRegAppTableRecord`).
function buildAppidRecord(
  name: string,
  handle: string | undefined,
  ownerHandle: string | undefined,
): DxfGroup[] {
  return [
    { code: 0, value: "APPID" },
    ...(handle ? [{ code: 5, value: handle }] : []),
    ...(ownerHandle ? [{ code: 330, value: ownerHandle }] : []),
    { code: 100, value: "AcDbSymbolTableRecord" },
    { code: 100, value: "AcDbRegAppTableRecord" },
    { code: 2, value: name },
    { code: 70, value: "0" },
  ];
}

/**
 * The structural facts about the APPID TABLE inside a raw tables group stream,
 * located by WALKING the `0 TABLE / 2 <tablename> / … / 0 ENDTAB` structure —
 * NOT by matching an arbitrary `2 APPID` group. Both `rawTablesRegisteredAppids`
 * (dedup) and `insertAppidRecords` (splice) consume this so the two never
 * disagree on where the APPID table is.
 *
 * @codex #442 R6: a `2 APPID` is the APPID-table opener ONLY when it is the
 * TABLE's NAME group — i.e. it immediately follows a `0 TABLE` marker. A RECORD
 * inside another table (a VIEW/UCS/custom-table entry literally NAMED "APPID",
 * emitted as its `2 APPID` name group) is NOT the APPID table. The prior code
 * matched any `2 APPID` regardless of position, so such a record made the walker
 * treat the OTHER table as the APPID table → the splice rewrote that table's
 * `70` and inserted APPID records into the wrong table, while the real APPID
 * table stayed absent. Tracking `expectTableName` (armed by `0 TABLE`, consumed
 * by the next `2 <name>`) makes only a true table-name `2 APPID` count.
 */
interface AppidTableScan {
  /** A real APPID TABLE (`0 TABLE` → `2 APPID`) was found. */
  found: boolean;
  /** Index of the table-head max-entries `70` group (or -1). */
  headerCountIndex: number;
  /** Index of the `0 ENDTAB` closing the APPID table (or -1). */
  endtabIndex: number;
  /** Number of `0 APPID` records already present in the APPID table. */
  recordCount: number;
  /** DECODED `2 <name>` of every `0 APPID` record (dedup key set). */
  registeredNames: string[];
  /** T9b — the APPID table-head own `5` (the records' `330` owner), if any. */
  headHandle: string | undefined;
  /** Index of the table-name `2 APPID` group (or -1). */
  nameIndex: number;
  /** Index of the table-head `330` owner group (or -1). */
  headOwnerIndex: number;
  /** Index of the table-head `100 AcDbSymbolTable` marker (or -1). */
  headSubclassIndex: number;
}

function scanAppidTable(rawTableGroups: DxfGroup[]): AppidTableScan {
  let expectTableName = false;
  let inAppidTable = false;
  let sawFirstAppidRecord = false;
  let currentRecordIsAppid = false;
  let found = false;
  let headerCountIndex = -1;
  let endtabIndex = -1;
  let recordCount = 0;
  const registeredNames: string[] = [];
  let headHandle: string | undefined;
  let nameIndex = -1;
  let headOwnerIndex = -1;
  let headSubclassIndex = -1;

  for (let i = 0; i < rawTableGroups.length; i++) {
    const g = rawTableGroups[i]!;
    if (g.code === 0 && g.value === "TABLE") {
      // A new table opens — the NEXT `2 <name>` group is its name.
      expectTableName = true;
      inAppidTable = false;
      sawFirstAppidRecord = false;
      currentRecordIsAppid = false;
      continue;
    }
    if (expectTableName) {
      // The `2 <name>` directly after `0 TABLE` names the table (any handle /
      // owner / subclass groups before it are skipped). Only a table NAME of
      // "APPID" opens the APPID table — a record's `2 APPID` never reaches here.
      if (g.code === 2) {
        inAppidTable = g.value === "APPID";
        if (inAppidTable) {
          found = true;
          nameIndex = i;
        }
        expectTableName = false;
      }
      continue;
    }
    if (!inAppidTable) continue;
    if (g.code === 0 && g.value === "ENDTAB") {
      endtabIndex = i;
      break;
    }
    if (g.code === 0 && g.value === "APPID") {
      sawFirstAppidRecord = true;
      currentRecordIsAppid = true;
      recordCount += 1;
      continue;
    }
    // The table-head max-entries `70` precedes the first `0 APPID` record.
    if (g.code === 70 && !sawFirstAppidRecord && headerCountIndex === -1) {
      headerCountIndex = i;
      continue;
    }
    // T9b — the table-head own handle (`5`) likewise precedes the first record.
    if (g.code === 5 && !sawFirstAppidRecord && headHandle === undefined) {
      const value = g.value.trim();
      if (value.length > 0) headHandle = value;
      continue;
    }
    // T9b review round 2 — the head's `330` owner / `100 AcDbSymbolTable`
    // marker, so a head-less-handle table can be completed in canonical order.
    if (g.code === 330 && !sawFirstAppidRecord && headOwnerIndex === -1) {
      headOwnerIndex = i;
      continue;
    }
    if (
      g.code === 100 &&
      g.value === "AcDbSymbolTable" &&
      !sawFirstAppidRecord &&
      headSubclassIndex === -1
    ) {
      headSubclassIndex = i;
      continue;
    }
    if (g.code === 2 && currentRecordIsAppid) {
      // DECODE the raw `\U+XXXX`-escaped APPID-table name with the SAME DXF-unicode
      // decode the entity/block xdata parser applies to a `1001` appid name
      // (entityParser.ts). Otherwise a non-ASCII appid stored escaped here (raw
      // `\U+00C4`) never matches its DECODED emitted form ("Ä") in
      // `ensureCustomXDataAppids`' `missing` check → the already-registered appid
      // reads as ABSENT → a SECOND APPID record is spliced in → duplicate table
      // entries. Decoding both sides makes decoded-emitted == decoded-registered.
      registeredNames.push(decodeDxfUnicode(g.value));
      currentRecordIsAppid = false;
    }
  }

  return {
    found,
    headerCountIndex,
    endtabIndex,
    recordCount,
    registeredNames,
    headHandle,
    nameIndex,
    headOwnerIndex,
    headSubclassIndex,
  };
}

/**
 * T9b review round 2 (P2) — give an EXISTING APPID table whose head carries no
 * own `5` a minted head handle, completing the head in canonical order
 * (`2 APPID → 5 <h> → 330 0 → 100 AcDbSymbolTable → 70 <n> …`). Only what is
 * MISSING is added: an existing `330` / `100 AcDbSymbolTable` is moved into
 * its canonical slot, every other head group keeps its relative order. Returns
 * a new group array (the input is untouched).
 */
function withSynthesizedAppidHeadHandle(
  rawTableGroups: DxfGroup[],
  scan: AppidTableScan,
  headHandle: string,
): DxfGroup[] {
  // The head spans from the name group to the first record / ENDTAB.
  let headEnd = scan.nameIndex + 1;
  while (headEnd < rawTableGroups.length && rawTableGroups[headEnd]!.code !== 0) {
    headEnd += 1;
  }
  const placed = new Set<number>([scan.headOwnerIndex, scan.headSubclassIndex]);
  const owner: DxfGroup =
    scan.headOwnerIndex !== -1
      ? rawTableGroups[scan.headOwnerIndex]!
      : { code: 330, value: "0" };
  const subclass: DxfGroup =
    scan.headSubclassIndex !== -1
      ? rawTableGroups[scan.headSubclassIndex]!
      : { code: 100, value: "AcDbSymbolTable" };
  const rest: DxfGroup[] = [];
  for (let i = scan.nameIndex + 1; i < headEnd; i += 1) {
    if (!placed.has(i)) rest.push(rawTableGroups[i]!);
  }
  return [
    ...rawTableGroups.slice(0, scan.nameIndex + 1),
    { code: 5, value: headHandle },
    owner,
    subclass,
    ...rest,
    ...rawTableGroups.slice(headEnd),
  ];
}

/**
 * The SET of APPID names `rawTableGroups` already declares (the `2 <name>` of
 * each `0 APPID` record inside the APPID table). Used to dedupe so the writer
 * never double-registers an appid.
 */
function rawTablesRegisteredAppids(rawTableGroups: DxfGroup[]): Set<string> {
  return new Set(scanAppidTable(rawTableGroups).registeredNames);
}

/**
 * Insert one APPID record per name in `names` into a tables-section's raw APPID
 * table, synthesising the APPID table itself when the section has none.
 * Returns a new `rawTableGroups` array (never mutates the input). `names` MUST
 * be pre-filtered to appids not already registered (see `ensureCustomXDataAppids`).
 */
function insertAppidRecords(
  rawTableGroups: DxfGroup[],
  names: string[],
  minter: HandSeedMinter | undefined,
): DxfGroup[] {
  if (names.length === 0) return rawTableGroups;
  let groups = rawTableGroups;
  // Locate the existing APPID table structurally (`0 TABLE` → `2 APPID`, never an
  // arbitrary `2 APPID` record name — @codex #442 R6). The scan yields the
  // table-head max-entries `70` (kept in sync with the records we splice in), the
  // number of `0 APPID` records already present, the table-head handle (T9b —
  // the new records' `330` owner) and the `0 ENDTAB` that closes the table (the
  // splice point — the new records land right before it).
  let scan = scanAppidTable(groups);
  const splice = scan.found && scan.endtabIndex !== -1;
  // T9b review round 2 (P2) — an EXISTING APPID table whose head has no own `5`
  // would leave every new record without a `330` (and the head handle-less →
  // silently minted by ezdxf). When a minter exists, mint the head handle and
  // complete the head in canonical position; the records below then own it.
  if (splice && scan.headHandle === undefined && minter) {
    groups = withSynthesizedAppidHeadHandle(groups, scan, minter.mint());
    scan = scanAppidTable(groups);
  }
  // T9b review round 1 (P2) — a SYNTHESIZED APPID table needs its own table
  // HEAD: `5 <head handle> / 330 0 / 100 AcDbSymbolTable / 70 <count>` (the
  // shape `pushSymbolTableHead` re-emits for a captured head and ezdxf writes
  // for every symbol table; the head's owner is the null handle `0`). Without
  // it `scan.headHandle` is undefined, the records get NO `330` and ezdxf
  // silently re-homes them + mints the head. The head handle is minted FIRST
  // (before the records) off the same `$HANDSEED` minter so the records'
  // `330` can point at it; a document without a minter keeps the legacy
  // head-less shape (no handle space to mint from — never fabricate one).
  const headHandle = splice ? scan.headHandle : minter?.mint();
  const newRecords = names.flatMap((name) =>
    buildAppidRecord(name, minter?.mint(), headHandle),
  );

  if (splice) {
    const result = groups.slice();
    // Keep the table-head record count (group 70) in sync with the records we
    // are about to splice in. AutoCAD/ezdxf write the actual entry count here;
    // leaving it stale makes strict readers/audits repair or reject the APPID
    // table (@codex #422 P2 round-2). Set it to the post-insertion count so it
    // matches the actual number of APPID records.
    if (scan.headerCountIndex !== -1) {
      result[scan.headerCountIndex] = {
        code: 70,
        value: String(scan.recordCount + names.length),
      };
    }
    result.splice(scan.endtabIndex, 0, ...newRecords);
    return result;
  }

  // No APPID table at all — synthesise a complete one (with a proper head when
  // a handle could be minted, see above) and append it.
  return [
    ...groups,
    { code: 0, value: "TABLE" },
    { code: 2, value: "APPID" },
    ...(headHandle !== undefined
      ? [
          { code: 5, value: headHandle },
          { code: 330, value: "0" },
          { code: 100, value: "AcDbSymbolTable" },
        ]
      : []),
    { code: 70, value: String(names.length) },
    ...newRecords,
    { code: 0, value: "ENDTAB" },
  ];
}

/**
 * Ensure the document's TABLES section declares an APPID record for EVERY
 * custom `1001 <appid>` XData the writer will emit (RevcloudProps,
 * EDIT_DWG_VP_OVERRIDES, EDIT_DWG_ARRAY_GROUP, AcDbBlockRepresentation,
 * AcadAnnotativeDecomposition, and any imported vendor appid). Derived BY
 * CONSTRUCTION from the actual emission sites (`collectEmittedXDataAppids`) so
 * it can never under-register. Creates a fresh tables-section when the document
 * has none (the `createDxfDocument` from-scratch case). Idempotent — never
 * duplicates an appid already declared in the APPID table (SL-12; generalises
 * Codex #243 round-4 R4-B from RevcloudProps to every emitted appid).
 *
 * @codex #442 R7: a TABLES section is EITHER the typed `tables-section` (APPID
 * table in `rawTableGroups`) OR a legacy `raw-section` named "TABLES" (some
 * callers build it verbatim via `createRawSection("TABLES", …)`; its APPID table
 * rides in `groups`). Matching ONLY the typed kind made a doc carrying a raw
 * TABLES section fall through to the "no TABLES section" branch → a SECOND
 * `SECTION/TABLES` was synthesized → DUPLICATE TABLES sections + duplicate app
 * registrations. Reusing EITHER kind in place — splicing into its existing APPID
 * table (or inserting one into that same section) — keeps exactly one TABLES
 * section and dedups against what it already registers.
 */
function ensureCustomXDataAppids(
  document: DxfDocument,
  minter: HandSeedMinter | undefined,
): DxfDocument {
  const emitted = collectEmittedXDataAppids(document);
  if (emitted.size === 0) return document;

  const tablesIndex = document.sections.findIndex(
    (s) =>
      s.kind === "tables-section" ||
      (s.kind === "raw-section" && s.name === "TABLES"),
  );

  // Deterministic, order-independent output: register missing appids sorted by
  // name (independent of entity traversal order).
  const emittedSorted = [...emitted].sort();

  if (tablesIndex === -1) {
    // No TABLES section — synthesise a minimal one carrying just the APPID
    // table. It must land at the CANONICAL DXF section position: AFTER any
    // preserved HEADER + CLASSES, BEFORE BLOCKS/ENTITIES/OBJECTS. The DXF
    // section order is HEADER → CLASSES → TABLES → BLOCKS → ENTITIES → OBJECTS,
    // and HEADER/CLASSES parse to `raw-section`s named "HEADER"/"CLASSES"
    // (parser.ts). The prior code prepended at index 0 — which, for an
    // imported/minimal doc that has a raw HEADER (+CLASSES) but NO TABLES,
    // emitted TABLES *before* HEADER: a DXF section-order violation (@codex
    // #422 P2-B). (ezdxf's readfile is tolerant of section order, but AutoCAD —
    // the parity target — and the spec require the canonical order.) Insert
    // right after the last HEADER/CLASSES section instead; `insertAt` stays 0
    // when neither is present (the from-scratch `createDxfDocument` case —
    // TABLES → ENTITIES, unchanged).
    const tablesSection: DxfTablesSection = {
      kind: "tables-section",
      textStyles: [],
      dimensionStyles: [],
      layers: [],
      blockRecords: [],
      rawTableGroups: insertAppidRecords([], emittedSorted, minter),
    };
    let insertAt = 0;
    for (let i = 0; i < document.sections.length; i++) {
      const s = document.sections[i]!;
      if (
        s.kind === "raw-section" &&
        (s.name === "HEADER" || s.name === "CLASSES")
      ) {
        insertAt = i + 1;
      }
    }
    const sections = [...document.sections];
    sections.splice(insertAt, 0, tablesSection);
    return { ...document, sections };
  }

  // Reuse the existing TABLES section (typed or raw) in place — never emit a
  // second one. Both kinds carry the APPID table in a raw group stream: the
  // typed section in `rawTableGroups`, the raw-section in `groups`.
  const existing = document.sections[tablesIndex]!;
  const existingGroups =
    existing.kind === "tables-section"
      ? existing.rawTableGroups
      : existing.kind === "raw-section"
        ? existing.groups
        : [];
  const registered = rawTablesRegisteredAppids(existingGroups);
  const missing = emittedSorted.filter((name) => !registered.has(name));
  if (missing.length === 0) return document;
  const updatedGroups = insertAppidRecords(existingGroups, missing, minter);
  const updated: DxfSection =
    existing.kind === "tables-section"
      ? { ...existing, rawTableGroups: updatedGroups }
      : existing.kind === "raw-section"
        ? { ...existing, groups: updatedGroups }
        : existing;
  const sections = [...document.sections];
  sections[tablesIndex] = updated;
  return { ...document, sections };
}

/**
 * Canonical DXF section order per the DXF R2018 spec (and ezdxf's writer):
 * HEADER → CLASSES → TABLES → BLOCKS → ENTITIES → OBJECTS, with the optional
 * THUMBNAILIMAGE / ACDSDATA trailer last. `serializeDxfDocument` synthesizes
 * missing sections from SEVERAL independent paths — the APPID TABLES stub
 * (`ensureCustomXDataAppids`) and the anonymous-dimension BLOCKS stub — and a
 * naive "tables first, then the stub, then the rest" reassembly reordered the
 * raw HEADER/CLASSES sections AFTER those stubs, emitting e.g. `TABLES, BLOCKS,
 * HEADER, CLASSES, ENTITIES` for an imported doc that has BOTH a REVCLOUD (needs
 * the APPID table) and a DIMENSION (needs a `*D` block-stub) — a section-order
 * violation (@codex #422 P2 round-3). Ranking every section by its canonical
 * slot closes the whole class: any synthesized section lands in its correct
 * position no matter which combination of synthesis paths ran.
 */
const CANONICAL_SECTION_ORDER: readonly string[] = [
  "HEADER",
  "CLASSES",
  "TABLES",
  "BLOCKS",
  "ENTITIES",
  "OBJECTS",
  "THUMBNAILIMAGE",
  "ACDSDATA",
];

/** The canonical DXF section name for a `DxfSection` (raw sections keep theirs). */
function canonicalSectionName(section: DxfSection): string {
  switch (section.kind) {
    case "tables-section":
      return "TABLES";
    case "blocks-section":
      return "BLOCKS";
    case "entities-section":
      return "ENTITIES";
    case "objects-section":
      return "OBJECTS";
    case "raw-section":
      return section.name;
  }
}

/**
 * Stable-sort sections into the canonical DXF order. Unknown raw sections
 * (rank -1 → tail) and sections sharing a rank keep their incoming relative
 * order (index tie-break), so an already-canonical input is left untouched.
 */
function placeSectionsInCanonicalOrder(sections: DxfSection[]): DxfSection[] {
  const rank = (section: DxfSection): number => {
    const i = CANONICAL_SECTION_ORDER.indexOf(canonicalSectionName(section));
    return i === -1 ? CANONICAL_SECTION_ORDER.length : i;
  };
  return sections
    .map((section, index) => ({ section, index }))
    .sort((a, b) => rank(a.section) - rank(b.section) || a.index - b.index)
    .map((entry) => entry.section);
}

export function serializeDxfDocument(
  document: DxfDocument,
  options: SerializeDxfOptions = {},
): string {
  const newline = options.newline ?? "\r\n";
  // Register an APPID table record for EVERY custom `1001 <appid>` XData the
  // writer will emit (RevcloudProps, EDIT_DWG_VP_OVERRIDES, EDIT_DWG_ARRAY_GROUP,
  // AcDbBlockRepresentation, AcadAnnotativeDecomposition, imported vendor
  // appids) — every XData appid needs an APPID table record per the DXF spec
  // (SL-12; generalises Codex #243 round-4 R4-B).
  // T9b — writer-synthesized records (APPID, `*D` stubs) mint their own group-5
  // off the HEADER `$HANDSEED`; the advanced seed is committed below.
  const minter = createHandSeedMinter(document.sections);
  const appidDocument = ensureCustomXDataAppids(document, minter);
  // Auto-assign *D### block names to dimensions and prepare a stub
  // BLOCKS section so the resulting DXF satisfies AutoCAD's requirement
  // that every DIMENSION entity reference a block.
  const { document: enrichedDocument, assignedNames } =
    assignAnonymousDimensionBlockNames(appidDocument);
  const sourceSections =
    enrichedDocument.sections.length === 0
      ? [{ kind: "entities-section", entities: [] } satisfies DxfSection]
      : enrichedDocument.sections;

  // Check for an existing BLOCKS section — either the legacy raw-section
  // (still produced by older callers) or the new structured blocks-section.
  const existingBlocksIndex = sourceSections.findIndex(
    (s) =>
      s.kind === "blocks-section" ||
      (s.kind === "raw-section" && s.name === "BLOCKS"),
  );
  const hasBlocksSection = existingBlocksIndex !== -1;

  let sections: DxfSection[];
  if (!hasBlocksSection) {
    // No BLOCKS section yet — inject a fresh stub containing the anonymous
    // dimension blocks (if any), then place EVERY section (the injected stub
    // plus any synthesized APPID TABLES + preserved raw HEADER/CLASSES) into the
    // canonical DXF section order. The previous "tables first, stub, then the
    // rest" reassembly reordered raw HEADER/CLASSES AFTER the stubs, which — when
    // both an APPID TABLES and a `*D` block-stub were synthesized — emitted
    // `TABLES, BLOCKS, HEADER, CLASSES, ENTITIES` (@codex #422 P2 round-3). The
    // canonical positioner keeps HEADER → CLASSES → TABLES → BLOCKS → ENTITIES →
    // OBJECTS regardless of which synthesis paths ran.
    const blocksStub = buildBlocksSectionStub(assignedNames, minter);
    sections = blocksStub
      ? placeSectionsInCanonicalOrder([...sourceSections, blocksStub])
      : sourceSections;
  } else if (assignedNames.length > 0) {
    // There is already a BLOCKS section — merge in any dimension stub
    // blocks that don't already exist. If the section is the structured
    // kind we append to `blocks[]`; if it's a legacy raw-section we append
    // the same stub definitions serialized as raw DxfGroups so every minted
    // `*D###` name resolves to a real BLOCK record (Codex #250 P2 round-3).
    sections = sourceSections.map((s, index) => {
      if (index !== existingBlocksIndex) return s;
      if (s.kind === "blocks-section") {
        const existingNames = new Set(s.blocks.map((b) => b.name));
        // Names already present only in passthrough rawBlockGroups (group
        // code 2) must NOT get a duplicate structured stub — they already
        // resolve. Seed them into the dedupe set alongside structured blocks.
        for (const g of s.rawBlockGroups) {
          if (g.code === 2 && typeof g.value === "string" && g.value) {
            existingNames.add(g.value);
          }
        }
        // Dedupe assigned names before building stubs: multiple block-child
        // DIMENSIONs may share the same `*D###` reference (e.g. two dims both
        // pointing at `*D7`), which pushes that name into `assignedNames` once
        // per child. Without deduping, each duplicate passes the
        // `existingNames` filter (which only knows pre-merge blocks) and we
        // append duplicate BLOCK definitions with the same `*D###` name →
        // invalid/ambiguous DXF. Each distinct missing name must yield exactly
        // ONE stub block (Codex #250 P2).
        const toAdd = [...new Set(assignedNames)]
          .filter((name) => !existingNames.has(name))
          .map((name) => buildStubBlockDefinition(name, minter));
        if (toAdd.length === 0) return s;
        return { ...s, blocks: [...s.blocks, ...toAdd] };
      }
      if (s.kind === "raw-section" && s.name === "BLOCKS") {
        // Legacy raw passthrough BLOCKS section (Codex #250 P2 round-3):
        // round-2 seeds the minter's `used` set from this section's
        // group-code-2 names so it never aliases an existing block, but the
        // merge previously left the raw groups UNCHANGED — so a freshly
        // minted `*D###` for a stub-less dimension had NO corresponding BLOCK
        // record here → the serialized DIMENSION pointed at a missing block.
        // Fix: append the SAME stub definitions (mirrored as raw DxfGroups via
        // serializeBlockDefinition, the single source of truth for stub shape)
        // so every minted name resolves. Dedupe against existing group-2 names
        // so we never duplicate a block that is already present.
        const existingNames = new Set<string>();
        for (const g of s.groups) {
          if (g.code === 2 && typeof g.value === "string" && g.value) {
            existingNames.add(g.value);
          }
        }
        const stubGroups = [...new Set(assignedNames)]
          .filter((name) => !existingNames.has(name))
          .flatMap((name) =>
            serializeBlockDefinition(buildStubBlockDefinition(name, minter)),
          );
        if (stubGroups.length === 0) return s;
        return { ...s, groups: [...s.groups, ...stubGroups] };
      }
      return s;
    });
  } else {
    sections = sourceSections;
  }
  // T9b — write the advanced `$HANDSEED` back (identity when nothing was minted).
  if (minter) sections = minter.commit(sections);

  // T9 PR-3 — version-gated `\U+XXXX` escaping of non-ASCII string content.
  // Explicit option wins; otherwise derive from the document's $ACADVER
  // (< AC1021 ⇒ legacy ANSI-codepage path ⇒ escape; R2007+ ⇒ raw UTF-16/UTF-8).
  // Computed BEFORE entity serialization because MTEXT must escape-then-chunk.
  const escapeUnicode =
    options.escapeUnicode ?? shouldEscapeForVersion(sections);

  // Version-gate the WIPEOUT/IMAGE `clip_mode` (group 290) — a DXF2010+ (AC1024)
  // attribute. Threaded through the entity-serialization chain (mirrors
  // `escapeUnicode`) so the WIPEOUT writer omits 290 when re-saving an imported
  // pre-R2010 drawing, matching ezdxf `export_entity` (`dxfversion >= DXF2010`).
  const emitClipMode = versionSupportsClipMode(sections);

  const groups = [
    ...sections.flatMap((section) =>
      serializeSection(section, escapeUnicode, emitClipMode),
    ),
    { code: 0, value: "EOF" },
  ];

  // Escape the remaining string-content groups (TEXT/ATTRIB/ATTDEF/DIMENSION
  // text/XDATA/symbol names/…) at the STRING-EMISSION step ONLY — never write
  // the escaped value back into the DxfGroup object. Raw sections, unsupported
  // entities, raw table+object groups and extraGroups REUSE caller-owned
  // `DxfGroup` objects from the parsed source document; mutating `group.value`
  // in place would corrupt the in-memory document (a later `escapeUnicode:false`
  // save, or any reader of those raw groups, would see escaped data). MTEXT
  // chunks are already escaped (idempotent here: their values are pure ASCII so
  // `escapeDxfUnicode` is a no-op).
  //
  // ## Group-value length after escaping (NON-MTEXT codes) — ezdxf-grounded
  //
  // The R2000+ DXF group-value soft limit is EXT_MAX_STR_LEN = 2049 chars
  // (ezdxf/lldxf/const.py; R12 is MAX_STR_LEN = 255). ×7 escape expansion means a
  // non-ASCII string stays within it iff its raw length ≤ 292 Cyrillic chars
  // (2049/7). Only MTEXT has a chunk mechanism (group-3 segments + final group-1);
  // a plain TEXT/ATTRIB/DIMENSION/XDATA group-1 has NO standard split form.
  //
  // We DELIBERATELY do NOT cap or split these groups: ezdxf 1.4.3 (the project
  // oracle) does NOT split or cap a long TEXT group-1 either — its tag writer
  // emits the whole escaped string as one line even at 2800 chars (a 400-char
  // Cyrillic label, well over 2049), and `ezdxf.readfile` reads OUR output back at
  // n=200/292/293/400 with audit errors==0 / fixes==0 and `decode_dxf_unicode`
  // recovers the exact text. Forcing a cap/split would DIVERGE from ezdxf/AutoCAD
  // and risk silent data loss. Realistic engineering labels are far under the
  // bound; the >292-char single-group pathological case is a shared format edge
  // both ezdxf and AutoCAD accept. A regression test
  // (dxfBridge.cyrillic.test.ts — "realistic long Cyrillic TEXT/ATTRIB escapes
  // within EXT_MAX_STR_LEN") guards the realistic bound; the format edge is
  // documented here rather than papered over with a divergent cap.
  const escapeIf = (group: DxfGroup): string =>
    escapeUnicode &&
    typeof group.value === "string" &&
    STRING_CONTENT_GROUP_CODES.has(group.code)
      ? escapeDxfUnicode(group.value)
      : group.value;

  return (
    groups
      .map((group) => `${group.code}${newline}${escapeIf(group)}`)
      .join(newline) + newline
  );
}

/**
 * Derive the default unicode-escape decision from the document's `$ACADVER`
 * HEADER variable (a raw-section `9 $ACADVER` group followed by `1 <AC10xx>`).
 * Pre-R2007 versions (< AC1021) store text in the legacy `$DWGCODEPAGE`
 * codepage and therefore need `\U+` escaping; R2007+ (AC1021 = R2007,
 * AC1024 = R2010, AC1027 = R2013, AC1032 = R2018) store text as native
 * UTF-16/UTF-8 and must NOT be escaped. Unknown / absent $ACADVER defaults to
 * escape (the current fixed R2000 export).
 */
function shouldEscapeForVersion(sections: readonly DxfSection[]): boolean {
  const acadVer = readAcadVer(sections);
  // No $ACADVER → assume legacy R2000 (the current export target) → escape.
  return acadVer === undefined ? true : acadVerNeedsEscape(acadVer);
}

/** `AC10xx` ⇒ needs `\U+` escaping iff the release is older than R2007 (AC1021). */
function acadVerNeedsEscape(acadVer: string): boolean {
  const match = /^AC(\d{4})$/.exec(acadVer);
  if (!match) return true; // unrecognised → conservative legacy default
  return Number.parseInt(match[1], 10) < 1021;
}

/**
 * Read the raw `$ACADVER` value (e.g. "AC1032") from the document's HEADER
 * raw-section, or `undefined` when absent. The SINGLE source of truth for the
 * version-gate seams (`shouldEscapeForVersion`, `versionSupportsClipMode`) so the
 * `9 $ACADVER` / `1 <AC10xx>` scan is not duplicated per gate.
 */
function readAcadVer(sections: readonly DxfSection[]): string | undefined {
  for (const section of sections) {
    if (section.kind !== "raw-section" || section.name !== "HEADER") continue;
    const groups = section.groups;
    for (let i = 0; i < groups.length - 1; i += 1) {
      if (groups[i].code === 9 && groups[i].value === "$ACADVER") {
        const verGroup = groups[i + 1];
        if (verGroup.code === 1 && typeof verGroup.value === "string") {
          return verGroup.value.trim();
        }
      }
    }
  }
  return undefined;
}

/**
 * Whether the target DXF version supports the WIPEOUT/IMAGE `clip_mode` (group
 * 290) attribute — a DXF2010+ (AC1024 = R2010) addition. ezdxf `acdb_image`
 * declares `clip_mode` with `dxfversion=DXF2010`, and `ImageBase.export_entity`
 * gates its export on `tagwriter.dxfversion >= DXF2010`; emitting 290 into an
 * older (AC1015 / AC1021) file makes older readers see a newer-version group.
 *
 * Derived from the document's `$ACADVER`, mirroring `shouldEscapeForVersion`.
 * Only a POSITIVELY-identified pre-R2010 release (an imported older drawing whose
 * source `$ACADVER` we preserve) suppresses 290; an absent / unrecognised
 * `$ACADVER` defaults to the writer's canonical export target — R2018 / AC1032,
 * which `ensureConformantSkeleton` synthesises — so a from-scratch WIPEOUT still
 * emits 290 while re-saving an imported AC1015 file omits it. (The default
 * differs from `shouldEscapeForVersion`'s legacy default on purpose: escaping is
 * a safety measure best defaulted ON, whereas 290 is a version-gated feature best
 * defaulted to the modern target the skeleton always mints.)
 */
function versionSupportsClipMode(sections: readonly DxfSection[]): boolean {
  const acadVer = readAcadVer(sections);
  return acadVer === undefined ? true : acadVerSupportsClipMode(acadVer);
}

/** `AC10xx` ⇒ supports clip_mode (290) iff the release is R2010 (AC1024)+. */
function acadVerSupportsClipMode(acadVer: string): boolean {
  const match = /^AC(\d{4})$/.exec(acadVer);
  if (!match) return true; // unrecognised → assume the modern (R2018) target
  return Number.parseInt(match[1], 10) >= 1024;
}
